'use client';

// src/Liveline.tsx
import { useCallback as useCallback2, useLayoutEffect as useLayoutEffect2, useMemo, useRef as useRef2, useState as useState2 } from "react";

// src/theme.ts
function parseColorRgb(color) {
  const hex = color.match(/^#([0-9a-f]{3,8})$/i);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  }
  const rgb = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgb) return [+rgb[1], +rgb[2], +rgb[3]];
  return [128, 128, 128];
}
function rgba(r, g, b, a) {
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}
function resolveTheme(color, mode) {
  const [r, g, b] = parseColorRgb(color);
  const isDark = mode === "dark";
  return {
    // Line
    line: color,
    lineWidth: 2,
    // Fill gradient
    fillTop: rgba(r, g, b, isDark ? 0.12 : 0.08),
    fillBottom: rgba(r, g, b, 0),
    // Grid
    gridLine: isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.06)",
    gridLabel: isDark ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 0, 0, 0.35)",
    // Dot — always semantic
    dotUp: "#22c55e",
    dotDown: "#ef4444",
    dotFlat: color,
    glowUp: "rgba(34, 197, 94, 0.18)",
    glowDown: "rgba(239, 68, 68, 0.18)",
    glowFlat: rgba(r, g, b, 0.12),
    // Badge
    badgeOuterBg: isDark ? "rgba(40, 40, 40, 0.95)" : "rgba(255, 255, 255, 0.95)",
    badgeOuterShadow: isDark ? "rgba(0, 0, 0, 0.4)" : "rgba(0, 0, 0, 0.15)",
    badgeBg: color,
    badgeText: "#ffffff",
    // Dash line
    dashLine: rgba(r, g, b, 0.4),
    // Reference line
    refLine: isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.12)",
    refLabel: isDark ? "rgba(255, 255, 255, 0.45)" : "rgba(0, 0, 0, 0.4)",
    // Time axis
    timeLabel: isDark ? "rgba(255, 255, 255, 0.35)" : "rgba(0, 0, 0, 0.3)",
    // Crosshair
    crosshairLine: isDark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.12)",
    tooltipBg: isDark ? "rgba(30, 30, 30, 0.95)" : "rgba(255, 255, 255, 0.95)",
    tooltipText: isDark ? "#e5e5e5" : "#1a1a1a",
    tooltipBorder: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)",
    // Background
    bgRgb: isDark ? [10, 10, 10] : [255, 255, 255],
    // Fonts
    labelFont: '11px "SF Mono", Menlo, Monaco, "Cascadia Code", monospace',
    valueFont: '600 11px "SF Mono", Menlo, monospace',
    badgeFont: '500 11px "SF Mono", Menlo, monospace'
  };
}
var SERIES_COLORS = [
  "#3b82f6",
  // blue
  "#ef4444",
  // red
  "#22c55e",
  // green
  "#f59e0b",
  // amber
  "#8b5cf6",
  // violet
  "#ec4899",
  // pink
  "#06b6d4",
  // cyan
  "#f97316"
  // orange
];
function resolveSeriesPalettes(series, mode, lineWidth) {
  const map = /* @__PURE__ */ new Map();
  for (let i = 0; i < series.length; i++) {
    const s = series[i];
    const color = s.color || SERIES_COLORS[i % SERIES_COLORS.length];
    const palette = resolveTheme(color, mode);
    if (lineWidth !== void 0) palette.lineWidth = lineWidth;
    map.set(s.id, palette);
  }
  return map;
}

// src/useLivelineEngine.ts
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

// src/canvas/dpr.ts
function getDpr() {
  if (typeof window === "undefined") return 1;
  return Math.min(window.devicePixelRatio || 1, 3);
}
function applyDpr(ctx, dpr, w, h) {
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);
}

// src/draw/candlestick.ts
var BULL = "#22c55e";
var BEAR = "#ef4444";
var BULL_RGB = [34, 197, 94];
var BEAR_RGB = [239, 68, 68];
function blendColor(t) {
  const r = Math.round(BEAR_RGB[0] + (BULL_RGB[0] - BEAR_RGB[0]) * t);
  const g = Math.round(BEAR_RGB[1] + (BULL_RGB[1] - BEAR_RGB[1]) * t);
  const b = Math.round(BEAR_RGB[2] + (BULL_RGB[2] - BEAR_RGB[2]) * t);
  return `rgb(${r},${g},${b})`;
}
function parseRgb(color) {
  const hex = color.match(/^#([0-9a-f]{6})$/i);
  if (hex) {
    const h = hex[1];
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  }
  const rgb = color.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgb) return [+rgb[1], +rgb[2], +rgb[3]];
  return [128, 128, 128];
}
function blendToAccent(candleColor, accentColor, t) {
  if (t <= 0) return candleColor;
  if (t >= 1) return accentColor;
  const [r1, g1, b1] = parseRgb(candleColor);
  const [r2, g2, b2] = parseRgb(accentColor);
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  return `rgb(${r},${g},${b})`;
}
function candleDims(layout, candleWidthSecs) {
  const pxPerSec = layout.chartW / (layout.rightEdge - layout.leftEdge);
  const candlePxW = candleWidthSecs * pxPerSec;
  const bodyW = Math.max(1, candlePxW * 0.7);
  const wickW = Math.max(0.8, Math.min(2, bodyW * 0.15));
  const radius = bodyW > 6 ? 1.5 : 0;
  return { bodyW, wickW, radius };
}
function roundedRect(ctx, x, y, w, h, r) {
  if (r <= 0 || h < r * 2) {
    ctx.rect(x, y, w, h);
    return;
  }
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}
function drawCandlesticks(ctx, layout, candles, candleWidthSecs, liveTime, now_ms, scrubX, scrubDim, liveAlpha = 1, liveBullBlend = -1, accentColor, accentBlend = 0) {
  if (candles.length === 0) return;
  const { toX, toY } = layout;
  const { bodyW, wickW, radius } = candleDims(layout, candleWidthSecs);
  const halfBody = bodyW / 2;
  const padL = layout.pad.left;
  const padR = layout.pad.left + layout.chartW;
  const livePulse = 0.12 + Math.sin(now_ms * 4e-3) * 0.08;
  for (const c of candles) {
    const cx = toX(c.time + candleWidthSecs / 2);
    if (cx + halfBody < padL || cx - halfBody > padR) continue;
    const isBull = c.close >= c.open;
    const isLive = c.time === liveTime;
    let color = isLive && liveBullBlend >= 0 ? blendColor(liveBullBlend) : isBull ? BULL : BEAR;
    if (accentColor && accentBlend > 0.01) {
      color = blendToAccent(color, accentColor, accentBlend);
    }
    let candleAlpha = isLive ? liveAlpha : 1;
    if (scrubDim > 0.01 && scrubX > 0) {
      const dist = cx - scrubX;
      if (dist > 0) {
        const fadeZone = bodyW * 1.5;
        const dimT = Math.min(dist / fadeZone, 1);
        candleAlpha *= 1 - scrubDim * 0.5 * dimT;
      }
    }
    const baseAlpha = ctx.globalAlpha;
    ctx.globalAlpha = baseAlpha * candleAlpha;
    const bodyHigh = Math.max(c.open, c.close);
    const bodyLow = Math.min(c.open, c.close);
    const bodyCenter = (toY(bodyHigh) + toY(bodyLow)) / 2;
    const bodyH = Math.max(1, toY(bodyLow) - toY(bodyHigh));
    const bodyTop = bodyCenter - bodyH / 2;
    const bodyBottom = bodyCenter + bodyH / 2;
    const wickTop = toY(c.high);
    const wickBottom = toY(c.low);
    ctx.lineCap = "round";
    ctx.strokeStyle = color;
    if (c.high > bodyHigh) {
      ctx.beginPath();
      ctx.moveTo(cx, bodyTop);
      ctx.lineTo(cx, Math.min(wickTop, bodyTop - 1));
      ctx.lineWidth = wickW;
      ctx.stroke();
    }
    if (c.low < bodyLow) {
      ctx.beginPath();
      ctx.moveTo(cx, bodyBottom);
      ctx.lineTo(cx, Math.max(wickBottom, bodyBottom + 1));
      ctx.lineWidth = wickW;
      ctx.stroke();
    }
    ctx.fillStyle = color;
    ctx.beginPath();
    roundedRect(ctx, cx - halfBody, bodyTop, bodyW, bodyH, radius);
    ctx.fill();
    if (isLive) {
      ctx.save();
      ctx.globalAlpha = baseAlpha * candleAlpha * livePulse;
      ctx.shadowColor = color;
      ctx.shadowBlur = 8;
      ctx.fillStyle = color;
      ctx.beginPath();
      roundedRect(ctx, cx - halfBody, bodyTop, bodyW, bodyH, radius);
      ctx.fill();
      ctx.restore();
    }
    ctx.globalAlpha = baseAlpha;
  }
}
function drawClosePrice(ctx, layout, palette, liveCandle, scrubDim, bullBlend = -1) {
  const y = layout.toY(liveCandle.close);
  if (y < layout.pad.top || y > layout.h - layout.pad.bottom) return;
  const isBull = liveCandle.close >= liveCandle.open;
  const color = bullBlend >= 0 ? blendColor(bullBlend) : isBull ? BULL : BEAR;
  const baseAlpha = ctx.globalAlpha;
  ctx.save();
  ctx.setLineDash([4, 4]);
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.globalAlpha = baseAlpha * (1 - scrubDim * 0.3) * 0.4;
  ctx.beginPath();
  ctx.moveTo(layout.pad.left, y);
  ctx.lineTo(layout.w - layout.pad.right, y);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();
}
function drawCandleCrosshair(ctx, layout, palette, hoverX, candle, hoverTime, formatValue, formatTime, opacity) {
  if (opacity < 0.01) return;
  const { h, pad } = layout;
  ctx.save();
  ctx.globalAlpha = opacity * 0.5;
  ctx.strokeStyle = palette.crosshairLine;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(hoverX, pad.top);
  ctx.lineTo(hoverX, h - pad.bottom);
  ctx.stroke();
  ctx.restore();
  if (opacity < 0.1 || layout.w < 200) return;
  const isBull = candle.close >= candle.open;
  const valueColor = isBull ? BULL : BEAR;
  const cl = formatValue(candle.close);
  const time = formatTime(hoverTime);
  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.font = '400 13px "SF Mono", Menlo, monospace';
  ctx.textAlign = "left";
  let parts;
  if (layout.w >= 400) {
    const o = formatValue(candle.open);
    const hi = formatValue(candle.high);
    const lo = formatValue(candle.low);
    parts = [
      { text: "O ", color: palette.gridLabel },
      { text: o, color: valueColor },
      { text: "   H ", color: palette.gridLabel },
      { text: hi, color: valueColor },
      { text: "   L ", color: palette.gridLabel },
      { text: lo, color: valueColor },
      { text: "   C ", color: palette.gridLabel },
      { text: cl, color: valueColor },
      { text: "  \xB7  ", color: palette.gridLabel },
      { text: time, color: palette.gridLabel }
    ];
  } else {
    parts = [
      { text: "C ", color: palette.gridLabel },
      { text: cl, color: valueColor },
      { text: "  \xB7  ", color: palette.gridLabel },
      { text: time, color: palette.gridLabel }
    ];
  }
  let totalW = 0;
  const widths = [];
  for (const p of parts) {
    const w = ctx.measureText(p.text).width;
    widths.push(w);
    totalW += w;
  }
  let tx = hoverX - totalW / 2;
  const minX = pad.left + 4;
  const maxX = layout.w - pad.right - totalW;
  if (tx < minX) tx = minX;
  if (tx > maxX) tx = maxX;
  const ty = pad.top + 24;
  ctx.strokeStyle = palette.tooltipBg;
  ctx.lineWidth = 3;
  ctx.lineJoin = "round";
  let cx = tx;
  for (let i = 0; i < parts.length; i++) {
    ctx.strokeText(parts[i].text, cx, ty);
    cx += widths[i];
  }
  cx = tx;
  for (let i = 0; i < parts.length; i++) {
    ctx.fillStyle = parts[i].color;
    ctx.fillText(parts[i].text, cx, ty);
    cx += widths[i];
  }
  ctx.restore();
}
function drawLineModeCrosshair(ctx, layout, palette, hoverX, value, hoverTime, formatValue, formatTime, opacity) {
  if (opacity < 0.01) return;
  const { h, pad } = layout;
  const y = layout.toY(value);
  ctx.save();
  ctx.globalAlpha = opacity * 0.5;
  ctx.strokeStyle = palette.crosshairLine;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(hoverX, pad.top);
  ctx.lineTo(hoverX, h - pad.bottom);
  ctx.stroke();
  ctx.globalAlpha = opacity * 0.3;
  ctx.beginPath();
  ctx.moveTo(pad.left, y);
  ctx.lineTo(layout.w - pad.right, y);
  ctx.stroke();
  ctx.restore();
  if (opacity < 0.1 || layout.w < 200) return;
  const val = formatValue(value);
  const time = formatTime(hoverTime);
  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.font = '400 13px "SF Mono", Menlo, monospace';
  ctx.textAlign = "left";
  const parts = [
    { text: val, color: palette.line },
    { text: "  \xB7  ", color: palette.gridLabel },
    { text: time, color: palette.gridLabel }
  ];
  let totalW = 0;
  const widths = [];
  for (const p of parts) {
    const w = ctx.measureText(p.text).width;
    widths.push(w);
    totalW += w;
  }
  let tx = hoverX - totalW / 2;
  const minX = pad.left + 4;
  const maxX = layout.w - pad.right - totalW;
  if (tx < minX) tx = minX;
  if (tx > maxX) tx = maxX;
  const ty = pad.top + 24;
  ctx.strokeStyle = palette.tooltipBg;
  ctx.lineWidth = 3;
  ctx.lineJoin = "round";
  let lx = tx;
  for (let i = 0; i < parts.length; i++) {
    ctx.strokeText(parts[i].text, lx, ty);
    lx += widths[i];
  }
  lx = tx;
  for (let i = 0; i < parts.length; i++) {
    ctx.fillStyle = parts[i].color;
    ctx.fillText(parts[i].text, lx, ty);
    lx += widths[i];
  }
  ctx.restore();
}

// src/draw/crosshair-primitives.ts
function clamp(value, min, max) {
  if (max < min) return min;
  return value < min ? min : value > max ? max : value;
}
function paintPill(ctx, x, y, width, height, radius, background, borderColor) {
  const resolvedRadius = Math.min(radius, height / 2, width / 2);
  ctx.beginPath();
  if (typeof ctx.roundRect === "function") {
    ctx.roundRect(x, y, width, height, resolvedRadius);
  } else {
    ctx.moveTo(x + resolvedRadius, y);
    ctx.arcTo(x + width, y, x + width, y + height, resolvedRadius);
    ctx.arcTo(x + width, y + height, x, y + height, resolvedRadius);
    ctx.arcTo(x, y + height, x, y, resolvedRadius);
    ctx.arcTo(x, y, x + width, y, resolvedRadius);
    ctx.closePath();
  }
  ctx.fillStyle = background;
  ctx.fill();
  if (borderColor) {
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = 1;
    ctx.stroke();
  }
}

// src/draw/crosshair.ts
function drawPerSeriesPillCrosshair(ctx, layout, palette, hoverX, hoverTime, bucketSecs, entries, formatValue, scrubOpacity, style, pinned = false) {
  if (scrubOpacity < 0.01 || entries.length === 0) return;
  const { w, h, pad, toY } = layout;
  if (!pinned) {
    ctx.save();
    ctx.globalAlpha = scrubOpacity * 0.5;
    ctx.strokeStyle = style.guideColor ?? palette.crosshairLine;
    ctx.lineWidth = style.guideWidth ?? 1;
    if (style.guideDashPattern) ctx.setLineDash(style.guideDashPattern);
    ctx.beginPath();
    ctx.moveTo(hoverX, pad.top);
    ctx.lineTo(hoverX, h - pad.bottom);
    ctx.stroke();
    ctx.restore();
  }
  const dotRadius = 4 * Math.min(scrubOpacity * 3, 1);
  if (dotRadius > 0.5) {
    ctx.save();
    for (const entry of entries) {
      ctx.beginPath();
      ctx.arc(hoverX, toY(entry.value), dotRadius, 0, Math.PI * 2);
      ctx.fillStyle = entry.color;
      ctx.fill();
    }
    ctx.restore();
  }
  if (scrubOpacity < 0.1 || w < 240) return;
  ctx.save();
  ctx.globalAlpha = scrubOpacity;
  ctx.textAlign = "left";
  let timePillBottom = pad.top;
  if (!pinned) {
    const family2 = style.timePillFontFamily ?? 'ui-monospace, "SF Mono", Menlo, monospace';
    const size2 = style.timePillFontSize ?? 11;
    const weight2 = style.timePillFontWeight ?? 500;
    const paddingX2 = style.timePillPaddingX ?? 8;
    const paddingY2 = style.timePillPaddingY ?? 4;
    const text = style.formatTimeRange ? style.formatTimeRange(hoverTime, hoverTime + bucketSecs) : defaultFormatTimeRange(hoverTime, hoverTime + bucketSecs);
    ctx.font = `${weight2} ${size2}px ${family2}`;
    const pillWidth = ctx.measureText(text).width + paddingX2 * 2;
    const pillHeight = size2 + paddingY2 * 2;
    const pillX = clamp(hoverX - pillWidth / 2, pad.left + 4, w - pad.right - pillWidth - 4);
    const pillY = pad.top >= pillHeight + 6 ? 4 : pad.top + 2;
    paintPill(
      ctx,
      pillX,
      pillY,
      pillWidth,
      pillHeight,
      style.timePillRadius ?? 6,
      style.timePillBackground ?? palette.tooltipBg,
      style.timePillBorderColor
    );
    ctx.fillStyle = style.timePillColor ?? palette.tooltipText;
    ctx.textBaseline = "middle";
    ctx.fillText(text, pillX + paddingX2, pillY + pillHeight / 2 + 0.5);
    timePillBottom = pillY + pillHeight;
  }
  const family = style.seriesPillFontFamily ?? "system-ui, -apple-system, sans-serif";
  const size = style.seriesPillFontSize ?? 12;
  const weight = style.seriesPillFontWeight ?? 400;
  const valueWeight = style.seriesPillValueFontWeight ?? 500;
  const paddingX = style.seriesPillPaddingX ?? 8;
  const paddingY = style.seriesPillPaddingY ?? 4;
  const dotSize = style.seriesPillDotSize ?? 8;
  const dotGap = style.seriesPillDotGap ?? 6;
  const labelValueGap = style.seriesPillLabelValueGap ?? 6;
  const maxLabelChars = style.maxLabelChars ?? 14;
  const stackGap = 4;
  const layouts = entries.map((entry) => {
    const label = truncate(entry.label, maxLabelChars);
    const value = style.formatSeriesValue?.(entry.value, entry.id) ?? formatValue(entry.value);
    ctx.font = `${weight} ${size}px ${family}`;
    const labelWidth = label ? ctx.measureText(label).width : 0;
    ctx.font = `${valueWeight} ${size}px ${family}`;
    const valueWidth = ctx.measureText(value).width;
    const width = dotSize + dotGap + labelWidth + (label ? labelValueGap : 0) + valueWidth + paddingX * 2;
    const height = size + paddingY * 2;
    const preferredY = toY(entry.value) - height / 2;
    return { entry, label, value, labelWidth, width, height, preferredY, y: preferredY };
  });
  const topBound = timePillBottom <= pad.top ? pad.top + 2 : timePillBottom + stackGap;
  const bottomBound = h - pad.bottom - 2;
  const order = layouts.map((_, index) => index).sort((a, b) => layouts[a].preferredY - layouts[b].preferredY);
  let previousBottom = topBound;
  for (const index of order) {
    const item = layouts[index];
    if (item.y < previousBottom + stackGap) item.y = previousBottom + stackGap;
    previousBottom = item.y + item.height;
  }
  const lastIndex = order.at(-1);
  if (lastIndex !== void 0) {
    const overflow = layouts[lastIndex].y + layouts[lastIndex].height - bottomBound;
    if (overflow > 0) {
      for (let index = order.length - 1; index >= 0; index -= 1) {
        const item = layouts[order[index]];
        item.y = Math.max(topBound, item.y - overflow);
      }
    }
  }
  for (const item of layouts) {
    item.y = clamp(item.y, topBound, Math.max(topBound, bottomBound - item.height));
    let pillX = hoverX + 8;
    if (pillX + item.width > w - pad.right - 4) pillX = hoverX - item.width - 8;
    pillX = clamp(pillX, pad.left + 4, w - pad.right - item.width - 4);
    paintPill(
      ctx,
      pillX,
      item.y,
      item.width,
      item.height,
      style.seriesPillRadius ?? 6,
      style.seriesPillBackground ?? palette.tooltipBg,
      style.seriesPillBorderColor
    );
    const dotX = pillX + paddingX + dotSize / 2;
    const centerY = item.y + item.height / 2;
    ctx.beginPath();
    ctx.arc(dotX, centerY, dotSize / 2, 0, Math.PI * 2);
    ctx.fillStyle = item.entry.color;
    ctx.fill();
    let textX = dotX + dotSize / 2 + dotGap;
    ctx.textBaseline = "middle";
    if (item.label) {
      ctx.font = `${weight} ${size}px ${family}`;
      ctx.fillStyle = style.seriesPillColor ?? palette.tooltipText;
      ctx.fillText(item.label, textX, centerY + 0.5);
      textX += item.labelWidth + labelValueGap;
    }
    ctx.font = `${valueWeight} ${size}px ${family}`;
    ctx.fillStyle = style.seriesPillValueColor ?? style.seriesPillColor ?? palette.tooltipText;
    ctx.fillText(item.value, textX, centerY + 0.5);
  }
  ctx.restore();
}
function truncate(value, maxChars) {
  if (value.length <= maxChars) return value;
  return `${value.slice(0, Math.max(1, maxChars - 1))}\u2026`;
}
function defaultFormatTimeRange(fromSecs, toSecs) {
  const from = new Date(fromSecs * 1e3);
  const to = new Date(toSecs * 1e3);
  const date = from.toLocaleDateString(void 0, { month: "short", day: "numeric" });
  const fromTime = from.toLocaleTimeString(void 0, { hour: "2-digit", minute: "2-digit" });
  if (toSecs <= fromSecs + 1) return `${date}, ${fromTime}`;
  const toTime = to.toLocaleTimeString(void 0, { hour: "2-digit", minute: "2-digit" });
  const sameDay = from.getFullYear() === to.getFullYear() && from.getMonth() === to.getMonth() && from.getDate() === to.getDate();
  if (sameDay) return `${date}, ${fromTime} \u2013 ${toTime}`;
  const toDate = to.toLocaleDateString(void 0, { month: "short", day: "numeric" });
  return `${date} ${fromTime} \u2013 ${toDate} ${toTime}`;
}
function drawCrosshair(ctx, layout, palette, hoverX, hoverValue, hoverTime, formatValue, formatTime, scrubOpacity, tooltipY, liveDotX, tooltipOutline) {
  if (scrubOpacity < 0.01) return;
  const { h, pad, toY } = layout;
  const y = toY(hoverValue);
  ctx.save();
  ctx.globalAlpha = scrubOpacity * 0.5;
  ctx.strokeStyle = palette.crosshairLine;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(hoverX, pad.top);
  ctx.lineTo(hoverX, h - pad.bottom);
  ctx.stroke();
  ctx.restore();
  const dotRadius = 4 * Math.min(scrubOpacity * 3, 1);
  if (dotRadius > 0.5) {
    ctx.globalAlpha = 1;
    ctx.beginPath();
    ctx.arc(hoverX, y, dotRadius, 0, Math.PI * 2);
    ctx.fillStyle = palette.line;
    ctx.fill();
  }
  if (scrubOpacity < 0.1 || layout.w < 300) return;
  const valueText = formatValue(hoverValue);
  const timeText = formatTime(hoverTime);
  const separator = "  \xB7  ";
  ctx.save();
  ctx.globalAlpha = scrubOpacity;
  ctx.font = '400 13px "SF Mono", Menlo, monospace';
  const valueW = ctx.measureText(valueText).width;
  const sepW = ctx.measureText(separator).width;
  const timeW = ctx.measureText(timeText).width;
  const totalW = valueW + sepW + timeW;
  let tx = hoverX - totalW / 2;
  const minX = pad.left + 4;
  const dotRightEdge = liveDotX != null ? liveDotX + 7 : layout.w - pad.right;
  const maxX = dotRightEdge - totalW;
  if (tx < minX) tx = minX;
  if (tx > maxX) tx = maxX;
  const ty = pad.top + (tooltipY ?? 14) + 10;
  ctx.textAlign = "left";
  if (tooltipOutline) {
    ctx.strokeStyle = palette.tooltipBg;
    ctx.lineWidth = 3;
    ctx.lineJoin = "round";
    ctx.strokeText(valueText, tx, ty);
    ctx.strokeText(separator + timeText, tx + valueW, ty);
  }
  ctx.fillStyle = palette.tooltipText;
  ctx.fillText(valueText, tx, ty);
  ctx.fillStyle = palette.gridLabel;
  ctx.fillText(separator + timeText, tx + valueW, ty);
  ctx.restore();
}
function drawMultiCrosshair(ctx, layout, palette, hoverX, hoverTime, entries, formatValue, formatTime, scrubOpacity, tooltipY, tooltipOutline, liveDotX, showTime = true) {
  if (scrubOpacity < 0.01 || entries.length === 0) return;
  const { h, pad, toY } = layout;
  ctx.save();
  ctx.globalAlpha = scrubOpacity * 0.5;
  ctx.strokeStyle = palette.crosshairLine;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(hoverX, pad.top);
  ctx.lineTo(hoverX, h - pad.bottom);
  ctx.stroke();
  ctx.restore();
  const dotRadius = 4 * Math.min(scrubOpacity * 3, 1);
  if (dotRadius > 0.5) {
    ctx.globalAlpha = 1;
    for (const entry of entries) {
      const y = toY(entry.value);
      ctx.beginPath();
      ctx.arc(hoverX, y, dotRadius, 0, Math.PI * 2);
      ctx.fillStyle = entry.color;
      ctx.fill();
    }
  }
  if (scrubOpacity < 0.1 || layout.w < 300) return;
  ctx.save();
  ctx.globalAlpha = scrubOpacity;
  ctx.font = '400 13px "SF Mono", Menlo, monospace';
  ctx.textAlign = "left";
  const timeText = showTime ? formatTime(hoverTime) : "";
  const sep = "  \xB7  ";
  const dotInline = " ";
  const segments = showTime ? [{ text: timeText, color: palette.gridLabel }] : [];
  for (const [index, e] of entries.entries()) {
    if (showTime || index > 0) segments.push({ text: sep, color: palette.gridLabel });
    segments.push({ text: dotInline, color: e.color, isDot: true });
    const label = e.label ? `${e.label} ` : "";
    if (label) segments.push({ text: label, color: palette.gridLabel });
    segments.push({ text: formatValue(e.value), color: palette.tooltipText });
  }
  let totalW = 0;
  const segWidths = [];
  for (const seg of segments) {
    const w = seg.isDot ? 12 : ctx.measureText(seg.text).width;
    segWidths.push(w);
    totalW += w;
  }
  let tx = hoverX - totalW / 2;
  const minX = pad.left + 4;
  const dotRightEdge = liveDotX != null ? liveDotX + 7 : layout.w - pad.right;
  const maxX = dotRightEdge - totalW;
  if (tx < minX) tx = minX;
  if (tx > maxX) tx = maxX;
  const ty = pad.top + (tooltipY ?? 14) + 10;
  if (tooltipOutline !== false) {
    ctx.strokeStyle = palette.tooltipBg;
    ctx.lineWidth = 3;
    ctx.lineJoin = "round";
    let ox2 = tx;
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      if (!seg.isDot) {
        ctx.strokeText(seg.text, ox2, ty);
      }
      ox2 += segWidths[i];
    }
  }
  let ox = tx;
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    if (seg.isDot) {
      ctx.beginPath();
      ctx.arc(ox + 4, ty - 4, 3.5, 0, Math.PI * 2);
      ctx.fillStyle = seg.color;
      ctx.fill();
    } else {
      ctx.fillStyle = seg.color;
      ctx.fillText(seg.text, ox, ty);
    }
    ox += segWidths[i];
  }
  ctx.restore();
}

// src/math/lerp.ts
function lerp(current, target, speed, dt = 16.67) {
  const factor = 1 - (1 - speed) ** (dt / 16.67);
  return current + (target - current) * factor;
}

// src/draw/dot.ts
var PULSE_INTERVAL = 1500;
var PULSE_DURATION = 900;
var MULTI_PULSE_MIN_RADIUS = 6;
var MULTI_PULSE_STAGGER_MS = 200;
function lerpColor(a, b, t) {
  const r = Math.round(a[0] + (b[0] - a[0]) * t);
  const g = Math.round(a[1] + (b[1] - a[1]) * t);
  const bl = Math.round(a[2] + (b[2] - a[2]) * t);
  return `rgb(${r},${g},${bl})`;
}
function drawDot(ctx, x, y, palette, pulse = true, scrubAmount = 0, now_ms = performance.now()) {
  const baseAlpha = ctx.globalAlpha;
  const dim = scrubAmount * 0.7;
  if (pulse && dim < 0.3) {
    const t = now_ms % PULSE_INTERVAL / PULSE_DURATION;
    if (t < 1) {
      const radius = 9 + t * 12;
      const pulseAlpha = 0.35 * (1 - t) * (1 - dim * 3);
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.strokeStyle = palette.line;
      ctx.lineWidth = 1.5;
      ctx.globalAlpha = baseAlpha * pulseAlpha;
      ctx.stroke();
    }
  }
  const outerRgb = parseColorRgb(palette.badgeOuterBg);
  ctx.save();
  ctx.globalAlpha = baseAlpha;
  ctx.shadowColor = palette.badgeOuterShadow;
  ctx.shadowBlur = 6 * (1 - dim);
  ctx.shadowOffsetY = 1;
  ctx.beginPath();
  ctx.arc(x, y, 6.5, 0, Math.PI * 2);
  ctx.fillStyle = palette.badgeOuterBg;
  ctx.fill();
  ctx.restore();
  ctx.globalAlpha = baseAlpha;
  ctx.beginPath();
  ctx.arc(x, y, 3.5, 0, Math.PI * 2);
  if (dim > 0.01) {
    const lineRgb = parseColorRgb(palette.line);
    ctx.fillStyle = lerpColor(lineRgb, outerRgb, dim);
  } else {
    ctx.fillStyle = palette.line;
  }
  ctx.fill();
}
function drawMultiDot(ctx, x, y, color, pulse = true, now_ms = performance.now(), radius = 3, pulseSize = 19) {
  const baseAlpha = ctx.globalAlpha;
  if (pulse) {
    const t = now_ms % PULSE_INTERVAL / PULSE_DURATION;
    if (t < 1) {
      const ringRadius = MULTI_PULSE_MIN_RADIUS + t * (pulseSize - MULTI_PULSE_MIN_RADIUS);
      const pulseAlpha = 0.3 * (1 - t);
      ctx.beginPath();
      ctx.arc(x, y, ringRadius, 0, Math.PI * 2);
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      ctx.globalAlpha = baseAlpha * pulseAlpha;
      ctx.stroke();
    }
  }
  ctx.globalAlpha = baseAlpha;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
}
function drawSimpleDot(ctx, x, y, color, radius = 3) {
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
}
function drawSeriesEndpointMarker(ctx, x, y, marker) {
  const radius = marker.radius ?? 7;
  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fillStyle = marker.fillColor;
  ctx.fill();
  ctx.beginPath();
  ctx.strokeStyle = marker.symbolColor;
  ctx.lineWidth = 1.75;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  if (marker.symbol === "check") {
    ctx.moveTo(x - 3.2, y);
    ctx.lineTo(x - 0.8, y + 2.4);
    ctx.lineTo(x + 3.8, y - 2.8);
  } else {
    ctx.moveTo(x - 2.6, y - 2.6);
    ctx.lineTo(x + 2.6, y + 2.6);
    ctx.moveTo(x + 2.6, y - 2.6);
    ctx.lineTo(x - 2.6, y + 2.6);
  }
  ctx.stroke();
  ctx.restore();
}
function drawArrows(ctx, x, y, momentum, palette, arrows, dt, now_ms = performance.now()) {
  const baseAlpha = ctx.globalAlpha;
  const upTarget = momentum === "up" ? 1 : 0;
  const downTarget = momentum === "down" ? 1 : 0;
  const canFadeInUp = arrows.down < 0.02;
  const canFadeInDown = arrows.up < 0.02;
  arrows.up = lerp(arrows.up, canFadeInUp ? upTarget : 0, upTarget > arrows.up ? 0.08 : 0.04, dt);
  arrows.down = lerp(
    arrows.down,
    canFadeInDown ? downTarget : 0,
    downTarget > arrows.down ? 0.08 : 0.04,
    dt
  );
  if (arrows.up < 0.01) arrows.up = 0;
  if (arrows.down < 0.01) arrows.down = 0;
  if (arrows.up > 0.99) arrows.up = 1;
  if (arrows.down > 0.99) arrows.down = 1;
  const cycle = now_ms % 1400 / 1400;
  const drawChevrons = (dir, opacity) => {
    if (opacity < 0.01) return;
    const baseX = x + 19;
    const baseY = y;
    ctx.save();
    ctx.strokeStyle = palette.gridLabel;
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    for (let i = 0; i < 2; i++) {
      const start = i * 0.2;
      const dur = 0.35;
      const localT = cycle - start;
      const wave = localT >= 0 && localT < dur ? Math.sin(localT / dur * Math.PI) : 0;
      const pulse = 0.3 + 0.7 * wave;
      ctx.globalAlpha = baseAlpha * opacity * pulse;
      const nudge = dir === -1 ? -3 : 3;
      const cy = baseY + dir * (i * 8 - 4) + nudge;
      ctx.beginPath();
      ctx.moveTo(baseX - 5, cy - dir * 3.5);
      ctx.lineTo(baseX, cy);
      ctx.lineTo(baseX + 5, cy - dir * 3.5);
      ctx.stroke();
    }
    ctx.restore();
  };
  drawChevrons(-1, arrows.up);
  drawChevrons(1, arrows.down);
  ctx.globalAlpha = baseAlpha;
}

// src/math/spline.ts
function drawSpline(ctx, pts) {
  if (pts.length < 2) return;
  if (pts.length === 2) {
    ctx.lineTo(pts[1][0], pts[1][1]);
    return;
  }
  const { intervals, tangents } = monotoneSplineGeometry(pts);
  for (let i = 0; i < pts.length - 1; i++) {
    const hi = intervals[i];
    ctx.bezierCurveTo(
      pts[i][0] + hi / 3,
      pts[i][1] + tangents[i] * hi / 3,
      pts[i + 1][0] - hi / 3,
      pts[i + 1][1] - tangents[i + 1] * hi / 3,
      pts[i + 1][0],
      pts[i + 1][1]
    );
  }
}
function sampleSplineYAtX(pts, x) {
  if (pts.length === 0) return null;
  if (x <= pts[0][0]) return pts[0][1];
  if (x >= pts[pts.length - 1][0]) return pts[pts.length - 1][1];
  const index = findSegmentIndex(pts, x);
  const { intervals, tangents } = monotoneSplineGeometry(pts);
  const interval = intervals[index];
  if (interval === 0) return pts[index + 1][1];
  const progress = (x - pts[index][0]) / interval;
  const progressSquared = progress * progress;
  const progressCubed = progressSquared * progress;
  const startWeight = 2 * progressCubed - 3 * progressSquared + 1;
  const startTangentWeight = progressCubed - 2 * progressSquared + progress;
  const endWeight = -2 * progressCubed + 3 * progressSquared;
  const endTangentWeight = progressCubed - progressSquared;
  return startWeight * pts[index][1] + startTangentWeight * interval * tangents[index] + endWeight * pts[index + 1][1] + endTangentWeight * interval * tangents[index + 1];
}
function findSegmentIndex(pts, x) {
  let low = 0;
  let high = pts.length - 1;
  while (high - low > 1) {
    const middle = low + high >> 1;
    if (pts[middle][0] <= x) low = middle;
    else high = middle;
  }
  return low;
}
function monotoneSplineGeometry(pts) {
  const n = pts.length;
  const delta = new Array(n - 1);
  const intervals = new Array(n - 1);
  for (let i = 0; i < n - 1; i++) {
    intervals[i] = pts[i + 1][0] - pts[i][0];
    delta[i] = intervals[i] === 0 ? 0 : (pts[i + 1][1] - pts[i][1]) / intervals[i];
  }
  const tangents = new Array(n);
  tangents[0] = delta[0];
  tangents[n - 1] = delta[n - 2];
  for (let i = 1; i < n - 1; i++) {
    tangents[i] = delta[i - 1] * delta[i] <= 0 ? 0 : (delta[i - 1] + delta[i]) / 2;
  }
  for (let i = 0; i < n - 1; i++) {
    if (delta[i] === 0) {
      tangents[i] = 0;
      tangents[i + 1] = 0;
      continue;
    }
    const alpha = tangents[i] / delta[i];
    const beta = tangents[i + 1] / delta[i];
    const magnitudeSquared = alpha * alpha + beta * beta;
    if (magnitudeSquared > 9) {
      const scale = 3 / Math.sqrt(magnitudeSquared);
      tangents[i] = scale * alpha * delta[i];
      tangents[i + 1] = scale * beta * delta[i];
    }
  }
  return { intervals, tangents };
}

// src/draw/loadingShape.ts
var LOADING_AMPLITUDE_RATIO = 0.07;
var LOADING_SCROLL_SPEED = 1e-3;
function loadingY(t, centerY, amplitude, scroll) {
  return centerY + amplitude * (Math.sin(t * 9.4 + scroll) * 0.55 + Math.sin(t * 15.7 + scroll * 1.3) * 0.3 + Math.sin(t * 4.2 + scroll * 0.7) * 0.15);
}
function loadingBreath(now_ms) {
  return 0.22 + 0.08 * Math.sin(now_ms / 1200 * Math.PI);
}

// src/draw/empty.ts
function drawEmpty(ctx, w, h, pad, palette, alpha = 1, now_ms = 0, skipLine = false, emptyText) {
  const chartW = w - pad.left - pad.right;
  const chartH = h - pad.top - pad.bottom;
  const centerY = pad.top + chartH / 2;
  const cx = pad.left + chartW / 2;
  const text = emptyText ?? "No data to display";
  const amplitude = chartH * LOADING_AMPLITUDE_RATIO;
  ctx.save();
  ctx.font = "400 12px system-ui, -apple-system, sans-serif";
  const textW = ctx.measureText(text).width;
  const gapHalf = textW / 2 + 20;
  const fadeW = 30;
  if (!skipLine) {
    const scroll = now_ms * LOADING_SCROLL_SPEED;
    const breath = loadingBreath(now_ms);
    const numPts = 32;
    const pts = [];
    for (let i = 0; i <= numPts; i++) {
      const t = i / numPts;
      const x = pad.left + t * chartW;
      const y = loadingY(t, centerY, amplitude, scroll);
      pts.push([x, y]);
    }
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    drawSpline(ctx, pts);
    ctx.strokeStyle = palette.gridLabel;
    ctx.lineWidth = palette.lineWidth;
    ctx.globalAlpha = breath * alpha;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
  }
  ctx.save();
  ctx.globalCompositeOperation = "destination-out";
  const gapLeft = cx - gapHalf - fadeW;
  const gapRight = cx + gapHalf + fadeW;
  const eraseGrad = ctx.createLinearGradient(gapLeft, 0, gapRight, 0);
  eraseGrad.addColorStop(0, "rgba(0,0,0,0)");
  eraseGrad.addColorStop(fadeW / (gapRight - gapLeft), "rgba(0,0,0,1)");
  eraseGrad.addColorStop(1 - fadeW / (gapRight - gapLeft), "rgba(0,0,0,1)");
  eraseGrad.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = eraseGrad;
  ctx.globalAlpha = alpha;
  const eraseH = amplitude * 2 + palette.lineWidth + 6;
  ctx.fillRect(gapLeft, centerY - eraseH / 2, gapRight - gapLeft, eraseH);
  ctx.restore();
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.globalAlpha = 0.35 * alpha;
  ctx.fillStyle = palette.gridLabel;
  ctx.fillText(text, cx, centerY);
  ctx.restore();
}

// src/draw/grid.ts
function pickInterval(valRange, pxPerUnit, minGap, prev) {
  if (prev > 0) {
    const px = prev * pxPerUnit;
    if (px >= minGap * 0.5 && px <= minGap * 4) return prev;
  }
  const divisorSets = [
    [2, 2.5, 2],
    [2, 2, 2.5],
    [2.5, 2, 2]
  ];
  let best = Infinity;
  for (const divs of divisorSets) {
    let span = 10 ** Math.ceil(Math.log10(valRange));
    let i = 0;
    while (span / divs[i % 3] * pxPerUnit >= minGap) {
      span /= divs[i % 3];
      i++;
    }
    if (span < best) best = span;
  }
  return best === Infinity ? valRange / 5 : best;
}
var FADE_IN = 0.18;
var FADE_OUT = 0.12;
var MAX_VALUE_AXIS_LABELS = 10;
var MAX_MEASURED_LABELS = 256;
function valueAxisInterval(layout, previousInterval) {
  const chartH = layout.h - layout.pad.top - layout.pad.bottom;
  const minimumGap = Math.max(36, chartH / (MAX_VALUE_AXIS_LABELS - 1));
  const pxPerUnit = chartH / layout.valRange;
  const eligiblePrevious = previousInterval * pxPerUnit >= minimumGap ? previousInterval : 0;
  return pickInterval(layout.valRange, pxPerUnit, minimumGap, eligiblePrevious);
}
function resolveGridLabelPaddingRight(ctx, layout, palette, formatValue, state, options, minimumRight) {
  const labelRightMargin = finiteNumber(options?.labelRightMargin);
  if (labelRightMargin === void 0) {
    state.measurement = void 0;
    return void 0;
  }
  const labels = gridLabelsForMeasurement(layout, state, formatValue);
  const gridEndGap = finiteNumber(options?.gridEndGap) ?? 0;
  const key = [
    palette.labelFont,
    labels.join(""),
    labelRightMargin,
    gridEndGap,
    minimumRight
  ].join("|");
  const cached = state.measurement;
  if (cached?.key === key) return cached.right;
  ctx.save();
  ctx.font = palette.labelFont;
  const textWidths = cached?.textWidths ?? /* @__PURE__ */ new Map();
  let maxTextWidth = 0;
  for (const label of labels) {
    let width = textWidths.get(label);
    if (width === void 0) {
      width = ctx.measureText(label).width;
      if (textWidths.size >= MAX_MEASURED_LABELS) {
        const oldestLabel = textWidths.keys().next().value;
        if (oldestLabel !== void 0) textWidths.delete(oldestLabel);
      }
      textWidths.set(label, width);
    }
    maxTextWidth = Math.max(maxTextWidth, width);
  }
  ctx.restore();
  const right = Math.max(minimumRight, Math.ceil(labelRightMargin + maxTextWidth + gridEndGap));
  state.measurement = {
    key,
    maxTextWidth,
    right,
    textWidths,
    labelRightMargin,
    gridEndGap
  };
  return right;
}
function gridLabelsForMeasurement(layout, state, formatValue) {
  const { h, pad, valRange, minVal, maxVal } = layout;
  const chartH = h - pad.top - pad.bottom;
  if (chartH <= 0 || valRange <= 0) return [];
  const coarse = valueAxisInterval(layout, state.interval);
  state.interval = coarse;
  const labelDecimals = fractionDigitsForStep(coarse);
  const keys = new Set(state.labels.keys());
  const first = Math.ceil(minVal / coarse) * coarse;
  for (let value = first; value <= maxVal; value += coarse) {
    keys.add(Math.round(value * 1e9));
  }
  return [...keys].map((key) => formatValue(key / 1e9, labelDecimals, coarse));
}
function fractionDigitsForStep(step) {
  if (!Number.isFinite(step) || step <= 0 || step >= 1) return 0;
  const leadingFractionDigits = Math.max(0, Math.ceil(-Math.log10(step)));
  const scaled = step * 10 ** leadingFractionDigits;
  const isSingleSignificantDigit = Number.isFinite(scaled) && Math.abs(scaled - Math.round(scaled)) <= 1e-9;
  return Math.min(8, leadingFractionDigits + (isSingleSignificantDigit ? 0 : 1));
}
function finiteNumber(value) {
  return value !== void 0 && Number.isFinite(value) ? value : void 0;
}
function drawGrid(ctx, layout, palette, formatValue, state, dt, axisBadgeY) {
  const { w, h, pad, valRange, minVal, maxVal, toY } = layout;
  const chartH = h - pad.top - pad.bottom;
  if (chartH <= 0 || valRange <= 0) return;
  const coarse = valueAxisInterval(layout, state.interval);
  state.interval = coarse;
  const labelDecimals = fractionDigitsForStep(coarse);
  const fadeZone = 32;
  const edgeAlpha = (y) => {
    const fromEdge = Math.min(y - pad.top, h - pad.bottom - y);
    if (fromEdge >= fadeZone) return 1;
    if (fromEdge <= 0) return 0;
    return fromEdge / fadeZone;
  };
  const targets = /* @__PURE__ */ new Map();
  const first = Math.ceil(minVal / coarse) * coarse;
  for (let val = first; val <= maxVal; val += coarse) {
    const y = toY(val);
    if (y < pad.top - 2 || y > h - pad.bottom + 2) continue;
    const target = edgeAlpha(y);
    const key = Math.round(val * 1e9);
    targets.set(key, target);
  }
  for (const [key, alpha] of state.labels) {
    const target = targets.get(key) ?? 0;
    const speed = target >= alpha ? FADE_IN : FADE_OUT;
    let next = lerp(alpha, target, speed, dt);
    if (Math.abs(next - target) < 0.02) next = target;
    if (next < 0.01 && target === 0) {
      state.labels.delete(key);
    } else {
      state.labels.set(key, next);
    }
  }
  for (const [key, target] of targets) {
    if (!state.labels.has(key)) {
      state.labels.set(key, target * FADE_IN);
    }
  }
  const baseAlpha = ctx.globalAlpha;
  ctx.setLineDash([1, 3]);
  ctx.lineWidth = 1;
  ctx.font = palette.labelFont;
  const measurement = state.measurement;
  for (const [key, alpha] of state.labels) {
    if (alpha < 0.02) continue;
    const val = key / 1e9;
    const y = toY(val);
    if (y < pad.top - 10 || y > h - pad.bottom + 10) continue;
    ctx.save();
    ctx.globalAlpha = baseAlpha * alpha;
    ctx.textAlign = measurement === void 0 ? "left" : "right";
    ctx.strokeStyle = palette.gridLine;
    ctx.beginPath();
    ctx.moveTo(pad.left, y);
    const labelLeft = measurement === void 0 ? w - pad.right + 8 : w - measurement.labelRightMargin - measurement.maxTextWidth;
    const gridEnd = measurement === void 0 ? w - pad.right : labelLeft - measurement.gridEndGap;
    ctx.lineTo(gridEnd, y);
    ctx.stroke();
    if (axisBadgeY === void 0 || Math.abs(y - axisBadgeY) > 16) {
      ctx.fillStyle = palette.gridLabel;
      ctx.fillText(
        formatValue(val, labelDecimals, coarse),
        measurement === void 0 ? labelLeft : w - measurement.labelRightMargin,
        y + 4
      );
    }
    ctx.restore();
  }
  ctx.setLineDash([]);
}

// src/draw/line.ts
function parseRgba(color) {
  const hex = color.match(/^#([0-9a-f]{3,8})$/i);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
      1
    ];
  }
  const rgba2 = color.match(/rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)/);
  if (rgba2) return [+rgba2[1], +rgba2[2], +rgba2[3], +rgba2[4]];
  const rgb = color.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgb) return [+rgb[1], +rgb[2], +rgb[3], 1];
  return [128, 128, 128, 1];
}
function blendColor2(c1, c2, t) {
  if (t <= 0) return c1;
  if (t >= 1) return c2;
  const [r1, g1, b1, a1] = parseRgba(c1);
  const [r2, g2, b2, a2] = parseRgba(c2);
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  const a = a1 + (a2 - a1) * t;
  if (a >= 0.995) return `rgb(${r},${g},${b})`;
  return `rgba(${r},${g},${b},${a.toFixed(3)})`;
}
function renderCurve(ctx, layout, palette, pts, showFill, lineAlpha = 1, fillAlpha = 1, strokeColor, curve = "monotone") {
  const { h, pad } = layout;
  const baseAlpha = ctx.globalAlpha;
  if (showFill && fillAlpha > 0.01) {
    ctx.globalAlpha = baseAlpha * fillAlpha;
    const grad = ctx.createLinearGradient(0, pad.top, 0, h - pad.bottom);
    grad.addColorStop(0, palette.fillTop);
    grad.addColorStop(1, palette.fillBottom);
    ctx.beginPath();
    ctx.moveTo(pts[0][0], h - pad.bottom);
    ctx.lineTo(pts[0][0], pts[0][1]);
    drawCurvePath(ctx, pts, curve);
    ctx.lineTo(pts[pts.length - 1][0], h - pad.bottom);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();
  }
  ctx.globalAlpha = baseAlpha * lineAlpha;
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  drawCurvePath(ctx, pts, curve);
  ctx.strokeStyle = strokeColor ?? palette.line;
  ctx.lineWidth = palette.lineWidth;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.stroke();
  ctx.globalAlpha = baseAlpha;
}
function drawCurvePath(ctx, points, curve) {
  if (curve === "monotone") {
    drawSpline(ctx, points);
    return;
  }
  if (curve === "rounded-linear") {
    drawRoundedLinearPath(ctx, points);
    return;
  }
  for (let index = 1; index < points.length; index += 1) {
    ctx.lineTo(points[index][0], points[index][1]);
  }
}
function sampleLineYAtX(points, x, curve) {
  if (points.length === 0) return null;
  if (curve === "monotone") return sampleSplineYAtX(points, x);
  if (curve === "rounded-linear") return sampleRoundedLinearYAtX(points, x);
  return sampleLinearYAtX(points, x);
}
var ROUNDED_LINEAR_CORNER_RADIUS = 4;
function drawRoundedLinearPath(ctx, points) {
  for (const segment of roundedLinearSegments(points)) {
    if (segment.control) {
      ctx.quadraticCurveTo(segment.control[0], segment.control[1], segment.end[0], segment.end[1]);
    } else {
      ctx.lineTo(segment.end[0], segment.end[1]);
    }
  }
}
function roundedLinearSegments(points) {
  if (points.length < 2) return [];
  const segments = [];
  const lastHistoricalIndex = points.length - 2;
  let cursor = points[0];
  for (let index = 1; index < lastHistoricalIndex; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    const next = points[index + 1];
    const incomingLength = Math.hypot(current[0] - previous[0], current[1] - previous[1]);
    const outgoingLength = Math.hypot(next[0] - current[0], next[1] - current[1]);
    const cornerDistance = Math.min(
      ROUNDED_LINEAR_CORNER_RADIUS,
      incomingLength / 2,
      outgoingLength / 2
    );
    if (cornerDistance === 0) {
      segments.push({ start: cursor, end: current });
      cursor = current;
      continue;
    }
    const before = [
      current[0] - (current[0] - previous[0]) / incomingLength * cornerDistance,
      current[1] - (current[1] - previous[1]) / incomingLength * cornerDistance
    ];
    const after = [
      current[0] + (next[0] - current[0]) / outgoingLength * cornerDistance,
      current[1] + (next[1] - current[1]) / outgoingLength * cornerDistance
    ];
    segments.push({ start: cursor, end: before });
    segments.push({ start: before, control: current, end: after });
    cursor = after;
  }
  const lastHistorical = points[lastHistoricalIndex];
  segments.push({ start: cursor, end: lastHistorical });
  segments.push({ start: lastHistorical, end: points[points.length - 1] });
  return segments;
}
function sampleRoundedLinearYAtX(points, x) {
  const segments = roundedLinearSegments(points);
  const segment = segments.find((entry) => x <= Math.max(entry.start[0], entry.end[0])) ?? segments[segments.length - 1];
  if (!segment) return points[0][1];
  if (!segment.control) return sampleLinearSegmentYAtX(segment.start, segment.end, x);
  let low = 0;
  let high = 1;
  for (let iteration = 0; iteration < 24; iteration += 1) {
    const progress = (low + high) / 2;
    const sampledX = quadraticCoordinate(
      segment.start[0],
      segment.control[0],
      segment.end[0],
      progress
    );
    if (sampledX < x) low = progress;
    else high = progress;
  }
  return quadraticCoordinate(
    segment.start[1],
    segment.control[1],
    segment.end[1],
    (low + high) / 2
  );
}
function sampleLinearYAtX(points, x) {
  if (x <= points[0][0]) return points[0][1];
  for (let index = 1; index < points.length; index += 1) {
    if (x <= points[index][0]) return sampleLinearSegmentYAtX(points[index - 1], points[index], x);
  }
  return points[points.length - 1][1];
}
function sampleLinearSegmentYAtX(start, end, x) {
  const width = end[0] - start[0];
  if (width === 0) return end[1];
  const progress = Math.max(0, Math.min(1, (x - start[0]) / width));
  return start[1] + (end[1] - start[1]) * progress;
}
function quadraticCoordinate(start, control, end, progress) {
  const inverse = 1 - progress;
  return inverse * inverse * start + 2 * inverse * progress * control + progress * progress * end;
}
function drawLine(ctx, layout, palette, visible, smoothValue, now, showFill, scrubX, scrubAmount = 0, chartReveal = 1, now_ms = 0, colorBlend = 1, skipDashLine = false, fillScale = 1, curve = "monotone", dashLineColor, futureStartTime, futureOpacity = 0.6) {
  const { h, pad, toX, toY, chartW, chartH } = layout;
  const incomingAlpha = ctx.globalAlpha;
  const centerY = pad.top + chartH / 2;
  const amplitude = chartH * LOADING_AMPLITUDE_RATIO;
  const scroll = now_ms * LOADING_SCROLL_SPEED;
  const morphY = chartReveal < 1 ? (rawY, x) => {
    const t = Math.max(0, Math.min(1, (x - pad.left) / chartW));
    const centerDist = Math.abs(t - 0.5) * 2;
    const localReveal = Math.max(0, Math.min(1, (chartReveal - centerDist * 0.4) / 0.6));
    const baseY = loadingY(t, centerY, amplitude, scroll);
    return baseY + (rawY - baseY) * localReveal;
  } : (rawY, _x) => rawY;
  const pts = visible.map((p) => {
    const x = toX(p.time);
    const y = morphY(toY(p.value), x);
    return [x, y];
  });
  const liveTipX = toX(now);
  const fullRightX = pad.left + chartW;
  const tipX = chartReveal < 1 ? liveTipX + (fullRightX - liveTipX) * (1 - chartReveal) : liveTipX;
  pts.push([tipX, morphY(toY(smoothValue), tipX)]);
  if (pts.length < 2) return;
  let lineAlpha = 1;
  let fillAlpha = fillScale;
  if (chartReveal < 1) {
    const breath = loadingBreath(now_ms);
    lineAlpha = breath + (1 - breath) * chartReveal;
    fillAlpha = chartReveal * fillScale;
  }
  const colorT = Math.min(1, chartReveal * 3) * colorBlend;
  const strokeColor = chartReveal < 1 || colorBlend < 1 ? blendColor2(palette.gridLabel, palette.line, colorT) : void 0;
  const isScrubbing = scrubX !== null;
  ctx.save();
  ctx.beginPath();
  ctx.rect(pad.left - 1, pad.top, chartW + 2, chartH);
  ctx.clip();
  const renderVisibleCurve = (minX, maxX, opacity, applyScrub) => {
    ctx.save();
    ctx.globalAlpha = incomingAlpha * opacity;
    if (minX !== void 0 || maxX !== void 0) {
      const left = minX ?? 0;
      const right = maxX ?? layout.w;
      ctx.beginPath();
      ctx.rect(left, 0, Math.max(0, right - left), h);
      ctx.clip();
    }
    if (applyScrub && isScrubbing && scrubX !== null) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, scrubX, h);
      ctx.clip();
      renderCurve(ctx, layout, palette, pts, showFill, lineAlpha, fillAlpha, strokeColor, curve);
      ctx.restore();
      ctx.save();
      ctx.beginPath();
      ctx.rect(scrubX, 0, layout.w - scrubX, h);
      ctx.clip();
      ctx.globalAlpha = incomingAlpha * opacity * (1 - scrubAmount * 0.6);
      renderCurve(ctx, layout, palette, pts, showFill, lineAlpha, fillAlpha, strokeColor, curve);
      ctx.restore();
    } else {
      renderCurve(ctx, layout, palette, pts, showFill, lineAlpha, fillAlpha, strokeColor, curve);
    }
    ctx.restore();
  };
  if (futureStartTime === void 0) {
    renderVisibleCurve(void 0, void 0, 1, true);
  } else {
    const futureStartX = toX(futureStartTime);
    const interactionOpacity = isScrubbing ? 1 - scrubAmount * 0.6 : 1;
    renderVisibleCurve(void 0, futureStartX, 1, true);
    renderVisibleCurve(
      futureStartX,
      void 0,
      Math.max(0, Math.min(1, futureOpacity)) * interactionOpacity,
      false
    );
  }
  ctx.restore();
  if (!skipDashLine) {
    const realCurrentY = Math.max(pad.top, Math.min(h - pad.bottom, toY(smoothValue)));
    const currentY = chartReveal < 1 ? centerY + (realCurrentY - centerY) * chartReveal : realCurrentY;
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = dashLineColor ?? palette.dashLine;
    ctx.lineWidth = 1;
    const dashBase = isScrubbing ? 1 - scrubAmount * 0.2 : 1;
    ctx.globalAlpha = incomingAlpha * (chartReveal < 1 ? dashBase * chartReveal : dashBase);
    ctx.beginPath();
    ctx.moveTo(pad.left, currentY);
    ctx.lineTo(layout.w - pad.right, currentY);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  ctx.globalAlpha = incomingAlpha;
  const last = pts[pts.length - 1];
  last[1] = Math.max(10, Math.min(h - 10, last[1]));
  return pts;
}

// src/math/interpolate.ts
function interpolateAtTime(points, time) {
  if (points.length === 0) return null;
  if (time <= points[0].time) return points[0].value;
  if (time >= points[points.length - 1].time) return points[points.length - 1].value;
  let lo = 0;
  let hi = points.length - 1;
  while (hi - lo > 1) {
    const mid = lo + hi >> 1;
    if (points[mid].time <= time) lo = mid;
    else hi = mid;
  }
  const p1 = points[lo];
  const p2 = points[hi];
  const dt = p2.time - p1.time;
  if (dt === 0) return p1.value;
  const t = (time - p1.time) / dt;
  return p1.value + (p2.value - p1.value) * t;
}
function interpolateRenderedLineAtTime(points, liveTime, liveValue, time) {
  if (points.length === 0) return null;
  const lastPoint = points[points.length - 1];
  if (liveTime < lastPoint.time || time <= lastPoint.time) {
    return interpolateAtTime(points, time);
  }
  if (time >= liveTime || liveTime === lastPoint.time) return liveValue;
  const progress = (time - lastPoint.time) / (liveTime - lastPoint.time);
  return lastPoint.value + (liveValue - lastPoint.value) * progress;
}

// src/draw/markers.ts
function condenseChartMarkers(markers, toX, distancePx) {
  if (distancePx <= 0) {
    return markers.map((marker) => ({ marker, count: 1, x: toX(marker.time) }));
  }
  const groups = /* @__PURE__ */ new Map();
  for (const marker of markers) {
    const key = JSON.stringify([
      marker.kind,
      marker.seriesId ?? "",
      marker.side ?? "",
      marker.color ?? "",
      marker.backgroundColor ?? ""
    ]);
    const group = groups.get(key) ?? [];
    group.push({ marker, x: toX(marker.time) });
    groups.set(key, group);
  }
  const condensed = [];
  for (const group of groups.values()) {
    group.sort((left, right) => left.x - right.x);
    let cluster;
    for (const position of group) {
      if (cluster === void 0) {
        cluster = createCluster(position);
        continue;
      }
      if (Math.abs(position.x - cluster.meanX) <= distancePx) {
        cluster = addToCluster(cluster, position);
        continue;
      }
      condensed.push(finalizeCluster(cluster));
      cluster = createCluster(position);
    }
    if (cluster !== void 0) condensed.push(finalizeCluster(cluster));
  }
  return condensed.sort((left, right) => left.x - right.x);
}
function drawChartMarkers(ctx, layout, markers, series, condenseDistancePx) {
  const visibleMarkers = markers.filter(
    (marker) => marker.time >= layout.leftEdge && marker.time <= layout.rightEdge
  );
  const condensed = condenseChartMarkers(visibleMarkers, layout.toX, condenseDistancePx);
  for (const { marker, count, x } of condensed) {
    const padTop = layout.pad.top;
    if (x < layout.pad.left || x > layout.w - layout.pad.right) continue;
    const color = marker.color ?? (marker.kind === "trade" ? "#22c55e" : "#f0b033");
    if (marker.kind === "boost") {
      ctx.save();
      ctx.setLineDash([3, 4]);
      ctx.strokeStyle = color;
      ctx.globalAlpha = 0.65;
      ctx.beginPath();
      ctx.moveTo(x, padTop);
      ctx.lineTo(x, layout.h - layout.pad.bottom);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(x, padTop + 2);
      ctx.lineTo(x + 5, padTop + 8);
      ctx.lineTo(x, padTop + 14);
      ctx.lineTo(x - 5, padTop + 8);
      ctx.closePath();
      ctx.fill();
      drawMarkerCount(ctx, layout, x, padTop + 22, count, color);
      ctx.restore();
      continue;
    }
    const owner = series.find((entry) => entry.id === marker.seriesId && (entry.alpha ?? 1) >= 0.5);
    if (owner === void 0) continue;
    const value = interpolateAtTime(owner.visible, marker.time);
    if (value === null) continue;
    const y = layout.toY(value);
    if (y < layout.pad.top || y > layout.h - layout.pad.bottom) continue;
    drawTradeMarkerIcon(ctx, x, y, marker);
    drawMarkerCount(ctx, layout, x, y - 14, count, color);
  }
}
function drawTradeMarkerIcon(ctx, x, y, marker) {
  const radius = 6;
  const iconSize = radius * 0.5;
  ctx.save();
  ctx.fillStyle = marker.backgroundColor ?? "#0d0d0d";
  ctx.beginPath();
  ctx.arc(x, y, radius + 1, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = marker.color ?? (marker.side === "sell" ? "rgba(239, 68, 68, 0.9)" : "rgba(34, 197, 94, 0.9)");
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "rgba(255, 255, 255, 0.9)";
  ctx.lineWidth = 1.5;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(x - iconSize, y);
  ctx.lineTo(x + iconSize, y);
  if (marker.side === "buy") {
    ctx.moveTo(x, y - iconSize);
    ctx.lineTo(x, y + iconSize);
  }
  ctx.stroke();
  ctx.restore();
}
function createCluster(position) {
  return {
    first: position.marker,
    count: 1,
    meanTime: position.marker.time,
    meanX: position.x
  };
}
function addToCluster(cluster, position) {
  const count = cluster.count + 1;
  return {
    ...cluster,
    count,
    meanTime: cluster.meanTime + (position.marker.time - cluster.meanTime) / count,
    meanX: cluster.meanX + (position.x - cluster.meanX) / count
  };
}
function finalizeCluster(cluster) {
  return {
    marker: { ...cluster.first, time: cluster.meanTime },
    count: cluster.count,
    x: cluster.meanX
  };
}
function drawMarkerCount(ctx, layout, x, y, count, color) {
  if (count < 2) return;
  const label = `\xD7${count}`;
  ctx.font = '600 10px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const width = Math.max(18, ctx.measureText(label).width + 8);
  const centerX = Math.max(
    layout.pad.left + width / 2,
    Math.min(layout.w - layout.pad.right - width / 2, x)
  );
  const centerY = Math.max(layout.pad.top + 8, Math.min(layout.h - layout.pad.bottom - 8, y));
  const left = centerX - width / 2;
  const top = centerY - 7;
  ctx.fillStyle = "rgba(15, 15, 15, 0.88)";
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(left, top, width, 14, 7);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.fillText(label, centerX, centerY + 0.5);
}

// src/draw/orderbook.ts
var GREEN = [34, 197, 94];
var RED = [239, 68, 68];
function isTuple(entry) {
  return !("size" in entry);
}
function entrySize(entry) {
  return isTuple(entry) ? entry[1] : entry.size;
}
function entryLabel(entry) {
  return isTuple(entry) ? void 0 : entry.label;
}
function entryColor(entry) {
  return isTuple(entry) ? void 0 : entry.color;
}
function entryId(entry) {
  return isTuple(entry) ? void 0 : entry.id;
}
function entryTime(entry) {
  return isTuple(entry) ? void 0 : entry.at;
}
function createOrderbookState(orderbook) {
  const state = {
    labels: [],
    spawnTimer: 0,
    smoothSpeed: BASE_SPEED,
    prevBidTotal: 0,
    prevAskTotal: 0,
    churnRate: 0,
    seenIds: /* @__PURE__ */ new Set(),
    seenIdsOrder: []
  };
  rememberOrderbookEntryIds(state, orderbook);
  return state;
}
function fenceOrderbookEntries(state, orderbook) {
  state.labels.length = 0;
  state.spawnTimer = 0;
  state.smoothSpeed = BASE_SPEED;
  state.prevBidTotal = 0;
  state.prevAskTotal = 0;
  state.churnRate = 0;
  rememberOrderbookEntryIds(state, orderbook);
}
function fenceOrderbookIds(state, ids) {
  for (const id of ids) rememberSeenId(state, id);
}
function rememberOrderbookEntryIds(state, orderbook) {
  if (orderbook === void 0) return;
  for (const entry of orderbook.bids) {
    const id = entryId(entry);
    if (id !== void 0) rememberSeenId(state, id);
  }
  for (const entry of orderbook.asks) {
    const id = entryId(entry);
    if (id !== void 0) rememberSeenId(state, id);
  }
}
var MAX_LABELS = 50;
var LABEL_LIFETIME = 6;
var SPAWN_INTERVAL = 40;
var MIN_LABEL_GAP = 22;
var BASE_SPEED = 60;
var MAX_SPEED = 160;
var SEEN_IDS_MAX = 5e3;
function mixColor(from, to, t) {
  const r = Math.round(from[0] + (to[0] - from[0]) * t);
  const g = Math.round(from[1] + (to[1] - from[1]) * t);
  const b = Math.round(from[2] + (to[2] - from[2]) * t);
  return `rgb(${r},${g},${b})`;
}
function drawOrderbook(ctx, layout, palette, orderbook, dt, state, swingMagnitude, nowSeconds) {
  const { pad, h, chartH } = layout;
  const dtSec = dt / 1e3;
  if (orderbook.bids.length === 0 && orderbook.asks.length === 0) return;
  let maxSize = 0;
  let bidTotal = 0;
  let askTotal = 0;
  for (const entry of orderbook.bids) {
    const size = entrySize(entry);
    bidTotal += size;
    if (size > maxSize) maxSize = size;
  }
  for (const entry of orderbook.asks) {
    const size = entrySize(entry);
    askTotal += size;
    if (size > maxSize) maxSize = size;
  }
  if (maxSize === 0) return;
  const prevTotal = state.prevBidTotal + state.prevAskTotal;
  let churnSignal = 0;
  if (prevTotal > 0) {
    const delta = Math.abs(bidTotal - state.prevBidTotal) + Math.abs(askTotal - state.prevAskTotal);
    churnSignal = Math.min(delta / prevTotal, 1);
  }
  state.prevBidTotal = bidTotal;
  state.prevAskTotal = askTotal;
  const churnLerp = churnSignal > state.churnRate ? 0.3 : 0.05;
  state.churnRate += (churnSignal - state.churnRate) * churnLerp;
  const activity = Math.max(Math.min(swingMagnitude * 5, 1), state.churnRate);
  const targetSpeed = BASE_SPEED + activity * (MAX_SPEED - BASE_SPEED);
  const speedLerp = 1 - 0.95 ** (dt / 16.67);
  state.smoothSpeed += (targetSpeed - state.smoothSpeed) * speedLerp;
  const speed = state.smoothSpeed;
  const labelX = pad.left + 8;
  const bottomY = h - pad.bottom - 6;
  const topY = pad.top;
  const bg = palette.bgRgb;
  state.spawnTimer += dt;
  while (state.spawnTimer >= SPAWN_INTERVAL && state.labels.length < MAX_LABELS) {
    state.spawnTimer -= SPAWN_INTERVAL;
    let tooClose = false;
    for (let j = 0; j < state.labels.length; j++) {
      if (Math.abs(state.labels[j].y - bottomY) < MIN_LABEL_GAP) {
        tooClose = true;
        break;
      }
    }
    if (tooClose) break;
    const allLevels = [];
    for (const entry of orderbook.bids) {
      const id = entryId(entry);
      if (id !== void 0 && state.seenIds.has(id)) continue;
      allLevels.push({
        size: entrySize(entry),
        green: true,
        label: entryLabel(entry),
        color: entryColor(entry),
        id,
        at: entryTime(entry)
      });
    }
    for (const entry of orderbook.asks) {
      const id = entryId(entry);
      if (id !== void 0 && state.seenIds.has(id)) continue;
      allLevels.push({
        size: entrySize(entry),
        green: false,
        label: entryLabel(entry),
        color: entryColor(entry),
        id,
        at: entryTime(entry)
      });
    }
    if (allLevels.length === 0) break;
    let totalWeight = 0;
    for (const l of allLevels) totalWeight += l.size;
    let r = Math.random() * totalWeight;
    let picked = allLevels[0];
    for (const l of allLevels) {
      r -= l.size;
      if (r <= 0) {
        picked = l;
        break;
      }
    }
    if (picked.id !== void 0) rememberSeenId(state, picked.id);
    const sizeRatio = picked.size / maxSize;
    const remainingLife = picked.at === void 0 || nowSeconds === void 0 ? LABEL_LIFETIME : Math.min(LABEL_LIFETIME, Math.max(0, picked.at + LABEL_LIFETIME - nowSeconds));
    if (remainingLife <= 0) continue;
    state.labels.push({
      y: bottomY,
      text: picked.label ?? `+ ${formatSize(picked.size)}`,
      green: picked.green,
      color: picked.color,
      life: remainingLife,
      maxLife: remainingLife,
      expiresAt: picked.at === void 0 ? void 0 : picked.at + LABEL_LIFETIME,
      intensity: 0.5 + sizeRatio * 0.5
    });
  }
  stepOrderbookLabels(state, dtSec, nowSeconds, speed, topY, bottomY);
  const baseAlpha = ctx.globalAlpha;
  ctx.save();
  ctx.font = '600 13px "SF Mono", Menlo, monospace';
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.globalAlpha = baseAlpha;
  const outlineColor = `rgb(${bg[0]},${bg[1]},${bg[2]})`;
  for (let i = 0; i < state.labels.length; i++) {
    const l = state.labels[i];
    const lifeRatio = l.life / l.maxLife;
    const fadeIn = Math.min((1 - lifeRatio) * 10, 1);
    const yRatio = (l.y - topY) / chartH;
    const fadeOut = yRatio < 0.45 ? yRatio / 0.45 : 1;
    const colorStrength = Math.max(0, l.intensity * fadeIn * fadeOut);
    const baseColor = l.green ? GREEN : RED;
    const fillColor = l.color ?? mixColor(baseColor, bg, 1 - colorStrength);
    const fillAlpha = l.color === void 0 ? baseAlpha : baseAlpha * colorStrength;
    ctx.strokeStyle = outlineColor;
    ctx.lineWidth = 4;
    ctx.lineJoin = "round";
    ctx.globalAlpha = baseAlpha * colorStrength;
    ctx.strokeText(l.text, labelX, l.y);
    ctx.fillStyle = fillColor;
    ctx.globalAlpha = fillAlpha;
    ctx.fillText(l.text, labelX, l.y);
  }
  ctx.globalAlpha = baseAlpha;
  ctx.restore();
}
function stepOrderbookLabels(state, dtSec, nowSeconds, speed, topY, bottomY) {
  const range = bottomY - topY;
  let writeIdx = 0;
  for (let i = 0; i < state.labels.length; i++) {
    const l = state.labels[i];
    l.life = l.expiresAt === void 0 || nowSeconds === void 0 ? l.life - dtSec : l.expiresAt - nowSeconds;
    if (l.life <= 0) continue;
    const yProgress = range > 0 ? (l.y - topY) / range : 1;
    l.y -= speed * (0.7 + 0.3 * yProgress) * dtSec;
    if (l.y < topY - 14) continue;
    state.labels[writeIdx++] = l;
  }
  state.labels.length = writeIdx;
}
function rememberSeenId(state, id) {
  if (state.seenIds.has(id)) return;
  state.seenIds.add(id);
  state.seenIdsOrder.push(id);
  while (state.seenIdsOrder.length > SEEN_IDS_MAX) {
    const evicted = state.seenIdsOrder.shift();
    if (evicted !== void 0) state.seenIds.delete(evicted);
  }
}
function formatSize(size) {
  if (size >= 10) return `$${Math.round(size)}`;
  if (size >= 1) return `$${size.toFixed(1)}`;
  return `$${size.toFixed(2)}`;
}

// src/draw/particles.ts
var MAX_PARTICLES = 80;
var PARTICLE_LIFETIME = 1;
var COOLDOWN_MS = 400;
var MAGNITUDE_THRESHOLD = 0.08;
var MAX_BURSTS = 3;
function createParticleState(suppressInitialBurst = false) {
  return {
    particles: [],
    cooldown: 0,
    burstCount: suppressInitialBurst ? MAX_BURSTS : 0
  };
}
function rearmParticleState(state) {
  state.particles.length = 0;
  state.cooldown = 0;
  state.burstCount = MAX_BURSTS;
}
function syncDegenPresentation(presentation, nextKey, isTransitioning, particles, shake) {
  const presentationChanged = presentation.key !== nextKey;
  const transitionChanged = presentation.transitioning !== isTransitioning;
  if (!presentationChanged && !transitionChanged) return !isTransitioning;
  presentation.key = nextKey;
  presentation.transitioning = isTransitioning;
  rearmParticleState(particles);
  shake.amplitude = 0;
  return false;
}
function spawnOnSwing(state, momentum, dotX, dotY, swingMagnitude, accentColor, dt, options) {
  state.cooldown = Math.max(0, state.cooldown - dt);
  if (swingMagnitude < MAGNITUDE_THRESHOLD) {
    state.burstCount = 0;
    return 0;
  }
  if (momentum === "flat") return 0;
  if (state.cooldown > 0) return 0;
  if (momentum === "down" && options?.downMomentum !== true) return 0;
  if (state.burstCount >= MAX_BURSTS) return 0;
  state.cooldown = COOLDOWN_MS;
  const scale = options?.scale ?? 1;
  const isUp = momentum === "up";
  const mag = Math.min(swingMagnitude * 5, 1);
  const burstFalloff = mag > 0.6 ? 1 : [1, 0.6, 0.35][state.burstCount] ?? 0.35;
  state.burstCount++;
  const count = Math.round((12 + mag * 20) * scale * burstFalloff);
  const speedMultiplier = 1 + mag * 0.8;
  for (let i = 0; i < count && state.particles.length < MAX_PARTICLES; i++) {
    const baseAngle = isUp ? -Math.PI / 2 : Math.PI / 2;
    const spread = Math.PI * 1.2;
    const angle = baseAngle + (Math.random() - 0.5) * spread;
    const speed = (60 + Math.random() * 100) * speedMultiplier;
    state.particles.push({
      x: dotX + (Math.random() - 0.5) * 24,
      y: dotY + (Math.random() - 0.5) * 8,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: 1,
      size: (1 + Math.random() * 1.2) * scale * burstFalloff,
      color: accentColor
    });
  }
  return burstFalloff;
}
function drawParticles(ctx, state, dt) {
  if (state.particles.length === 0) return;
  const dtSec = dt / 1e3;
  ctx.save();
  let writeIdx = 0;
  for (let i = 0; i < state.particles.length; i++) {
    const p = state.particles[i];
    p.life -= dtSec / PARTICLE_LIFETIME;
    if (p.life <= 0) continue;
    p.x += p.vx * dtSec;
    p.y += p.vy * dtSec;
    p.vx *= 0.95;
    p.vy *= 0.95;
    ctx.globalAlpha = p.life * 0.55;
    ctx.fillStyle = p.color;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size * (0.5 + p.life * 0.5), 0, Math.PI * 2);
    ctx.fill();
    state.particles[writeIdx++] = p;
  }
  state.particles.length = writeIdx;
  ctx.restore();
}

// src/math/range.ts
function referenceValueForRange(referenceLine) {
  return referenceLine?.excludeFromRange === true ? void 0 : referenceLine?.value;
}
function referenceValuesForRange(referenceLine, referenceLines) {
  return [referenceLine, ...referenceLines ?? []].flatMap((line) => {
    const value = referenceValueForRange(line);
    return value === void 0 ? [] : [value];
  });
}
function computeWindowBounds(points, startTime, endTime, currentTime, currentValue) {
  let min = currentValue;
  let max = currentValue;
  const include = (value) => {
    if (value < min) min = value;
    if (value > max) max = value;
  };
  const boundaryValue = valueAtTime(points, startTime, currentTime, currentValue);
  if (boundaryValue !== null) include(boundaryValue);
  for (const point of points) {
    if (point.time >= startTime && point.time <= endTime) include(point.value);
  }
  return { min, max };
}
function valueAtTime(points, time, currentTime, currentValue) {
  if (points.length === 0) return currentValue;
  const lastPoint = points[points.length - 1];
  if (time <= lastPoint.time) return interpolateAtTime(points, time);
  if (currentTime <= lastPoint.time) return currentValue;
  if (time >= currentTime) return currentValue;
  const progress = (time - lastPoint.time) / (currentTime - lastPoint.time);
  return lastPoint.value + (currentValue - lastPoint.value) * progress;
}
function computeRangeFromBounds(targetMin, targetMax, referenceValue, exaggerate) {
  const referenceValues = Array.isArray(referenceValue) ? referenceValue : referenceValue === void 0 ? [] : [referenceValue];
  for (const value of referenceValues) {
    if (value < targetMin) targetMin = value;
    if (value > targetMax) targetMax = value;
  }
  const valuesAreNonNegative = targetMin >= 0;
  const rawRange = targetMax - targetMin;
  const marginFactor = exaggerate ? 0.01 : 0.12;
  const valueMagnitude = Math.max(Math.abs(targetMin), Math.abs(targetMax));
  const relativeMinimumRange = valueMagnitude * (exaggerate ? 0.04 : 0.2);
  const nearFlatThreshold = valueMagnitude * 0.01;
  const nearFlatMinimumRange = rawRange <= nearFlatThreshold ? relativeMinimumRange || (exaggerate ? 0.04 : 0.4) : 0;
  const safetyFloor = valueMagnitude * 1e-7;
  const minRange = Math.max(
    rawRange * (exaggerate ? 0.02 : 0.1),
    nearFlatMinimumRange,
    safetyFloor
  );
  if (rawRange < minRange) {
    const mid = (targetMin + targetMax) / 2;
    targetMin = mid - minRange / 2;
    targetMax = mid + minRange / 2;
  } else {
    const margin = rawRange * marginFactor;
    targetMin -= margin;
    targetMax += margin;
  }
  if (valuesAreNonNegative && targetMin < 0) {
    targetMax += -targetMin;
    targetMin = 0;
  }
  return { min: targetMin, max: targetMax };
}
function computeConfiguredRange(cfg, targetMin, targetMax, referenceLine = cfg.referenceLine, referenceWindow) {
  let min = targetMin;
  let max = targetMax;
  for (const value of referenceValuesForRange(referenceLine, cfg.referenceLines)) {
    if (value < min) min = value;
    if (value > max) max = value;
  }
  if (cfg.extendRange !== void 0) {
    const referenceBounds = referenceLine?.value === void 0 || referenceWindow === void 0 ? void 0 : computeWindowBounds(
      referenceLine.points ?? [],
      referenceWindow.start,
      referenceWindow.end,
      referenceWindow.end,
      referenceLine.value
    );
    const extended = cfg.extendRange({ min, max }, referenceLine?.value, referenceBounds);
    if (Number.isFinite(extended.min) && Number.isFinite(extended.max) && extended.max >= extended.min) {
      min = extended.min;
      max = extended.max;
    }
  }
  return computeRangeFromBounds(min, max, void 0, cfg.exaggerate);
}

// src/draw/referencePath.ts
function drawReferencePath(ctx, layout, palette, ref) {
  const points = ref.points;
  const first = points?.[0];
  if (points === void 0 || first === void 0) return;
  const reveal = ref.pathReveal?.();
  ctx.save();
  ctx.beginPath();
  const top = reveal === void 0 ? layout.pad.top : 0;
  ctx.rect(layout.pad.left, top, layout.chartW, layout.h - layout.pad.bottom - top);
  ctx.clip();
  ctx.strokeStyle = ref.color ?? palette.refLine;
  ctx.lineWidth = ref.width ?? 1;
  if (ref.style !== "solid") ctx.setLineDash(ref.dashPattern ?? [4, 4]);
  ctx.beginPath();
  if (reveal === void 0) {
    ctx.moveTo(layout.toX(first.time), layout.toY(first.value));
    for (let index = 1; index < points.length; index += 1) {
      const point = points[index];
      if (point !== void 0) ctx.lineTo(layout.toX(point.time), layout.toY(point.value));
    }
  } else {
    traceRevealedPath(ctx, layout, points, ref.value, reveal);
  }
  ctx.stroke();
  ctx.restore();
}
function traceRevealedPath(ctx, layout, points, value, reveal) {
  const progress = Math.max(0, Math.min(1, reveal));
  const bottom = layout.h - layout.pad.bottom;
  const flatY = Math.max(15, Math.min(bottom - 12, layout.toY(value)));
  const leftValue = interpolateAtTime(points, layout.leftEdge) ?? value;
  const rightValue = interpolateAtTime(points, layout.rightEdge) ?? value;
  const bounds = computeWindowBounds(
    points,
    layout.leftEdge,
    layout.rightEdge,
    layout.rightEdge,
    rightValue
  );
  const min = Math.min(layout.minVal, bounds.min);
  const max = Math.max(layout.maxVal, bounds.max);
  const range = max - min || layout.valRange;
  const toY = (pointValue) => {
    const historyY = layout.pad.top + (1 - (pointValue - min) / range) * layout.chartH;
    return flatY + (historyY - flatY) * progress;
  };
  ctx.moveTo(layout.pad.left, toY(leftValue));
  for (const point of points) {
    if (point.time >= layout.leftEdge && point.time <= layout.rightEdge) {
      ctx.lineTo(layout.toX(point.time), toY(point.value));
    }
  }
  ctx.lineTo(layout.w - layout.pad.right, toY(rightValue));
}

// src/draw/referenceLine.ts
var defaultNumberFormat = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2
});
var BOB_AMPLITUDE_PX = 1.5;
var BOB_PERIOD_MS = 1e3;
var BADGE_STACK_STEP_PX = 26;
var AXIS_BADGE_MARGIN_PX = 4;
function referenceAxisBadgePaddingRight(ctx, ref) {
  if (ref?.value === void 0 || ref.labelPosition !== "right-axis") return 0;
  ctx.save();
  ctx.font = ref.labelFont ?? "500 11px system-ui, sans-serif";
  const inRangeWidth = ctx.measureText(formatReferenceLabel(ref)).width + 16;
  let badgeWidth = inRangeWidth;
  if (ref.offAxisBadge === true) {
    ctx.font = ref.labelFont ?? "600 11px system-ui, sans-serif";
    badgeWidth = Math.max(badgeWidth, ctx.measureText(formatTargetBadgeLabel(ref)).width + 29);
  }
  ctx.restore();
  return Math.ceil(badgeWidth + AXIS_BADGE_MARGIN_PX * 2);
}
function referenceAxisBadgeCenterY(layout, ref) {
  if (ref?.value === void 0 || ref.labelPosition !== "right-axis") return void 0;
  const edge = classifyReferenceEdge(ref.value, layout);
  if (edge !== "in") {
    if (ref.offAxisBadge !== true) return void 0;
    return edge === "above" ? 15 : layout.h - layout.pad.bottom - 12;
  }
  const y = layout.toY(ref.value);
  return Math.max(layout.pad.top + 11, Math.min(y, layout.h - layout.pad.bottom - 11));
}
function classifyReferenceEdge(value, layout) {
  const y = layout.toY(value);
  if (y < layout.pad.top) return "above";
  if (y > layout.h - layout.pad.bottom) return "below";
  return "in";
}
function drawReferenceLines(ctx, layout, palette, references, now_ms = 0, reducedMotion = false) {
  const badgeCounts = {
    above: 0,
    below: 0
  };
  for (const reference of references) {
    const edge = reference.value === void 0 ? "in" : classifyReferenceEdge(reference.value, layout);
    const badgeIndex = edge !== "in" && reference.offAxisBadge === true ? badgeCounts[edge]++ : 0;
    drawReferenceLine(ctx, layout, palette, reference, now_ms, reducedMotion, badgeIndex);
  }
}
function drawReferenceLine(ctx, layout, palette, ref, now_ms = 0, reducedMotion = false, badgeIndex = 0) {
  if (ref.at !== void 0) {
    drawTimeReferenceLine(ctx, layout, palette, ref);
    return;
  }
  const edge = classifyReferenceEdge(ref.value, layout);
  if (ref.points !== void 0 && ref.points.length > 0) {
    drawReferencePath(ctx, layout, palette, ref);
    if (ref.offAxisBadge === true && edge !== "in") {
      drawTargetBadge(ctx, layout, palette, ref, edge, now_ms, reducedMotion, badgeIndex);
    } else {
      drawReferencePathLabel(ctx, layout, palette, ref);
    }
    return;
  }
  if (ref.offAxisBadge === true && edge !== "in") {
    drawTargetBadge(ctx, layout, palette, ref, edge, now_ms, reducedMotion, badgeIndex);
    return;
  }
  const { w, h, pad, toY } = layout;
  const y = toY(ref.value);
  if (y < pad.top - 10 || y > h - pad.bottom + 10) return;
  const color = ref.color ?? palette.refLine;
  const labelColor = ref.labelColor ?? ref.color ?? palette.refLabel;
  const lineWidth = ref.width ?? 1;
  const labelFont = ref.labelFont ?? "500 11px system-ui, sans-serif";
  const dashPattern = ref.dashPattern ?? [4, 4];
  const label = formatReferenceLabel(ref);
  if (ref.labelBadge === true && label.length > 0) {
    if (ref.labelPosition === "right-axis") {
      ctx.save();
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      if (ref.style === "dashed") ctx.setLineDash(dashPattern);
      ctx.beginPath();
      ctx.moveTo(pad.left, y);
      ctx.lineTo(w - pad.right, y);
      ctx.stroke();
      ctx.restore();
      drawReferencePathLabel(ctx, layout, palette, ref);
      return;
    }
    drawInlineBadge(
      ctx,
      layout,
      y,
      ref.label ?? label,
      color,
      labelColor,
      labelFont,
      dashPattern,
      ref.style === "dashed",
      lineWidth
    );
    return;
  }
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  if (ref.style === "dashed" || ref.style === void 0 && label.length === 0) {
    ctx.setLineDash(dashPattern);
  }
  if (label.length === 0) {
    ctx.beginPath();
    ctx.moveTo(pad.left, y);
    ctx.lineTo(w - pad.right, y);
    ctx.stroke();
    ctx.restore();
    return;
  }
  ctx.font = labelFont;
  const textWidth = ctx.measureText(label).width;
  const labelX = resolveLabelX(ref.labelPosition, layout, textWidth);
  const gap = 8;
  const leftEnd = labelX - textWidth / 2 - gap;
  const rightStart = labelX + textWidth / 2 + gap;
  if (leftEnd > pad.left) {
    ctx.beginPath();
    ctx.moveTo(pad.left, y);
    ctx.lineTo(leftEnd, y);
    ctx.stroke();
  }
  if (rightStart < w - pad.right) {
    ctx.beginPath();
    ctx.moveTo(rightStart, y);
    ctx.lineTo(w - pad.right, y);
    ctx.stroke();
  }
  ctx.fillStyle = labelColor;
  ctx.textAlign = "center";
  ctx.fillText(label, labelX, y + 4);
  ctx.restore();
}
function drawReferencePathLabel(ctx, layout, palette, ref) {
  const label = formatReferenceLabel(ref);
  const y = layout.toY(ref.value);
  if (label.length === 0 || y < layout.pad.top - 10 || y > layout.h - layout.pad.bottom + 10) {
    return;
  }
  ctx.save();
  ctx.font = ref.labelFont ?? "500 11px system-ui, sans-serif";
  const textWidth = ctx.measureText(label).width;
  if (ref.labelBadge === true) {
    const boxWidth = textWidth + 16;
    const boxHeight = 22;
    const x2 = resolveLabelX(ref.labelPosition, layout, boxWidth);
    const boxY = Math.max(
      layout.pad.top,
      Math.min(y - boxHeight / 2, layout.h - layout.pad.bottom - boxHeight)
    );
    roundRectPath(ctx, x2 - boxWidth / 2, boxY, boxWidth, boxHeight, 6);
    ctx.fillStyle = palette.badgeOuterBg;
    ctx.fill();
    ctx.fillStyle = ref.color ?? palette.refLine;
    ctx.globalAlpha = 0.12;
    ctx.fill();
    ctx.globalAlpha = 1;
    ctx.fillStyle = ref.labelColor ?? ref.color ?? palette.refLabel;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(label, x2, boxY + boxHeight / 2 + 0.5);
    ctx.restore();
    return;
  }
  const x = resolveLabelX(ref.labelPosition, layout, textWidth);
  ctx.textAlign = "center";
  ctx.lineWidth = 3;
  ctx.strokeStyle = `rgb(${palette.bgRgb.join(", ")})`;
  ctx.strokeText(label, x, y + 4);
  ctx.fillStyle = ref.labelColor ?? ref.color ?? palette.refLabel;
  ctx.fillText(label, x, y + 4);
  ctx.restore();
}
function drawTimeReferenceLine(ctx, layout, palette, ref) {
  const x = layout.toX(ref.at);
  if (!Number.isFinite(x) || x < layout.pad.left || x > layout.w - layout.pad.right) return;
  ctx.save();
  ctx.strokeStyle = ref.color ?? palette.refLine;
  ctx.lineWidth = ref.width ?? 1;
  ctx.setLineDash(ref.style === "solid" ? [] : ref.dashPattern ?? [4, 4]);
  ctx.beginPath();
  ctx.moveTo(x, layout.pad.top);
  ctx.lineTo(x, layout.h - layout.pad.bottom);
  ctx.stroke();
  if (ref.label) {
    ctx.font = ref.labelFont ?? "500 11px system-ui, sans-serif";
    const labelWidth = ctx.measureText(ref.label).width;
    const labelX = Math.min(
      Math.max(x + 6, layout.pad.left + 4),
      layout.w - layout.pad.right - labelWidth - 8
    );
    const labelY = layout.pad.top + 2;
    ctx.fillStyle = palette.tooltipBg;
    ctx.fillRect(labelX - 4, labelY - 2, labelWidth + 8, 16);
    ctx.fillStyle = ref.labelColor ?? palette.tooltipText;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText(ref.label, labelX, labelY);
  }
  ctx.restore();
}
function drawInlineBadge(ctx, layout, y, label, color, textColor, font, dashPattern, isDashed, lineWidth) {
  const { w, h, pad } = layout;
  const horizontalPadding = 8;
  const radius = 6;
  const boxHeight = 22;
  ctx.save();
  ctx.font = font;
  const boxWidth = horizontalPadding * 2 + ctx.measureText(label).width;
  const boxX = pad.left;
  const boxY = Math.max(pad.top, Math.min(y - boxHeight / 2, h - pad.bottom - boxHeight));
  const lineY = boxY + boxHeight / 2;
  const lineStart = boxX + boxWidth + 6;
  if (lineStart < w - pad.right) {
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    if (isDashed) ctx.setLineDash(dashPattern);
    ctx.beginPath();
    ctx.moveTo(lineStart, lineY);
    ctx.lineTo(w - pad.right, lineY);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  ctx.fillStyle = color;
  ctx.globalAlpha = 0.12;
  roundRectPath(ctx, boxX, boxY, boxWidth, boxHeight, radius);
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.fillStyle = textColor;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(label, boxX + horizontalPadding, lineY + 0.5);
  ctx.restore();
}
function formatReferenceLabel(ref) {
  const label = ref.label ?? "";
  if (ref.showValue !== true) return label;
  const value = (ref.formatValue ?? defaultNumberFormat.format)(ref.value);
  return label.length > 0 ? `${label} ${value}` : value;
}
function resolveLabelX(position, layout, textWidth) {
  if (position === "left") return layout.pad.left + 4 + textWidth / 2;
  if (position === "right") return layout.w - layout.pad.right - 4 - textWidth / 2;
  if (position === "right-axis") return layout.w - AXIS_BADGE_MARGIN_PX - textWidth / 2;
  return layout.pad.left + layout.chartW / 2;
}
function formatTargetBadgeLabel(ref) {
  const label = ref.offAxisBadgeLabel ?? ref.label ?? "";
  const value = (ref.formatValue ?? defaultNumberFormat.format)(ref.value);
  return label.length > 0 ? `${label}: ${value}` : value;
}
function drawTargetBadge(ctx, layout, palette, ref, edge, now_ms, reducedMotion, badgeIndex) {
  const { w, h, pad } = layout;
  const accent = ref.color ?? ref.labelColor ?? palette.refLine;
  const text = formatTargetBadgeLabel(ref);
  const font = ref.labelFont ?? "600 11px system-ui, sans-serif";
  const dashPattern = ref.dashPattern ?? [4, 4];
  const boxHeight = 22;
  const stackOffset = badgeIndex * BADGE_STACK_STEP_PX;
  const lineY = edge === "above" ? 4 + boxHeight / 2 + stackOffset : h - pad.bottom - 12 - stackOffset;
  ctx.save();
  ctx.font = font;
  const textWidth = ctx.measureText(text).width;
  const horizontalPadding = 8;
  const chevronWidth = 8;
  const chevronGap = 5;
  const boxWidth = horizontalPadding * 2 + chevronWidth + chevronGap + textWidth;
  const boxX = ref.labelPosition === "right-axis" ? w - AXIS_BADGE_MARGIN_PX - boxWidth : pad.left;
  const boxY = lineY - boxHeight / 2;
  ctx.strokeStyle = accent;
  ctx.lineWidth = ref.width ?? 1;
  ctx.setLineDash(dashPattern);
  const lineStart = ref.labelPosition === "right-axis" ? pad.left : boxX + boxWidth + 6;
  const hasMorphPath = ref.pathReveal !== void 0 && (ref.points?.length ?? 0) > 0;
  if (!hasMorphPath && lineStart < w - pad.right) {
    ctx.beginPath();
    ctx.moveTo(lineStart, lineY);
    ctx.lineTo(w - pad.right, lineY);
    ctx.stroke();
  }
  ctx.setLineDash([]);
  ctx.fillStyle = accent;
  ctx.globalAlpha = 0.12;
  roundRectPath(ctx, boxX, boxY, boxWidth, boxHeight, 6);
  ctx.fill();
  ctx.globalAlpha = 1;
  const direction = edge === "above" ? -1 : 1;
  const bob = reducedMotion ? 0 : Math.sin(now_ms / BOB_PERIOD_MS * Math.PI * 2) * BOB_AMPLITUDE_PX;
  const chevronX = boxX + horizontalPadding + chevronWidth / 2;
  drawChevron(ctx, chevronX, lineY + bob * direction, chevronWidth, direction, accent);
  ctx.fillStyle = accent;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(text, boxX + horizontalPadding + chevronWidth + chevronGap, lineY + 0.5);
  ctx.restore();
}
function roundRectPath(ctx, x, y, width, height, radius) {
  const clampedRadius = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  if (typeof ctx.roundRect === "function") {
    ctx.roundRect(x, y, width, height, clampedRadius);
    return;
  }
  ctx.moveTo(x + clampedRadius, y);
  ctx.arcTo(x + width, y, x + width, y + height, clampedRadius);
  ctx.arcTo(x + width, y + height, x, y + height, clampedRadius);
  ctx.arcTo(x, y + height, x, y, clampedRadius);
  ctx.arcTo(x, y, x + width, y, clampedRadius);
  ctx.closePath();
}
function drawChevron(ctx, centerX, centerY, width, direction, color) {
  const halfWidth = width / 2;
  const height = 4;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(centerX - halfWidth, centerY - height / 2 * direction);
  ctx.lineTo(centerX, centerY + height / 2 * direction);
  ctx.lineTo(centerX + halfWidth, centerY - height / 2 * direction);
  ctx.stroke();
  ctx.restore();
}

// src/math/intervals.ts
function niceTimeInterval(windowSecs) {
  if (windowSecs <= 15) return 2;
  if (windowSecs <= 30) return 5;
  if (windowSecs <= 60) return 10;
  if (windowSecs <= 120) return 15;
  if (windowSecs <= 300) return 30;
  if (windowSecs <= 600) return 60;
  if (windowSecs <= 1800) return 300;
  if (windowSecs <= 3600) return 600;
  if (windowSecs <= 14400) return 1800;
  if (windowSecs <= 43200) return 3600;
  if (windowSecs <= 86400) return 7200;
  if (windowSecs <= 2 * 86400) return 14400;
  if (windowSecs <= 4 * 86400) return 43200;
  if (windowSecs <= 14 * 86400) return 86400;
  if (windowSecs <= 60 * 86400) return 2 * 86400;
  if (windowSecs <= 180 * 86400) return 7 * 86400;
  if (windowSecs <= 730 * 86400) return 30 * 86400;
  return 90 * 86400;
}
function firstTickTime(leftEdge, interval) {
  if (interval >= 86400) {
    const dayStart = new Date((leftEdge - interval) * 1e3);
    dayStart.setHours(0, 0, 0, 0);
    return dayStart.getTime() / 1e3;
  }
  if (86400 % interval === 0) {
    const dayStart = new Date(leftEdge * 1e3);
    dayStart.setHours(0, 0, 0, 0);
    const dayStartSec = dayStart.getTime() / 1e3;
    const stepsFromMidnight = Math.floor((leftEdge - dayStartSec) / interval);
    return dayStartSec + (stepsFromMidnight - 1) * interval;
  }
  return Math.ceil((leftEdge - interval) / interval) * interval;
}

// src/draw/timeAxis.ts
var FADE = 0.08;
function drawTimeAxis(ctx, layout, palette, windowSecs, targetWindowSecs, formatTime, state, dt) {
  const { h, pad, leftEdge, rightEdge, toX } = layout;
  const chartLeft = pad.left;
  const chartRight = layout.w - pad.right;
  const chartW = chartRight - chartLeft;
  const fadeZone = 50;
  const edgeAlpha = (x) => {
    const fromLeft = x - chartLeft;
    const fromRight = chartRight - x;
    const fromEdge = Math.min(fromLeft, fromRight);
    if (fromEdge >= fadeZone) return 1;
    if (fromEdge <= 0) return 0;
    return fromEdge / fadeZone;
  };
  ctx.font = palette.labelFont;
  const formatChanged = state.formatWindowSecs !== targetWindowSecs;
  const targetPxPerSec = chartW / targetWindowSecs;
  let interval = niceTimeInterval(targetWindowSecs);
  while (interval * targetPxPerSec < 60 && interval < targetWindowSecs) {
    interval *= 2;
  }
  const targets = /* @__PURE__ */ new Set();
  for (let t = firstTickTime(leftEdge, interval); t <= rightEdge + interval && targets.size < 30; t += interval) {
    targets.add(Math.round(t * 100));
  }
  for (const key of targets) {
    const existing = state.labels.get(key);
    if (!existing) {
      const text = formatTime(key / 100, targetWindowSecs);
      state.labels.set(key, { alpha: 0, text, width: ctx.measureText(text).width });
    } else if (formatChanged) {
      const text = formatTime(key / 100, targetWindowSecs);
      if (text !== existing.text) {
        existing.text = text;
        existing.width = ctx.measureText(text).width;
      }
    }
  }
  state.formatWindowSecs = targetWindowSecs;
  for (const [key, label] of state.labels) {
    const x = toX(key / 100);
    const isTarget = targets.has(key);
    const target = isTarget ? edgeAlpha(x) : 0;
    let next = lerp(label.alpha, target, FADE, dt);
    if (Math.abs(next - target) < 0.02) next = target;
    if (next < 0.01 && target === 0 && !isTarget) {
      state.labels.delete(key);
    } else {
      label.alpha = next;
    }
  }
  const baseAlpha = ctx.globalAlpha;
  const lineY = h - pad.bottom;
  const tickLen = 5;
  ctx.strokeStyle = palette.gridLine;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(chartLeft, lineY);
  ctx.lineTo(chartRight, lineY);
  ctx.stroke();
  ctx.textAlign = "center";
  const labels = [];
  for (const [key, label] of state.labels) {
    if (label.alpha < 0.02) continue;
    const x = toX(key / 100);
    if (x < chartLeft - 20 || x > chartRight) continue;
    labels.push({ x, alpha: label.alpha, text: label.text, w: label.width });
  }
  labels.sort((a, b) => a.x - b.x);
  const drawn = [];
  for (const label of labels) {
    const left = label.x - label.w / 2;
    if (drawn.length > 0) {
      const prev = drawn[drawn.length - 1];
      const prevRight = prev.x + prev.w / 2;
      if (left < prevRight + 8) {
        if (label.alpha > prev.alpha) {
          drawn[drawn.length - 1] = label;
        }
        continue;
      }
    }
    drawn.push(label);
  }
  for (const label of drawn) {
    ctx.save();
    ctx.globalAlpha = baseAlpha * label.alpha;
    ctx.strokeStyle = palette.gridLine;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(label.x, lineY);
    ctx.lineTo(label.x, lineY + tickLen);
    ctx.stroke();
    ctx.fillStyle = palette.timeLabel;
    ctx.fillText(label.text, label.x, lineY + tickLen + 14);
    ctx.restore();
  }
}

// src/draw/timeHorizon.ts
function drawTimeHorizon(ctx, layout, palette, options) {
  if (options.progress <= 0.01) return;
  const x = layout.w - layout.pad.right;
  const labelY = layout.pad.top + 9;
  ctx.save();
  ctx.globalAlpha *= Math.min(1, options.progress);
  ctx.strokeStyle = options.color ?? palette.refLine;
  ctx.lineWidth = 1.5;
  ctx.setLineDash([4, 5]);
  ctx.beginPath();
  ctx.moveTo(x, layout.pad.top);
  ctx.lineTo(x, layout.h - layout.pad.bottom);
  ctx.stroke();
  ctx.font = "600 11px ui-monospace, SFMono-Regular, Menlo, monospace";
  const textWidth = ctx.measureText(options.label).width;
  ctx.fillStyle = `rgba(${palette.bgRgb.join(",")},0.92)`;
  ctx.fillRect(x - textWidth - 12, labelY - 9, textWidth + 12, 18);
  ctx.fillStyle = options.color ?? palette.refLabel;
  ctx.textAlign = "right";
  ctx.textBaseline = "middle";
  ctx.fillText(options.label, x - 4, labelY);
  ctx.restore();
}

// src/draw/index.ts
var SHAKE_DECAY_RATE = 2e-3;
var SHAKE_MIN_AMPLITUDE = 0.2;
var FADE_EDGE_WIDTH = 40;
var CROSSHAIR_FADE_MIN_PX = 5;
function createShakeState() {
  return { amplitude: 0 };
}
function drawFrame(ctx, layout, palette, opts) {
  const shake = opts.shakeState;
  let shakeX = 0;
  let shakeY = 0;
  if (shake && shake.amplitude > SHAKE_MIN_AMPLITUDE) {
    shakeX = (Math.random() - 0.5) * 2 * shake.amplitude;
    shakeY = (Math.random() - 0.5) * 2 * shake.amplitude;
    ctx.save();
    ctx.translate(shakeX, shakeY);
  }
  if (shake) {
    const decayRate = SHAKE_DECAY_RATE ** (opts.dt / 1e3);
    shake.amplitude *= decayRate;
    if (shake.amplitude < SHAKE_MIN_AMPLITUDE) shake.amplitude = 0;
  }
  const reveal = opts.chartReveal;
  const pause = opts.pauseProgress;
  const revealRamp = (start, end) => {
    const t = Math.max(0, Math.min(1, (reveal - start) / (end - start)));
    return t * t * (3 - 2 * t);
  };
  const referenceLines = [opts.referenceLine, ...opts.referenceLines ?? []].filter(
    (line) => line !== void 0
  );
  if (referenceLines.length > 0 && reveal > 0.01) {
    ctx.save();
    if (reveal < 1) ctx.globalAlpha = reveal;
    drawReferenceLines(ctx, layout, palette, referenceLines, opts.now_ms, opts.reducedMotion);
    ctx.restore();
  }
  if (opts.showGrid) {
    const gridAlpha = reveal < 1 ? revealRamp(0.15, 0.7) : 1;
    if (gridAlpha > 0.01) {
      ctx.save();
      if (gridAlpha < 1) ctx.globalAlpha = gridAlpha;
      drawGrid(
        ctx,
        layout,
        palette,
        opts.formatValue,
        opts.gridState,
        opts.dt,
        referenceAxisBadgeCenterY(layout, opts.referenceLine)
      );
      ctx.restore();
    }
  }
  if (opts.orderbookData && opts.orderbookState && reveal > 0.01) {
    ctx.save();
    if (reveal < 1) ctx.globalAlpha = reveal;
    drawOrderbook(
      ctx,
      layout,
      palette,
      opts.orderbookData,
      opts.dt,
      opts.orderbookState,
      opts.swingMagnitude,
      opts.orderbookNow
    );
    ctx.restore();
  }
  const scrubX = opts.scrubAmount > 0.05 ? opts.hoverX : null;
  const pts = drawLine(
    ctx,
    layout,
    palette,
    opts.visible,
    opts.smoothValue,
    opts.now,
    opts.showFill,
    scrubX,
    opts.scrubAmount,
    reveal,
    opts.now_ms,
    1,
    !opts.showValueLine,
    1,
    opts.lineCurve,
    opts.useSeriesColorForDashLines ? palette.line : void 0
  );
  if (opts.showTimeAxis) {
    const timeAlpha = reveal < 1 ? revealRamp(0.15, 0.7) : 1;
    if (timeAlpha > 0.01) {
      ctx.save();
      if (timeAlpha < 1) ctx.globalAlpha = timeAlpha;
      drawTimeAxis(
        ctx,
        layout,
        palette,
        opts.windowSecs,
        opts.targetWindowSecs,
        opts.formatTime,
        opts.timeAxisState,
        opts.dt
      );
      ctx.restore();
    }
  }
  if (pts && pts.length > 0) {
    const lastPt = pts[pts.length - 1];
    let dotScrub = opts.scrubAmount;
    if (opts.hoverX !== null && dotScrub > 0) {
      const distToLive = lastPt[0] - opts.hoverX;
      const fadeStart = Math.min(80, layout.chartW * 0.3);
      dotScrub = distToLive < CROSSHAIR_FADE_MIN_PX ? 0 : distToLive >= fadeStart ? opts.scrubAmount : (distToLive - CROSSHAIR_FADE_MIN_PX) / (fadeStart - CROSSHAIR_FADE_MIN_PX) * opts.scrubAmount;
    }
    const dotAlpha = reveal < 0.3 ? 0 : (reveal - 0.3) / 0.7;
    const showPulse = opts.showPulse && reveal > 0.6 && pause < 0.5;
    if (dotAlpha > 0.01) {
      ctx.save();
      if (dotAlpha < 1) ctx.globalAlpha = dotAlpha;
      drawDot(ctx, lastPt[0], lastPt[1], palette, showPulse, dotScrub, opts.now_ms);
      ctx.restore();
    }
    if (opts.showMomentum) {
      const arrowReveal = reveal < 1 ? revealRamp(0.6, 1) : 1;
      const arrowAlpha = arrowReveal * (1 - pause);
      if (arrowAlpha > 0.01) {
        ctx.save();
        if (arrowAlpha < 1) ctx.globalAlpha = arrowAlpha;
        drawArrows(
          ctx,
          lastPt[0],
          lastPt[1],
          opts.momentum,
          palette,
          opts.arrowState,
          opts.dt,
          opts.now_ms
        );
        ctx.restore();
      }
    }
    if (opts.particleState && reveal > 0.9) {
      const burstIntensity = spawnOnSwing(
        opts.particleState,
        opts.momentum,
        lastPt[0],
        lastPt[1],
        opts.swingMagnitude,
        palette.line,
        opts.dt,
        opts.particleOptions
      );
      if (burstIntensity > 0 && shake) {
        shake.amplitude = (3 + opts.swingMagnitude * 4) * burstIntensity;
      }
      drawParticles(ctx, opts.particleState, opts.dt);
    }
  }
  if (opts.edgeFade) {
    const fadeW = FADE_EDGE_WIDTH;
    ctx.save();
    ctx.globalCompositeOperation = "destination-out";
    const fadeGrad = ctx.createLinearGradient(layout.pad.left, 0, layout.pad.left + fadeW, 0);
    fadeGrad.addColorStop(0, "rgba(0, 0, 0, 1)");
    fadeGrad.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.fillStyle = fadeGrad;
    ctx.fillRect(0, 0, layout.pad.left + fadeW, layout.h);
    ctx.restore();
  }
  if (!opts.loading && reveal > 0.7 && opts.hoverX !== null && opts.hoverTime !== null && pts && pts.length > 0) {
    const hoverValue = screenYToValue(layout, sampleLineYAtX(pts, opts.hoverX, opts.lineCurve));
    const lastPt = pts[pts.length - 1];
    const distToLive = lastPt[0] - opts.hoverX;
    const fadeStart = Math.min(80, layout.chartW * 0.3);
    const scrubOpacity = distToLive < CROSSHAIR_FADE_MIN_PX ? 0 : distToLive >= fadeStart ? opts.scrubAmount : (distToLive - CROSSHAIR_FADE_MIN_PX) / (fadeStart - CROSSHAIR_FADE_MIN_PX) * opts.scrubAmount;
    if (scrubOpacity > 0.01 && hoverValue !== null) {
      drawCrosshair(
        ctx,
        layout,
        palette,
        opts.hoverX,
        hoverValue,
        opts.hoverTime,
        opts.formatValue,
        opts.formatTime,
        scrubOpacity,
        opts.tooltipY,
        lastPt[0],
        // liveDotX — tooltip right edge stops here
        opts.tooltipOutline
      );
    }
  }
  if (shake && (shakeX !== 0 || shakeY !== 0)) {
    ctx.restore();
  }
}
function drawMultiFrame(ctx, layout, opts) {
  const palette = opts.primaryPalette;
  const reveal = opts.chartReveal;
  const shake = opts.shakeState;
  let shakeX = 0;
  let shakeY = 0;
  if (shake && shake.amplitude > SHAKE_MIN_AMPLITUDE) {
    shakeX = (Math.random() - 0.5) * 2 * shake.amplitude;
    shakeY = (Math.random() - 0.5) * 2 * shake.amplitude;
    ctx.save();
    ctx.translate(shakeX, shakeY);
  }
  if (shake) {
    const decayRate = SHAKE_DECAY_RATE ** (opts.dt / 1e3);
    shake.amplitude *= decayRate;
    if (shake.amplitude < SHAKE_MIN_AMPLITUDE) shake.amplitude = 0;
  }
  const revealRamp = (start, end) => {
    const t = Math.max(0, Math.min(1, (reveal - start) / (end - start)));
    return t * t * (3 - 2 * t);
  };
  const referenceLines = [opts.referenceLine, ...opts.referenceLines ?? []].filter(
    (line) => line !== void 0
  );
  if (referenceLines.length > 0 && reveal > 0.01) {
    ctx.save();
    if (reveal < 1) ctx.globalAlpha = reveal;
    drawReferenceLines(ctx, layout, palette, referenceLines, opts.now_ms, opts.reducedMotion);
    ctx.restore();
  }
  if (opts.showGrid) {
    const gridAlpha = reveal < 1 ? revealRamp(0.15, 0.7) : 1;
    if (gridAlpha > 0.01) {
      ctx.save();
      if (gridAlpha < 1) ctx.globalAlpha = gridAlpha;
      drawGrid(
        ctx,
        layout,
        palette,
        opts.formatValue,
        opts.gridState,
        opts.dt,
        referenceAxisBadgeCenterY(layout, opts.referenceLine)
      );
      ctx.restore();
    }
  }
  if (opts.orderbookData && opts.orderbookState && reveal > 0.01) {
    ctx.save();
    if (reveal < 1) ctx.globalAlpha = reveal;
    drawOrderbook(
      ctx,
      layout,
      palette,
      opts.orderbookData,
      opts.dt,
      opts.orderbookState,
      Math.max(0, ...opts.series.map((series) => series.swingMagnitude)),
      opts.orderbookNow
    );
    ctx.restore();
  }
  const scrubX = opts.scrubAmount > 0.05 ? opts.hoverX : null;
  const allPts = [];
  for (let si = 0; si < opts.series.length; si++) {
    const s = opts.series[si];
    const seriesAlpha = s.alpha ?? 1;
    const secondaryFade = si > 0 && reveal < 1 ? Math.min(1, reveal * 2) : 1;
    const combinedAlpha = secondaryFade * seriesAlpha;
    if (combinedAlpha < 0.01) continue;
    ctx.save();
    if (combinedAlpha < 1) ctx.globalAlpha = combinedAlpha;
    const pts = drawLine(
      ctx,
      layout,
      s.palette,
      s.visible,
      s.smoothValue,
      opts.now,
      opts.showFill ?? false,
      scrubX,
      opts.scrubAmount,
      reveal,
      opts.now_ms,
      1,
      !opts.showValueLine,
      1,
      opts.lineCurve,
      opts.useSeriesColorForDashLines ? s.palette.line : void 0,
      s.futureStartTime,
      s.futureOpacity
    );
    ctx.restore();
    if (pts && pts.length > 0) {
      allPts.push({
        id: s.id,
        pts,
        palette: s.palette,
        label: s.label,
        alpha: seriesAlpha,
        ...s.endpointMarker === void 0 ? {} : { endpointMarker: s.endpointMarker },
        ...s.futureStartTime === void 0 ? {} : { futureStartX: layout.toX(s.futureStartTime) },
        momentum: s.momentum,
        swingMagnitude: s.swingMagnitude
      });
    }
  }
  if (opts.showTimeAxis) {
    const timeAlpha = reveal < 1 ? revealRamp(0.15, 0.7) : 1;
    if (timeAlpha > 0.01) {
      ctx.save();
      if (timeAlpha < 1) ctx.globalAlpha = timeAlpha;
      drawTimeAxis(
        ctx,
        layout,
        palette,
        opts.windowSecs,
        opts.targetWindowSecs,
        opts.formatTime,
        opts.timeAxisState,
        opts.dt
      );
      ctx.restore();
    }
  }
  if (opts.timeHorizon !== void 0) {
    drawTimeHorizon(ctx, layout, palette, opts.timeHorizon);
  }
  if (reveal > 0.3 && allPts.length > 0) {
    const dotAlpha = (reveal - 0.3) / 0.7;
    const showPulse = opts.showPulse && !opts.reducedMotion && reveal > 0.6 && opts.pauseProgress < 0.5;
    for (let entryIndex = 0; entryIndex < allPts.length; entryIndex += 1) {
      const entry = allPts[entryIndex];
      if (entry.alpha < 0.01) continue;
      const lastPt = entry.pts[entry.pts.length - 1];
      ctx.save();
      ctx.globalAlpha = dotAlpha * entry.alpha;
      if (entry.futureStartX !== void 0) {
        const currentY = sampleLineYAtX(entry.pts, entry.futureStartX, opts.lineCurve);
        if (currentY !== null) {
          drawMultiDot(
            ctx,
            entry.futureStartX,
            currentY,
            entry.palette.line,
            showPulse && entry.alpha > 0.5,
            opts.now_ms + entryIndex * MULTI_PULSE_STAGGER_MS,
            opts.dotSize,
            opts.pulseSize
          );
        }
        if (entry.endpointMarker === void 0) {
          drawSimpleDot(ctx, lastPt[0], lastPt[1], entry.palette.line, opts.dotSize);
        } else {
          drawSeriesEndpointMarker(ctx, lastPt[0], lastPt[1], entry.endpointMarker);
        }
      } else if (showPulse && entry.alpha > 0.5) {
        drawMultiDot(
          ctx,
          lastPt[0],
          lastPt[1],
          entry.palette.line,
          true,
          opts.now_ms + entryIndex * MULTI_PULSE_STAGGER_MS,
          opts.dotSize,
          opts.pulseSize
        );
      } else {
        drawSimpleDot(ctx, lastPt[0], lastPt[1], entry.palette.line, opts.dotSize);
      }
      ctx.restore();
    }
  }
  if (opts.showMarkers !== false) {
    drawChartMarkers(
      ctx,
      layout,
      opts.markers ?? [],
      opts.series,
      opts.markerCondenseDistance ?? 0
    );
  }
  if (opts.particleState && reveal > 0.9 && allPts.length > 0) {
    const target = allPts.reduce(
      (strongest, entry) => entry.swingMagnitude > strongest.swingMagnitude ? entry : strongest
    );
    const lastPt = target.pts[target.pts.length - 1];
    const burstIntensity = spawnOnSwing(
      opts.particleState,
      target.momentum,
      lastPt[0],
      lastPt[1],
      target.swingMagnitude,
      target.palette.line,
      opts.dt,
      opts.particleOptions
    );
    if (burstIntensity > 0 && shake) {
      shake.amplitude = (3 + target.swingMagnitude * 4) * burstIntensity;
    }
    drawParticles(ctx, opts.particleState, opts.dt);
  }
  if (opts.edgeFade) {
    ctx.save();
    ctx.globalCompositeOperation = "destination-out";
    const fadeGrad = ctx.createLinearGradient(
      layout.pad.left,
      0,
      layout.pad.left + FADE_EDGE_WIDTH,
      0
    );
    fadeGrad.addColorStop(0, "rgba(0, 0, 0, 1)");
    fadeGrad.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.fillStyle = fadeGrad;
    ctx.fillRect(0, 0, layout.pad.left + FADE_EDGE_WIDTH, layout.h);
    ctx.restore();
  }
  if (!opts.loading && reveal > 0.7 && opts.hoverX !== null && opts.hoverTime !== null && allPts.length > 0 && opts.scrubAmount > 0.01) {
    let maxLiveDotX = 0;
    for (const entry of allPts) {
      if (entry.alpha < 0.01) continue;
      const lastX = entry.pts[entry.pts.length - 1][0];
      if (lastX > maxLiveDotX) maxLiveDotX = lastX;
    }
    const hoverEntries = allPts.flatMap((entry) => {
      if (entry.alpha < 0.5) return [];
      const value = screenYToValue(
        layout,
        sampleLineYAtX(entry.pts, opts.hoverX ?? 0, opts.lineCurve)
      );
      return value === null ? [] : [{ id: entry.id, color: entry.palette.line, label: entry.label ?? "", value }];
    });
    const perSeriesPills = opts.crosshairStyle?.layout === "per-series-pill";
    const distToLive = maxLiveDotX - opts.hoverX;
    const fadeStart = Math.min(80, layout.chartW * 0.3);
    const scrubOpacity = perSeriesPills || opts.pinnedTooltip ? opts.scrubAmount : distToLive < CROSSHAIR_FADE_MIN_PX ? 0 : distToLive >= fadeStart ? opts.scrubAmount : (distToLive - CROSSHAIR_FADE_MIN_PX) / (fadeStart - CROSSHAIR_FADE_MIN_PX) * opts.scrubAmount;
    if (scrubOpacity > 0.01 && hoverEntries.length > 0) {
      if (perSeriesPills && opts.crosshairStyle) {
        drawPerSeriesPillCrosshair(
          ctx,
          layout,
          palette,
          opts.hoverX,
          opts.hoverTime,
          opts.bucketSecs,
          hoverEntries,
          opts.formatValue,
          scrubOpacity,
          opts.crosshairStyle,
          opts.pinnedTooltip
        );
      } else if (opts.crosshairStyle?.layout !== "none") {
        drawMultiCrosshair(
          ctx,
          layout,
          palette,
          opts.hoverX,
          opts.hoverTime,
          hoverEntries,
          opts.formatValue,
          opts.formatTime,
          scrubOpacity,
          opts.tooltipY,
          opts.tooltipOutline,
          maxLiveDotX,
          !opts.pinnedTooltip
        );
      }
    }
  }
  if (shake && (shakeX !== 0 || shakeY !== 0)) {
    ctx.restore();
  }
}
function screenYToValue(layout, y) {
  if (y === null || layout.chartH === 0) return null;
  const chartBottom = layout.h - layout.pad.bottom;
  return layout.minVal + (chartBottom - y) / layout.chartH * layout.valRange;
}
function drawCandleFrame(ctx, layout, palette, opts) {
  const { w, h, pad, chartW, chartH } = layout;
  const reveal = opts.chartReveal;
  if ((opts.referenceLines?.length ?? 0) > 0 && reveal > 0.01) {
    ctx.save();
    ctx.globalAlpha *= reveal;
    drawReferenceLines(
      ctx,
      layout,
      palette,
      opts.referenceLines ?? [],
      opts.now_ms,
      opts.reducedMotion
    );
    ctx.restore();
  }
  const fullLineMode = opts.lineModeProg >= 0.99;
  const revealLine = fullLineMode ? 1 - reveal : (1 - reveal) * (1 - reveal) * (1 - reveal);
  const lp = Math.max(opts.lineModeProg, revealLine);
  const colorBlend = lp > 1e-3 ? opts.lineModeProg / lp : 1;
  const revealRamp = (start, end) => {
    const t = Math.max(0, Math.min(1, (reveal - start) / (end - start)));
    return t * t * (3 - 2 * t);
  };
  const gridAlpha = revealRamp(0.25, 0.6);
  if (opts.showGrid && gridAlpha > 0.01) {
    ctx.save();
    if (gridAlpha < 1) ctx.globalAlpha = gridAlpha;
    drawGrid(ctx, layout, palette, opts.formatValue, opts.gridState, opts.dt);
    ctx.restore();
  }
  if (opts.orderbookData && opts.orderbookState && reveal > 0.01) {
    ctx.save();
    if (reveal < 1) ctx.globalAlpha = reveal;
    drawOrderbook(
      ctx,
      layout,
      palette,
      opts.orderbookData,
      opts.dt,
      opts.orderbookState,
      0,
      opts.orderbookNow
    );
    ctx.restore();
  }
  let linePts;
  if (lp > 0.01 && opts.lineVisible.length >= 2) {
    const scrubX = opts.scrubAmount > 0.05 ? opts.hoverX : null;
    ctx.save();
    ctx.globalAlpha = lp;
    linePts = drawLine(
      ctx,
      layout,
      palette,
      opts.lineVisible,
      opts.lineSmoothValue,
      opts.now,
      opts.lineModeProg > 0.01,
      scrubX,
      opts.scrubAmount,
      opts.chartReveal,
      opts.now_ms,
      colorBlend,
      !opts.showValueLine || !fullLineMode,
      opts.lineModeProg,
      // fillScale — fill fades smoothly with line mode transition
      opts.lineCurve,
      opts.useSeriesColorForDashLines ? palette.line : void 0
    );
    ctx.restore();
  }
  const closeAlpha = revealRamp(0.4, 0.8);
  const closeSource = opts.closePriceCandle ?? opts.liveCandle;
  if (opts.showValueLine && closeSource && closeAlpha > 0.01) {
    if (lp < 0.99) {
      ctx.save();
      ctx.globalAlpha = closeAlpha * (1 - lp);
      drawClosePrice(ctx, layout, palette, closeSource, opts.scrubAmount, opts.liveBullBlend);
      ctx.restore();
    }
    if (lp > 0.01 && !fullLineMode) {
      const dashY = layout.toY(closeSource.close);
      if (dashY >= pad.top && dashY <= h - pad.bottom) {
        ctx.save();
        ctx.setLineDash([4, 4]);
        ctx.strokeStyle = opts.useSeriesColorForDashLines ? palette.line : palette.dashLine;
        ctx.lineWidth = 1;
        ctx.globalAlpha = closeAlpha * lp * (1 - opts.scrubAmount * 0.2);
        ctx.beginPath();
        ctx.moveTo(pad.left, dashY);
        ctx.lineTo(w - pad.right, dashY);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.restore();
      }
    }
  }
  const candleAlpha = opts.chartReveal * (1 - lp);
  if (candleAlpha > 0.01) {
    const ohlcScale = reveal * reveal * (3 - 2 * reveal);
    const collapseC = (c) => ohlcScale >= 0.99 ? c : {
      time: c.time,
      open: c.close + (c.open - c.close) * ohlcScale,
      high: c.close + (c.high - c.close) * ohlcScale,
      low: c.close + (c.low - c.close) * ohlcScale,
      close: c.close
    };
    const revealCandles = ohlcScale < 0.99 ? opts.candles.map(collapseC) : opts.candles;
    const revealOld = ohlcScale < 0.99 && opts.oldCandles.length > 0 ? opts.oldCandles.map(collapseC) : opts.oldCandles;
    ctx.save();
    ctx.beginPath();
    ctx.rect(pad.left - 1, pad.top, chartW + 2, chartH);
    ctx.clip();
    const accentCol = lp > 0.01 ? palette.line : void 0;
    if (opts.morphT >= 0 && revealOld.length > 0) {
      ctx.globalAlpha = (1 - opts.morphT) * candleAlpha;
      drawCandlesticks(
        ctx,
        layout,
        revealOld,
        opts.oldWidth,
        -1,
        opts.now_ms,
        opts.hoverX ?? 0,
        opts.scrubAmount,
        1,
        -1,
        accentCol,
        lp
      );
      ctx.globalAlpha = opts.morphT * candleAlpha;
      drawCandlesticks(
        ctx,
        layout,
        revealCandles,
        opts.displayCandleWidth,
        opts.liveCandle?.time ?? -1,
        opts.now_ms,
        opts.hoverX ?? 0,
        opts.scrubAmount,
        opts.liveBirthAlpha,
        opts.liveBullBlend,
        accentCol,
        lp
      );
      ctx.globalAlpha = 1;
    } else {
      if (candleAlpha < 1) ctx.globalAlpha = candleAlpha;
      drawCandlesticks(
        ctx,
        layout,
        revealCandles,
        opts.displayCandleWidth,
        opts.liveCandle?.time ?? -1,
        opts.now_ms,
        opts.hoverX ?? 0,
        opts.scrubAmount,
        opts.liveBirthAlpha,
        opts.liveBullBlend,
        accentCol,
        lp
      );
    }
    ctx.restore();
  }
  if (lp > 0.5 && linePts && linePts.length > 0 && reveal > 0.3) {
    const lastPt = linePts[linePts.length - 1];
    const dotAlpha = (lp - 0.5) * 2 * ((reveal - 0.3) / 0.7);
    const showPulse = opts.showPulse !== false && lp > 0.8 && reveal > 0.6;
    if (dotAlpha > 0.01) {
      ctx.save();
      ctx.globalAlpha = dotAlpha;
      drawDot(ctx, lastPt[0], lastPt[1], palette, showPulse, opts.scrubAmount, opts.now_ms);
      ctx.restore();
    }
  }
  const timeAlpha = revealRamp(0.25, 0.6);
  if (opts.showTimeAxis && timeAlpha > 0.01) {
    ctx.save();
    if (timeAlpha < 1) ctx.globalAlpha = timeAlpha;
    drawTimeAxis(
      ctx,
      layout,
      palette,
      opts.targetWindowSecs,
      opts.targetWindowSecs,
      opts.formatTime,
      opts.timeAxisState,
      opts.dt
    );
    ctx.restore();
  }
  if (opts.edgeFade) {
    ctx.save();
    ctx.globalCompositeOperation = "destination-out";
    const fadeGrad = ctx.createLinearGradient(pad.left, 0, pad.left + FADE_EDGE_WIDTH, 0);
    fadeGrad.addColorStop(0, "rgba(0, 0, 0, 1)");
    fadeGrad.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.fillStyle = fadeGrad;
    ctx.fillRect(0, 0, pad.left + FADE_EDGE_WIDTH, h);
    ctx.restore();
  }
  if (opts.showEmptyOverlay) {
    const bgAlpha = 1 - opts.chartReveal;
    if (bgAlpha > 0.01) {
      const bgEmptyAlpha = (1 - opts.loadingAlpha) * bgAlpha;
      if (bgEmptyAlpha > 0.01) {
        drawEmpty(ctx, w, h, pad, palette, bgEmptyAlpha, opts.now_ms, true, opts.emptyText);
      }
    }
  }
  if (!opts.loading && opts.chartReveal > 0.7 && opts.hoveredCandle && opts.hoverX !== null && opts.scrubAmount > 0.01) {
    if (opts.lineModeProg > 0.5) {
      drawLineModeCrosshair(
        ctx,
        layout,
        palette,
        opts.hoverX,
        opts.hoveredCandle.close,
        opts.hoverTime ?? 0,
        opts.formatValue,
        opts.formatTime,
        opts.scrubAmount
      );
    } else {
      drawCandleCrosshair(
        ctx,
        layout,
        palette,
        opts.hoverX,
        opts.hoveredCandle,
        opts.hoverTime ?? 0,
        opts.formatValue,
        opts.formatTime,
        opts.scrubAmount
      );
    }
  }
}

// src/draw/badge.ts
function badgeSvgPath(pillW, pillH, tailLen, tailSpread) {
  const r = pillH / 2;
  const cx = tailLen + pillW - r;
  const tl = tailLen + r;
  return [
    `M${tl},0`,
    `L${cx},0`,
    `A${r},${r},0,0,1,${cx},${pillH}`,
    `L${tl},${pillH}`,
    `C${tailLen + 2},${pillH},${3},${r + tailSpread},0,${r}`,
    `C${3},${r - tailSpread},${tailLen + 2},0,${tl},0`,
    "Z"
  ].join(" ");
}
function badgePillOnly(pillW, pillH) {
  const r = pillH / 2;
  return [
    `M${r},0`,
    `L${pillW - r},0`,
    `A${r},${r},0,0,1,${pillW - r},${pillH}`,
    `L${r},${pillH}`,
    `A${r},${r},0,0,1,${r},0`,
    "Z"
  ].join(" ");
}
var BADGE_PAD_X = 10;
var BADGE_PAD_Y = 3;
var BADGE_TAIL_LEN = 5;
var BADGE_TAIL_SPREAD = 2.5;
var BADGE_LINE_H = 16;

// src/draw/loading.ts
function drawLoading(ctx, w, h, pad, palette, now_ms, alpha = 1, strokeColor) {
  const chartW = w - pad.left - pad.right;
  const chartH = h - pad.top - pad.bottom;
  const centerY = pad.top + chartH / 2;
  const leftX = pad.left;
  const amplitude = chartH * LOADING_AMPLITUDE_RATIO;
  const scroll = now_ms * LOADING_SCROLL_SPEED;
  const breath = loadingBreath(now_ms);
  const numPts = 32;
  const pts = [];
  for (let i = 0; i <= numPts; i++) {
    const t = i / numPts;
    const x = leftX + t * chartW;
    const y = loadingY(t, centerY, amplitude, scroll);
    pts.push([x, y]);
  }
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  drawSpline(ctx, pts);
  ctx.strokeStyle = strokeColor ?? palette.line;
  ctx.lineWidth = palette.lineWidth;
  ctx.globalAlpha = breath * alpha;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.stroke();
  ctx.restore();
}

// src/math/clipPointsToWindow.ts
function hasRenderableLineWindow(points) {
  return points.length > 0;
}
function clipPointsToWindow(points, startTime, endTime) {
  let predecessor;
  const visible = [];
  for (const point of points) {
    if (point.time < startTime) {
      predecessor = point;
    } else if (point.time <= endTime) {
      visible.push(point);
    }
  }
  if (predecessor !== void 0) {
    visible.unshift(predecessor);
  }
  return visible;
}
function buildRenderableLineWindow(points, startTime, endTime, currentTime, currentValue) {
  const visible = clipPointsToWindow(points, startTime, endTime);
  if (hasRenderableLineWindow(visible) || !Number.isFinite(currentValue)) return visible;
  return [
    { time: startTime, value: currentValue },
    { time: Math.max(startTime, currentTime), value: currentValue }
  ];
}

// src/math/frameSource.ts
function createLivelineFrameState() {
  return {
    revision: null,
    painted: null,
    morph: null
  };
}
function resolveLivelinePaintedFrame(state, canonical, animationNowMs, morphDurationMs, reducedMotion, easing) {
  if (canonical === null) return null;
  if (state.painted === null || reducedMotion || morphDurationMs <= 0) {
    state.revision = canonical.revision;
    state.painted = canonical;
    state.morph = null;
    return canonical;
  }
  if (state.revision !== canonical.revision) {
    const isLate = canonical.earliestAffectedTime !== null && canonical.earliestAffectedTime < canonical.logicalNowSeconds;
    if (isLate && framesDiffer(state.painted, canonical)) {
      state.morph = { baseline: state.painted, startedAtMs: animationNowMs };
    }
    state.revision = canonical.revision;
  }
  const morph = state.morph;
  if (morph === null) {
    state.painted = canonical;
    return canonical;
  }
  const progress = Math.min(Math.max((animationNowMs - morph.startedAtMs) / morphDurationMs, 0), 1);
  if (progress >= 1) {
    state.painted = canonical;
    state.morph = null;
    return canonical;
  }
  const painted = interpolateFrames(morph.baseline, canonical, easing?.(progress) ?? progress);
  state.painted = painted;
  return painted;
}
function framesDiffer(baseline, canonical) {
  const baselineById = new Map(baseline.series.map((series) => [series.id, series]));
  if (baseline.series.length !== canonical.series.length) return true;
  for (const target of canonical.series) {
    const source = baselineById.get(target.id);
    if (source === void 0) return true;
    const times = unionTimes(source, baseline.nowSeconds, target, canonical.nowSeconds);
    for (const time of times) {
      const left = valueAt(source, baseline.nowSeconds, time, true);
      const right = valueAt(target, canonical.nowSeconds, time, false);
      if (left === null || right === null || Math.abs(left - right) > Number.EPSILON) return true;
    }
  }
  return false;
}
function interpolateFrames(baseline, canonical, progress) {
  const baselineById = new Map(baseline.series.map((series2) => [series2.id, series2]));
  const series = [];
  for (const target of canonical.series) {
    const source = baselineById.get(target.id);
    if (source === void 0) {
      series.push(target);
      continue;
    }
    const times = unionTimes(source, baseline.nowSeconds, target, canonical.nowSeconds);
    const data = [];
    let value = target.value;
    for (const time of times) {
      const baselineValue = valueAt(source, baseline.nowSeconds, time, true);
      const targetValue = valueAt(target, canonical.nowSeconds, time, false);
      if (baselineValue === null || targetValue === null) continue;
      const paintedValue = baselineValue + (targetValue - baselineValue) * progress;
      if (time < canonical.nowSeconds) data.push({ time, value: paintedValue });
      else if (time === canonical.nowSeconds) value = paintedValue;
    }
    series.push({ id: target.id, data, value });
  }
  return {
    ...canonical,
    series: [...series].sort((left, right) => right.value - left.value)
  };
}
function unionTimes(baseline, baselineNow, canonical, canonicalNow) {
  const times = /* @__PURE__ */ new Set();
  for (const point of baseline.data) {
    if (point.time <= canonicalNow) times.add(point.time);
  }
  for (const point of canonical.data) {
    if (point.time <= canonicalNow) times.add(point.time);
  }
  if (baselineNow <= canonicalNow) times.add(baselineNow);
  times.add(canonicalNow);
  return [...times].sort((left, right) => left - right);
}
function valueAt(series, head, time, extendPastHead) {
  if (time >= head) return extendPastHead || time === head ? series.value : null;
  const points = series.data;
  if (points.length === 0) return Number.isFinite(series.value) ? series.value : null;
  const first = points[0];
  if (time <= first.time) return first.value;
  const last = points[points.length - 1];
  if (time > last.time) {
    if (head <= last.time) return last.value;
    const progress2 = (time - last.time) / (head - last.time);
    return last.value + (series.value - last.value) * progress2;
  }
  let low = 0;
  let high = points.length - 1;
  while (high - low > 1) {
    const middle = low + high >> 1;
    if (points[middle].time <= time) low = middle;
    else high = middle;
  }
  const left = points[low];
  const right = points[high];
  if (right.time === left.time) return left.value;
  const progress = (time - left.time) / (right.time - left.time);
  return left.value + (right.value - left.value) * progress;
}

// src/math/momentum.ts
function detectMomentum(points, lookback = 20) {
  if (points.length < 5) return "flat";
  const start = Math.max(0, points.length - lookback);
  let min = Infinity;
  let max = -Infinity;
  for (let i = start; i < points.length; i++) {
    const v = points[i].value;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  const range = max - min;
  if (range === 0) return "flat";
  const tailStart = Math.max(start, points.length - 5);
  const first = points[tailStart].value;
  const last = points[points.length - 1].value;
  const delta = last - first;
  const threshold = range * 0.12;
  if (delta > threshold) return "up";
  if (delta < -threshold) return "down";
  return "flat";
}

// src/math/reveal.ts
function initialChartRevealProgress(args) {
  return !args.loading && args.hasData ? 1 : 0;
}

// src/math/timeHorizon.ts
function stepTimeHorizonProgress(current, active, deltaMs, durationMs, reducedMotion) {
  if (reducedMotion) return active ? 1 : 0;
  const safeDuration = Number.isFinite(durationMs) && durationMs > 0 ? durationMs : 1;
  const step = Math.max(0, deltaMs) / safeDuration;
  return active ? Math.min(1, current + step) : Math.max(0, current - step);
}
function resolveTimeHorizonFrame(baseLeftEdge, baseRightEdge, now, horizon, progress) {
  const baseline = { leftEdge: baseLeftEdge, rightEdge: baseRightEdge, lineEndTime: now };
  if (!Number.isFinite(horizon) || horizon <= now) return baseline;
  const clamped = Math.max(0, Math.min(1, progress));
  if (clamped === 0) return baseline;
  const eased = clamped * clamped * (3 - 2 * clamped);
  return {
    leftEdge: baseLeftEdge,
    rightEdge: baseRightEdge + (horizon - baseRightEdge) * eased,
    lineEndTime: now + (horizon - now) * eased
  };
}
function resolveTimeHorizonProjection(projection, now, lineEndTime, currentValue) {
  if (projection === void 0 || lineEndTime <= now || !Number.isFinite(currentValue)) {
    return { points: [], value: currentValue };
  }
  const livePoint = { time: now, value: currentValue };
  const points = [];
  let previous = livePoint;
  for (const point of projection) {
    if (!Number.isFinite(point.time) || !Number.isFinite(point.value) || point.time <= now)
      continue;
    if (point.time <= previous.time) continue;
    if (point.time <= lineEndTime) {
      if (points.length === 0) points.push(livePoint);
      points.push(point);
      previous = point;
      continue;
    }
    const span = point.time - previous.time;
    const progress = span <= 0 ? 1 : (lineEndTime - previous.time) / span;
    const boundary = {
      time: lineEndTime,
      value: previous.value + (point.value - previous.value) * Math.max(0, Math.min(1, progress))
    };
    if (points.length === 0) points.push(livePoint);
    points.push(boundary);
    return { points, value: boundary.value };
  }
  return { points, value: previous.value };
}
function formatTimeHorizonCountdown(remainingSeconds) {
  const totalSeconds = Math.max(0, Math.ceil(remainingSeconds));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor(totalSeconds % 3600 / 60);
  const seconds = totalSeconds % 60;
  const paddedMinutes = String(minutes).padStart(2, "0");
  const paddedSeconds = String(seconds).padStart(2, "0");
  return hours > 0 ? `${String(hours)}:${paddedMinutes}:${paddedSeconds}` : `${String(minutes)}:${paddedSeconds}`;
}

// src/math/window.ts
var WINDOW_BUFFER = 0.05;
var WINDOW_BUFFER_NO_BADGE = 0.015;
function windowForHistorySpan(historySeconds, showBadge = false) {
  const buffer = showBadge ? WINDOW_BUFFER : WINDOW_BUFFER_NO_BADGE;
  return historySeconds / (1 - buffer);
}

// src/math/windowTransition.ts
var WINDOW_TRANSITION_MS = 750;
function updateWindowTransition(cfg, wt, displayWindow, displayMin, displayMax, noMotion, now_ms, now, points, smoothValue, buffer, referenceLine) {
  const targetWindow = resolveLineWindow(cfg.windowSecs, cfg.historyStartTime, now, buffer);
  if (wt.historyStartTime !== cfg.historyStartTime || cfg.historyStartTime === void 0 && wt.to !== cfg.windowSecs) {
    wt.historyStartTime = cfg.historyStartTime;
    wt.from = displayWindow;
    wt.to = cfg.windowSecs;
    wt.startMs = now_ms;
    wt.rangeFromMin = displayMin;
    wt.rangeFromMax = displayMax;
    const targetRightEdge = now + targetWindow * buffer;
    const targetLeftEdge = targetRightEdge - targetWindow;
    if (points.length > 0) {
      const targetBounds = computeWindowBounds(
        points,
        targetLeftEdge,
        targetRightEdge,
        now,
        smoothValue
      );
      const targetRange = computeConfiguredRange(
        cfg,
        targetBounds.min,
        targetBounds.max,
        referenceLine,
        { start: targetLeftEdge, end: targetRightEdge }
      );
      wt.rangeToMin = targetRange.min;
      wt.rangeToMax = targetRange.max;
    }
  }
  let windowTransProgress = 0;
  let completedWindowSecs = null;
  let resultWindow;
  if (noMotion || wt.startMs === 0) {
    resultWindow = targetWindow;
    if (noMotion && wt.startMs !== 0) {
      wt.startMs = 0;
      completedWindowSecs = cfg.windowSecs;
    }
  } else {
    const elapsed = now_ms - wt.startMs;
    const duration = WINDOW_TRANSITION_MS;
    const t = Math.min(elapsed / duration, 1);
    const eased = (1 - Math.cos(t * Math.PI)) / 2;
    windowTransProgress = eased;
    const logFrom = Math.log(wt.from);
    const logTo = Math.log(targetWindow);
    resultWindow = Math.exp(logFrom + (logTo - logFrom) * eased);
    if (t >= 1) {
      resultWindow = targetWindow;
      wt.startMs = 0;
      windowTransProgress = 0;
      completedWindowSecs = cfg.windowSecs;
    }
  }
  return { windowSecs: resultWindow, windowTransProgress, completedWindowSecs };
}
function resolveLineWindow(windowSecs, historyStartTime, now, buffer) {
  return historyStartTime === void 0 ? windowSecs : Math.max(1, now - historyStartTime) / (1 - buffer);
}

// src/useLivelineEngine.ts
var SVG_NS = "http://www.w3.org/2000/svg";
function estimateBucketSecs(entries) {
  for (const entry of entries) {
    const previous = entry.visible.at(-2);
    const latest = entry.visible.at(-1);
    if (previous === void 0 || latest === void 0) continue;
    const delta = latest.time - previous.time;
    if (delta > 0) return delta;
  }
  return 0;
}
function applyMeasuredYAxisPadding(ctx, layout, cfg, gridState) {
  const right = cfg.showGrid ? resolveGridLabelPaddingRight(
    ctx,
    layout,
    cfg.palette,
    cfg.formatValue,
    gridState,
    cfg.axisOptions?.y,
    layout.pad.right
  ) : void 0;
  layout.pad.right = Math.max(
    layout.pad.right,
    right ?? 0,
    referenceAxisBadgePaddingRight(ctx, cfg.referenceLine)
  );
}
var MAX_DELTA_MS = 50;
var SCRUB_LERP_SPEED = 0.12;
var BADGE_WIDTH_LERP = 0.15;
var BADGE_Y_LERP = 0.35;
var BADGE_Y_LERP_TRANSITIONING = 0.5;
var MOMENTUM_COLOR_LERP = 0.12;
var WINDOW_TRANSITION_MS2 = 750;
var VALUE_SNAP_THRESHOLD = 1e-3;
var ADAPTIVE_SPEED_BOOST = 0.2;
var MULTI_RANGE_LERP_SPEED = 0.4;
var MOMENTUM_GREEN = [34, 197, 94];
var MOMENTUM_RED = [239, 68, 68];
var CHART_REVEAL_SPEED = 0.14;
var CHART_REVEAL_SPEED_FWD = 0.09;
var PAUSE_PROGRESS_SPEED = 0.12;
var PAUSE_CATCHUP_SPEED = 0.08;
var PAUSE_CATCHUP_SPEED_FAST = 0.22;
var LOADING_ALPHA_SPEED = 0.14;
var SERIES_TOGGLE_SPEED = 0.1;
var CANDLE_LERP_SPEED = 0.25;
var CANDLE_WIDTH_TRANS_MS = 300;
var LINE_MORPH_MS = 500;
var CLOSE_LINE_LERP_SPEED = 0.25;
var LINE_DENSITY_MS = 350;
var LINE_LERP_BASE = 0.08;
var LINE_ADAPTIVE_BOOST = 0.2;
var LINE_SNAP_THRESHOLD = 1e-3;
var RANGE_LERP_SPEED = 0.15;
var RANGE_ADAPTIVE_BOOST = 0.2;
var CANDLE_BUFFER_NO_BADGE = 0.015;
function computeAdaptiveSpeed(value, displayValue, displayMin, displayMax, lerpSpeed, noMotion) {
  const valGap = Math.abs(value - displayValue);
  const prevRange = displayMax - displayMin || 1;
  const gapRatio = Math.min(valGap / prevRange, 1);
  return noMotion ? 1 : lerpSpeed + (1 - gapRatio) * ADAPTIVE_SPEED_BOOST;
}
function updateRange(computedRange, rangeInited, targetMin, targetMax, displayMin, displayMax, isTransitioning, windowTransProgress, wt, adaptiveSpeed, chartH, dt, paintedRange) {
  if (!rangeInited) {
    displayMin = computedRange.min;
    displayMax = computedRange.max;
    targetMin = computedRange.min;
    targetMax = computedRange.max;
  } else if (isTransitioning) {
    displayMin = wt.rangeFromMin + (wt.rangeToMin - wt.rangeFromMin) * windowTransProgress;
    displayMax = wt.rangeFromMax + (wt.rangeToMax - wt.rangeFromMax) * windowTransProgress;
    targetMin = computedRange.min;
    targetMax = computedRange.max;
  } else {
    const curRange = displayMax - displayMin;
    targetMin = computedRange.min;
    targetMax = computedRange.max;
    displayMin = lerp(displayMin, targetMin, adaptiveSpeed, dt);
    displayMax = lerp(displayMax, targetMax, adaptiveSpeed, dt);
    const pxThreshold = 0.5 * curRange / chartH || 1e-3;
    if (Math.abs(displayMin - targetMin) < pxThreshold) displayMin = targetMin;
    if (Math.abs(displayMax - targetMax) < pxThreshold) displayMax = targetMax;
  }
  if (paintedRange !== void 0) {
    displayMin = Math.min(displayMin, paintedRange.min);
    displayMax = Math.max(displayMax, paintedRange.max);
  }
  return {
    minVal: displayMin,
    maxVal: displayMax,
    valRange: displayMax - displayMin || 1e-3,
    targetMin,
    targetMax,
    displayMin,
    displayMax,
    rangeInited: true
  };
}
function indexConfiguredSeries(configured) {
  const configuredById = /* @__PURE__ */ new Map();
  for (const series of configured ?? []) configuredById.set(series.id, series);
  return configuredById;
}
function resolveFrameMultiSeries(configuredById, frame) {
  if (frame === null) return [];
  return frame.series.flatMap((sourced) => {
    const series = configuredById.get(sourced.id);
    return series === void 0 ? [] : [{ ...series, data: sourced.data, value: sourced.value }];
  });
}
function resolveFrameAnimationDelta(frameSource, deltaMs, pausedDeltaMs) {
  return frameSource === void 0 ? deltaMs : pausedDeltaMs;
}
function filterOrderbookAt(orderbook, nowSeconds) {
  if (orderbook === void 0) return void 0;
  const eligible = (entry) => {
    if (!("size" in entry) || entry.at === void 0) return true;
    const ageSeconds = nowSeconds - entry.at;
    return ageSeconds >= 0 && ageSeconds < 6;
  };
  const bids = orderbook.bids.filter(eligible);
  const asks = orderbook.asks.filter(eligible);
  return bids.length === orderbook.bids.length && asks.length === orderbook.asks.length ? orderbook : { bids, asks };
}
function resolveFrameOrderbook(orderbook, sourceHeadSeconds) {
  return sourceHeadSeconds === void 0 ? orderbook : filterOrderbookAt(orderbook, sourceHeadSeconds);
}
function resolveFrameMarkers(markers, sourceHeadSeconds) {
  return sourceHeadSeconds === void 0 ? markers : markers?.filter((marker) => marker.time <= sourceHeadSeconds);
}
function resolveActiveFrameSourceHead(frameSource, rememberedHeadSeconds) {
  return frameSource === void 0 ? void 0 : rememberedHeadSeconds ?? void 0;
}
function updateHoverState(hoverPixelX, pad, w, layout, now, visible, smoothValue, scrubAmount, lastHover, cfg, noMotion, leftEdge, rightEdge, chartW, dt) {
  let hoverValue = null;
  let hoverTime = null;
  let hoverChartX = null;
  let isActiveHover = false;
  if (hoverPixelX !== null && hoverPixelX >= pad.left && hoverPixelX <= w - pad.right) {
    const maxHoverX = layout.toX(now);
    const clampedX = Math.min(hoverPixelX, maxHoverX);
    const t = leftEdge + (clampedX - pad.left) / chartW * (rightEdge - leftEdge);
    const v = interpolateRenderedLineAtTime(visible, now, smoothValue, t);
    if (v !== null) {
      hoverValue = v;
      hoverTime = t;
      hoverChartX = clampedX;
      isActiveHover = true;
      lastHover = { x: clampedX, value: v, time: t };
      cfg.onHover?.({ time: t, value: v, x: clampedX, y: layout.toY(v) });
    }
  }
  const scrubTarget = isActiveHover ? 1 : 0;
  if (noMotion) {
    scrubAmount = scrubTarget;
  } else {
    scrubAmount += (scrubTarget - scrubAmount) * SCRUB_LERP_SPEED;
    if (scrubAmount < 0.01) scrubAmount = 0;
    if (scrubAmount > 0.99) scrubAmount = 1;
  }
  let drawHoverX = hoverChartX;
  let drawHoverValue = hoverValue;
  let drawHoverTime = hoverTime;
  if (!isActiveHover && scrubAmount > 0 && lastHover) {
    drawHoverX = lastHover.x;
    drawHoverValue = lastHover.value;
    drawHoverTime = lastHover.time;
  }
  return {
    hoverX: drawHoverX,
    hoverValue: drawHoverValue,
    hoverTime: drawHoverTime,
    scrubAmount,
    isActiveHover,
    lastHover
  };
}
function updateBadgeDOM(badge, cfg, smoothValue, layout, momentum, badgeY, badgeColor, isWindowTransitioning, noMotion, ctx, dt, chartReveal = 1) {
  if (cfg.loading || !cfg.showBadge || chartReveal < 0.25) {
    badge.container.style.display = "none";
    return badgeY;
  }
  badge.container.style.display = "";
  const badgeOpacity = chartReveal < 0.5 ? (chartReveal - 0.25) / 0.25 : 1;
  badge.container.style.opacity = badgeOpacity < 1 ? String(badgeOpacity) : "";
  const { w, h, pad } = layout;
  const text = cfg.formatValue(smoothValue);
  badge.text.textContent = text;
  badge.text.style.font = cfg.palette.labelFont;
  badge.text.style.lineHeight = `${BADGE_LINE_H}px`;
  const tailLen = cfg.badgeTail ? BADGE_TAIL_LEN : 0;
  badge.text.style.padding = `${BADGE_PAD_Y}px ${BADGE_PAD_X}px ${BADGE_PAD_Y}px ${tailLen + BADGE_PAD_X}px`;
  ctx.font = cfg.palette.labelFont;
  const template = text.replace(/[0-9]/g, "8");
  const targetTextW = ctx.measureText(template).width;
  badge.targetW = targetTextW;
  if (badge.displayW === 0) badge.displayW = targetTextW;
  badge.displayW = lerp(badge.displayW, badge.targetW, BADGE_WIDTH_LERP, dt);
  if (Math.abs(badge.displayW - badge.targetW) < 0.3) badge.displayW = badge.targetW;
  const textW = badge.displayW;
  const pillW = textW + BADGE_PAD_X * 2;
  const pillH = BADGE_LINE_H + BADGE_PAD_Y * 2;
  const totalW = tailLen + pillW;
  badge.svg.setAttribute("width", String(Math.ceil(totalW)));
  badge.svg.setAttribute("height", String(pillH));
  badge.svg.setAttribute("viewBox", `0 0 ${totalW} ${pillH}`);
  badge.path.setAttribute(
    "d",
    cfg.badgeTail ? badgeSvgPath(pillW, pillH, BADGE_TAIL_LEN, BADGE_TAIL_SPREAD) : badgePillOnly(pillW, pillH)
  );
  const centerY = pad.top + layout.chartH / 2;
  const realTargetY = Math.max(pad.top, Math.min(h - pad.bottom, layout.toY(smoothValue)));
  const targetBadgeY = chartReveal < 1 ? centerY + (realTargetY - centerY) * chartReveal : realTargetY;
  if (badgeY === null || noMotion) {
    badgeY = targetBadgeY;
  } else {
    const badgeSpeed = isWindowTransitioning ? BADGE_Y_LERP_TRANSITIONING : BADGE_Y_LERP;
    badgeY = lerp(badgeY, targetBadgeY, badgeSpeed, dt);
  }
  const badgeLeft = w - pad.right + 8 - BADGE_PAD_X - tailLen;
  const badgeTop = badgeY - pillH / 2;
  badge.container.style.transform = `translate3d(${badgeLeft}px, ${badgeTop}px, 0)`;
  if (cfg.badgeVariant === "minimal") {
    badge.path.setAttribute("fill", cfg.palette.badgeOuterBg);
    badge.text.style.color = cfg.palette.tooltipText;
    badge.container.style.filter = `drop-shadow(0 1px 4px ${cfg.palette.badgeOuterShadow})`;
  } else {
    badge.container.style.filter = "";
    badge.text.style.color = "#fff";
    const bs = badgeColor;
    let fillColor;
    if (!cfg.showMomentum) {
      fillColor = cfg.palette.line;
    } else {
      const target = momentum === "up" ? 1 : momentum === "down" ? 0 : bs.green;
      bs.green = noMotion ? target : lerp(bs.green, target, MOMENTUM_COLOR_LERP, dt);
      if (bs.green > 0.99) bs.green = 1;
      if (bs.green < 0.01) bs.green = 0;
      const g = bs.green;
      const rr = Math.round(MOMENTUM_RED[0] + (MOMENTUM_GREEN[0] - MOMENTUM_RED[0]) * g);
      const gg = Math.round(MOMENTUM_RED[1] + (MOMENTUM_GREEN[1] - MOMENTUM_RED[1]) * g);
      const bb = Math.round(MOMENTUM_RED[2] + (MOMENTUM_GREEN[2] - MOMENTUM_RED[2]) * g);
      fillColor = `rgb(${rr},${gg},${bb})`;
    }
    badge.path.setAttribute("fill", fillColor);
  }
  return badgeY;
}
function computeCandleRange(candles) {
  let min = Infinity;
  let max = -Infinity;
  for (const c of candles) {
    if (c.low < min) min = c.low;
    if (c.high > max) max = c.high;
  }
  return computeRangeFromBounds(min, max);
}
function candleAtX(candles, hoverX, candleWidth, layout) {
  const time = layout.leftEdge + (hoverX - layout.pad.left) / layout.chartW * (layout.rightEdge - layout.leftEdge);
  let lo = 0;
  let hi = candles.length - 1;
  while (lo <= hi) {
    const mid = lo + hi >> 1;
    const c = candles[mid];
    if (time < c.time) hi = mid - 1;
    else if (time >= c.time + candleWidth) lo = mid + 1;
    else return c;
  }
  return null;
}
function updateCandleRange(computedRange, rangeInited, displayMin, displayMax, isTransitioning, windowTransProgress, wt, chartH, dt) {
  if (!rangeInited) {
    return {
      minVal: computedRange.min,
      maxVal: computedRange.max,
      valRange: computedRange.max - computedRange.min || 1e-3,
      displayMin: computedRange.min,
      displayMax: computedRange.max,
      rangeInited: true
    };
  }
  if (isTransitioning) {
    displayMin = wt.rangeFromMin + (wt.rangeToMin - wt.rangeFromMin) * windowTransProgress;
    displayMax = wt.rangeFromMax + (wt.rangeToMax - wt.rangeFromMax) * windowTransProgress;
  } else {
    const curRange = displayMax - displayMin || 1;
    const gapMin = Math.abs(displayMin - computedRange.min);
    const gapMax = Math.abs(displayMax - computedRange.max);
    const gapRatio = Math.min((gapMin + gapMax) / curRange, 1);
    const speed = RANGE_LERP_SPEED + (1 - gapRatio) * RANGE_ADAPTIVE_BOOST;
    displayMin = lerp(displayMin, computedRange.min, speed, dt);
    displayMax = lerp(displayMax, computedRange.max, speed, dt);
    const pxThreshold = 0.5 * curRange / chartH || 1e-3;
    if (Math.abs(displayMin - computedRange.min) < pxThreshold) displayMin = computedRange.min;
    if (Math.abs(displayMax - computedRange.max) < pxThreshold) displayMax = computedRange.max;
  }
  return {
    minVal: displayMin,
    maxVal: displayMax,
    valRange: displayMax - displayMin || 1e-3,
    displayMin,
    displayMax,
    rangeInited: true
  };
}
function updateCandleWindowTransition(targetWindowSecs, wt, displayWindow, displayMin, displayMax, noMotion, now_ms, now, candles, liveCandle, candleWidth, buffer) {
  if (wt.to !== targetWindowSecs) {
    wt.from = displayWindow;
    wt.to = targetWindowSecs;
    wt.startMs = now_ms;
    wt.rangeFromMin = displayMin;
    wt.rangeFromMax = displayMax;
    const targetRightEdge = now + targetWindowSecs * buffer;
    const targetLeftEdge = targetRightEdge - targetWindowSecs;
    const targetVisible = [];
    for (const c of candles) {
      if (c.time + candleWidth >= targetLeftEdge && c.time <= targetRightEdge) {
        targetVisible.push(c);
      }
    }
    if (liveCandle && liveCandle.time + candleWidth >= targetLeftEdge && liveCandle.time <= targetRightEdge) {
      targetVisible.push(liveCandle);
    }
    if (targetVisible.length > 0) {
      const tr = computeCandleRange(targetVisible);
      wt.rangeToMin = tr.min;
      wt.rangeToMax = tr.max;
    }
  }
  let windowTransProgress = 0;
  let completedWindowSecs = null;
  let resultWindow;
  if (noMotion || wt.startMs === 0) {
    resultWindow = targetWindowSecs;
    if (noMotion && wt.startMs !== 0) {
      wt.startMs = 0;
      completedWindowSecs = targetWindowSecs;
    }
  } else {
    const elapsed = now_ms - wt.startMs;
    const t = Math.min(elapsed / WINDOW_TRANSITION_MS2, 1);
    const eased = (1 - Math.cos(t * Math.PI)) / 2;
    windowTransProgress = eased;
    const logFrom = Math.log(wt.from);
    const logTo = Math.log(wt.to);
    resultWindow = Math.exp(logFrom + (logTo - logFrom) * eased);
    if (t >= 1) {
      resultWindow = targetWindowSecs;
      wt.startMs = 0;
      windowTransProgress = 0;
      completedWindowSecs = targetWindowSecs;
    }
  }
  return { windowSecs: resultWindow, windowTransProgress, completedWindowSecs };
}
function useLivelineEngine(canvasRef, containerRef, config) {
  const configRef = useRef(config);
  const reportedPlotLayoutRef = useRef(null);
  const frameStateRef = useRef(createLivelineFrameState());
  const sourceCanvasPaintedRef = useRef(false);
  const configuredSeriesByIdRef = useRef(indexConfiguredSeries(config.multiSeries));
  const frameReferenceLineRef = useRef(
    config.referenceLine?.value === void 0 ? void 0 : { ...config.referenceLine }
  );
  const displayValueRef = useRef(config.value);
  const displayValuesRef = useRef(/* @__PURE__ */ new Map());
  const seriesAlphaRef = useRef(/* @__PURE__ */ new Map());
  const displayMinRef = useRef(0);
  const displayMaxRef = useRef(0);
  const targetMinRef = useRef(0);
  const targetMaxRef = useRef(0);
  const rangeInitedRef = useRef(false);
  const displayWindowRef = useRef(config.windowSecs);
  const windowTransitionRef = useRef({
    historyStartTime: config.historyStartTime,
    from: config.windowSecs,
    to: config.windowSecs,
    startMs: 0,
    rangeFromMin: 0,
    rangeFromMax: 0,
    rangeToMin: 0,
    rangeToMax: 0
  });
  const arrowStateRef = useRef({ up: 0, down: 0 });
  const gridStateRef = useRef({ interval: 0, labels: /* @__PURE__ */ new Map() });
  const timeAxisStateRef = useRef({
    labels: /* @__PURE__ */ new Map()
  });
  const orderbookStateRef = useLazyRef(createOrderbookState);
  const sourceOrderbookFencedRef = useRef(config.frameSource === void 0);
  const lastSourceHeadSecondsRef = useRef(null);
  useLayoutEffect(() => {
    if (configRef.current.multiSeries !== config.multiSeries) {
      configuredSeriesByIdRef.current = indexConfiguredSeries(config.multiSeries);
    }
    if (configRef.current.referenceLine !== config.referenceLine) {
      frameReferenceLineRef.current = config.referenceLine?.value === void 0 ? void 0 : { ...config.referenceLine };
    }
    if (configRef.current.frameSource !== config.frameSource) {
      frameStateRef.current = createLivelineFrameState();
      sourceCanvasPaintedRef.current = false;
      lastSourceHeadSecondsRef.current = null;
      displayValuesRef.current.clear();
      rangeInitedRef.current = false;
      sourceOrderbookFencedRef.current = config.frameSource === void 0;
    }
    configRef.current = config;
    if (document.hidden && config.frameSource === void 0) {
      fenceOrderbookEntries(orderbookStateRef.current, config.orderbookData);
    }
  }, [config, orderbookStateRef]);
  const initialOrderbookFenceRef = useRef(true);
  useLayoutEffect(() => {
    if (!initialOrderbookFenceRef.current) return;
    initialOrderbookFenceRef.current = false;
    const cfg = configRef.current;
    if (cfg.frameSource !== void 0) return;
    const initialSeenIds = cfg.getOrderbookInitialSeenIds?.();
    if (initialSeenIds !== void 0) {
      fenceOrderbookIds(orderbookStateRef.current, initialSeenIds);
      return;
    }
    fenceOrderbookEntries(orderbookStateRef.current, cfg.orderbookData);
  }, [orderbookStateRef]);
  const particleStateRef = useLazyRef(
    () => createParticleState(config.degenOptions?.suppressInitialBurst === true)
  );
  const shakeStateRef = useLazyRef(createShakeState);
  const degenPresentationKey = `${config.mode}:${config.windowSecs}`;
  const degenPresentationStateRef = useRef({
    key: degenPresentationKey,
    transitioning: false
  });
  const badgeColorRef = useRef({ green: 1 });
  const badgeYRef = useRef(null);
  const reducedMotionRef = useRef(false);
  const sizeRef = useRef({ w: 0, h: 0 });
  const ctxRef = useRef(null);
  const rafRef = useRef(0);
  const lastFrameRef = useRef(0);
  const timeHorizonProgressRef = useRef(0);
  const badgeRef = useRef(null);
  const hoverXRef = useRef(null);
  const scrubAmountRef = useRef(0);
  const lastHoverRef = useRef(null);
  const lastHoverEntriesRef = useRef(
    []
  );
  const chartRevealRef = useRef(null);
  const pauseProgressRef = useRef(0);
  const timeDebtRef = useRef(0);
  const pausedSourceFrameRef = useRef(null);
  const lastDataRef = useRef([]);
  const lastMultiSeriesRef = useRef([]);
  const frozenNowRef = useRef(0);
  const pausedDataRef = useRef(null);
  const pausedMultiDataRef = useRef(
    null
  );
  const loadingAlphaRef = useRef(config.loading ? 1 : 0);
  const displayCandleRef = useRef(null);
  const liveBirthAlphaRef = useRef(1);
  const liveBullRef = useRef(0.5);
  const lineSmoothCloseRef = useRef(0);
  const lineSmoothInitedRef = useRef(false);
  const closeLineSmoothRef = useRef(0);
  const closeLineSmoothInitedRef = useRef(false);
  const lineModeProgRef = useRef(0);
  const lineModeTransRef = useRef({ startMs: 0, from: 0, to: 0 });
  const lineDensityProgRef = useRef(0);
  const lineDensityTransRef = useRef({ startMs: 0, from: 0, to: 0 });
  const lineTickSmoothRef = useRef(0);
  const lineTickSmoothInitedRef = useRef(false);
  const candleWidthTransRef = useRef({
    fromWidth: config.candleWidth ?? 1,
    toWidth: config.candleWidth ?? 1,
    startMs: 0,
    oldCandles: [],
    oldWidth: config.candleWidth ?? 1
  });
  const prevCandleDataRef = useRef({
    candles: [],
    width: config.candleWidth ?? 1
  });
  const pausedCandlesRef = useRef(null);
  const pausedLiveRef = useRef(null);
  const pausedLineDataRef = useRef(null);
  const pausedLineValueRef = useRef(null);
  const lastCandlesRef = useRef([]);
  const lastLiveRef = useRef(null);
  const lastLineDataStashRef = useRef([]);
  const lastLineValueStashRef = useRef(void 0);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const el = document.createElement("div");
    el.style.cssText = "position:absolute;top:0;left:0;pointer-events:none;will-change:transform;display:none;z-index:1;";
    const svg = document.createElementNS(SVG_NS, "svg");
    svg.style.cssText = "position:absolute;top:0;left:0;";
    const path = document.createElementNS(SVG_NS, "path");
    svg.appendChild(path);
    const text = document.createElement("span");
    text.style.cssText = "position:relative;display:block;color:#fff;white-space:nowrap;";
    el.appendChild(svg);
    el.appendChild(text);
    container.appendChild(el);
    badgeRef.current = { container: el, svg, path, text, displayW: 0, targetW: 0 };
    return () => {
      container.removeChild(el);
      badgeRef.current = null;
    };
  }, [containerRef]);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      sizeRef.current = { w: width, h: height };
    });
    ro.observe(container);
    const rect = container.getBoundingClientRect();
    sizeRef.current = { w: rect.width, h: rect.height };
    return () => ro.disconnect();
  }, [containerRef]);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onMove = (e) => {
      if (!configRef.current.scrub) return;
      const rect = container.getBoundingClientRect();
      hoverXRef.current = e.clientX - rect.left;
    };
    const onLeave = () => {
      hoverXRef.current = null;
      configRef.current.onHover?.(null);
    };
    const onTouchStart = (e) => {
      if (!configRef.current.scrub) return;
      if (e.touches.length !== 1) return;
      const rect = container.getBoundingClientRect();
      hoverXRef.current = e.touches[0].clientX - rect.left;
    };
    const onTouchMove = (e) => {
      if (!configRef.current.scrub) return;
      if (e.touches.length !== 1) return;
      e.preventDefault();
      const rect = container.getBoundingClientRect();
      hoverXRef.current = e.touches[0].clientX - rect.left;
    };
    const onTouchEnd = () => {
      hoverXRef.current = null;
      configRef.current.onHover?.(null);
    };
    container.addEventListener("mousemove", onMove);
    container.addEventListener("mouseleave", onLeave);
    container.addEventListener("touchstart", onTouchStart, { passive: true });
    container.addEventListener("touchmove", onTouchMove, { passive: false });
    container.addEventListener("touchend", onTouchEnd);
    container.addEventListener("touchcancel", onTouchEnd);
    return () => {
      container.removeEventListener("mousemove", onMove);
      container.removeEventListener("mouseleave", onLeave);
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onTouchMove);
      container.removeEventListener("touchend", onTouchEnd);
      container.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [containerRef]);
  useEffect(() => {
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    reducedMotionRef.current = mql.matches;
    const onChange = (e) => {
      reducedMotionRef.current = e.matches;
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  const draw = useCallback(() => {
    if (document.hidden) {
      rafRef.current = 0;
      return;
    }
    const canvas = canvasRef.current;
    const { w, h } = sizeRef.current;
    if (!canvas || w === 0 || h === 0) {
      rafRef.current = requestAnimationFrame(draw);
      return;
    }
    const cfg = configRef.current;
    if (chartRevealRef.current === null) {
      displayWindowRef.current = cfg.windowSecs;
      windowTransitionRef.current.from = cfg.windowSecs;
      windowTransitionRef.current.to = cfg.windowSecs;
      windowTransitionRef.current.historyStartTime = cfg.historyStartTime;
      windowTransitionRef.current.startMs = 0;
    }
    const dpr = getDpr();
    const now_ms = Number(document.timeline.currentTime ?? performance.now());
    const dt = lastFrameRef.current ? Math.min(now_ms - lastFrameRef.current, MAX_DELTA_MS) : 16.67;
    lastFrameRef.current = now_ms;
    const noMotion = reducedMotionRef.current;
    const isCandle = cfg.mode === "candle";
    let sourceFrame = null;
    if (!isCandle && cfg.frameSource !== void 0) {
      sourceFrame = cfg.paused && pausedSourceFrameRef.current !== null ? pausedSourceFrameRef.current : resolveLivelinePaintedFrame(
        frameStateRef.current,
        cfg.frameSource.getFrame(performance.now()),
        now_ms,
        cfg.frameSource.morphDurationMs,
        noMotion,
        cfg.frameSource.morphEasing
      );
      if (cfg.paused && pausedSourceFrameRef.current === null) {
        pausedSourceFrameRef.current = sourceFrame;
      }
      if (!cfg.paused) pausedSourceFrameRef.current = null;
    }
    if (sourceFrame !== null) lastSourceHeadSecondsRef.current = sourceFrame.nowSeconds;
    const presentationNowMs = now_ms;
    if (sourceFrame !== null && !sourceOrderbookFencedRef.current) {
      fenceOrderbookEntries(
        orderbookStateRef.current,
        filterOrderbookAt(cfg.orderbookData, sourceFrame.nowSeconds)
      );
      sourceOrderbookFencedRef.current = true;
    }
    const targetOpacity = sourceFrame?.opacity ?? 1;
    if (canvas.style.opacity !== String(targetOpacity)) {
      canvas.style.opacity = String(targetOpacity);
    }
    const targetW = Math.round(w * dpr);
    const targetH = Math.round(h * dpr);
    if (canvas.width !== targetW || canvas.height !== targetH) {
      canvas.width = targetW;
      canvas.height = targetH;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    }
    let ctx = ctxRef.current;
    if (!ctx || ctx.canvas !== canvas) {
      ctx = canvas.getContext("2d");
      ctxRef.current = ctx;
    }
    if (!ctx) {
      rafRef.current = requestAnimationFrame(draw);
      return;
    }
    applyDpr(ctx, dpr, w, h);
    if (sourceFrame === null) sourceCanvasPaintedRef.current = false;
    const configuredMultiSeries = cfg.multiSeries ?? [];
    const currentMultiSeries = cfg.frameSource === void 0 ? configuredMultiSeries : resolveFrameMultiSeries(configuredSeriesByIdRef.current, sourceFrame);
    if (isCandle) {
      if (cfg.paused && pausedCandlesRef.current === null && (cfg.candles?.length ?? 0) > 0) {
        pausedCandlesRef.current = cfg.candles.slice();
        pausedLiveRef.current = cfg.liveCandle ?? null;
        pausedLineDataRef.current = cfg.lineData?.slice() ?? null;
        pausedLineValueRef.current = cfg.lineValue ?? null;
      }
      if (!cfg.paused) {
        pausedCandlesRef.current = null;
        pausedLiveRef.current = null;
        pausedLineDataRef.current = null;
        pausedLineValueRef.current = null;
      }
    } else if (cfg.isMultiSeries) {
      if (cfg.paused && pausedMultiDataRef.current === null) {
        const snap = /* @__PURE__ */ new Map();
        for (const s of currentMultiSeries) {
          if (hasRenderableLineWindow(s.data)) {
            snap.set(s.id, { data: s.data.slice(), value: s.value });
          }
        }
        if (snap.size > 0) pausedMultiDataRef.current = snap;
      }
      if (!cfg.paused) {
        pausedMultiDataRef.current = null;
      }
    } else {
      if (cfg.paused && pausedDataRef.current === null && hasRenderableLineWindow(cfg.data)) {
        pausedDataRef.current = cfg.data.slice();
      }
      if (!cfg.paused) {
        pausedDataRef.current = null;
      }
    }
    const points = isCandle ? [] : pausedDataRef.current ?? cfg.data;
    const effectiveCandles = isCandle ? pausedCandlesRef.current ?? cfg.candles ?? [] : [];
    const hasMultiData = cfg.isMultiSeries && currentMultiSeries.some((s) => hasRenderableLineWindow(s.data) || Number.isFinite(s.value));
    const hasData = isCandle ? effectiveCandles.length > 0 || (pausedCandlesRef.current ? pausedLiveRef.current !== null : cfg.liveCandle !== void 0) : hasMultiData || hasRenderableLineWindow(points);
    const pad = { ...cfg.padding };
    const chartH = h - pad.top - pad.bottom;
    const pauseTarget = cfg.paused ? 1 : 0;
    pauseProgressRef.current = noMotion ? pauseTarget : lerp(pauseProgressRef.current, pauseTarget, PAUSE_PROGRESS_SPEED, dt);
    if (pauseProgressRef.current < 5e-3) pauseProgressRef.current = 0;
    if (pauseProgressRef.current > 0.995) pauseProgressRef.current = 1;
    const pauseProgress = pauseProgressRef.current;
    const pausedDt = dt * (1 - pauseProgress);
    const frameAnimationDt = resolveFrameAnimationDelta(cfg.frameSource, dt, pausedDt);
    const realDtSec = dt / 1e3;
    timeDebtRef.current += realDtSec * pauseProgress;
    if (!cfg.paused && timeDebtRef.current > 1e-3) {
      const catchUpSpeed = timeDebtRef.current > 10 ? PAUSE_CATCHUP_SPEED_FAST : PAUSE_CATCHUP_SPEED;
      timeDebtRef.current = lerp(timeDebtRef.current, 0, catchUpSpeed, dt);
      if (timeDebtRef.current < 0.01) timeDebtRef.current = 0;
    }
    const loadingTarget = cfg.loading ? 1 : 0;
    loadingAlphaRef.current = noMotion ? loadingTarget : lerp(loadingAlphaRef.current, loadingTarget, LOADING_ALPHA_SPEED, dt);
    if (loadingAlphaRef.current < 0.01) loadingAlphaRef.current = 0;
    if (loadingAlphaRef.current > 0.99) loadingAlphaRef.current = 1;
    const loadingAlpha = loadingAlphaRef.current;
    const revealTarget = !cfg.loading && hasData ? 1 : 0;
    const previousReveal = chartRevealRef.current ?? initialChartRevealProgress({ loading: cfg.loading ?? false, hasData });
    chartRevealRef.current = noMotion ? revealTarget : lerp(
      previousReveal,
      revealTarget,
      revealTarget === 1 ? CHART_REVEAL_SPEED_FWD : CHART_REVEAL_SPEED,
      dt
    );
    if (Math.abs(chartRevealRef.current - revealTarget) < 5e-3) {
      chartRevealRef.current = revealTarget;
    }
    const chartReveal = chartRevealRef.current;
    if (chartReveal < 0.01) {
      rangeInitedRef.current = false;
    }
    let useStash;
    let useMultiStash = false;
    if (isCandle) {
      useStash = !hasData && chartReveal > 5e-3 && lastCandlesRef.current.length > 0;
    } else {
      useMultiStash = !hasData && chartReveal > 5e-3 && lastMultiSeriesRef.current.length > 0;
      if (hasMultiData) {
        lastMultiSeriesRef.current = currentMultiSeries.map((s) => ({
          id: s.id,
          data: s.data.slice(),
          value: s.value,
          palette: s.palette,
          label: s.label
        }));
      }
      if (hasData && !cfg.isMultiSeries) lastMultiSeriesRef.current = [];
      useStash = !useMultiStash && !hasData && chartReveal > 5e-3 && hasRenderableLineWindow(lastDataRef.current);
      if (hasData && !cfg.isMultiSeries) lastDataRef.current = points;
    }
    if (isCandle) {
      const lmt = lineModeTransRef.current;
      const lineModeTarget = cfg.lineMode ? 1 : 0;
      if (lmt.to !== lineModeTarget) {
        lmt.from = lineModeProgRef.current;
        lmt.to = lineModeTarget;
        lmt.startMs = now_ms;
      }
      if (lmt.startMs > 0) {
        const elapsed = now_ms - lmt.startMs;
        const t = Math.min(elapsed / LINE_MORPH_MS, 1);
        lineModeProgRef.current = lmt.from + (lmt.to - lmt.from) * ((1 - Math.cos(t * Math.PI)) / 2);
        if (t >= 1) {
          lineModeProgRef.current = lmt.to;
          lmt.startMs = 0;
        }
      } else {
        lineModeProgRef.current = lmt.to;
      }
    }
    if (!hasData && !useStash && !useMultiStash) {
      const loadingColor = isCandle || cfg.isMultiSeries || lastMultiSeriesRef.current.length > 0 ? cfg.palette.gridLabel : void 0;
      if (loadingAlpha > 0.01) {
        drawLoading(ctx, w, h, pad, cfg.palette, now_ms, loadingAlpha, loadingColor);
      }
      if (1 - loadingAlpha > 0.01) {
        drawEmpty(ctx, w, h, pad, cfg.palette, 1 - loadingAlpha, now_ms, false, cfg.emptyText);
      }
      if (cfg.edgeFade) {
        ctx.save();
        ctx.globalCompositeOperation = "destination-out";
        const fadeGrad = ctx.createLinearGradient(pad.left, 0, pad.left + FADE_EDGE_WIDTH, 0);
        fadeGrad.addColorStop(0, "rgba(0, 0, 0, 1)");
        fadeGrad.addColorStop(1, "rgba(0, 0, 0, 0)");
        ctx.fillStyle = fadeGrad;
        ctx.fillRect(0, 0, pad.left + FADE_EDGE_WIDTH, h);
        ctx.restore();
      }
      if (badgeRef.current) badgeRef.current.container.style.display = "none";
      rafRef.current = requestAnimationFrame(draw);
      return;
    }
    let paintedWindowCompletion = null;
    if (isCandle) {
      const candleBuffer = CANDLE_BUFFER_NO_BADGE;
      const liveNow = cfg.nowSeconds ?? Date.now() / 1e3 - timeDebtRef.current;
      if (hasData) frozenNowRef.current = liveNow;
      const now = hasData || chartReveal < 5e-3 ? liveNow : frozenNowRef.current;
      const rawLive = pausedCandlesRef.current ? pausedLiveRef.current ?? void 0 : cfg.liveCandle;
      let effectiveLineData = pausedLineDataRef.current ?? cfg.lineData;
      let effectiveLineValue = pausedLineValueRef.current ?? cfg.lineValue;
      if (hasData && effectiveLineData && effectiveLineData.length > 0) {
        lastLineDataStashRef.current = effectiveLineData;
        lastLineValueStashRef.current = effectiveLineValue;
      }
      if (useStash && lastLineDataStashRef.current.length > 0) {
        effectiveLineData = lastLineDataStashRef.current;
        effectiveLineValue = lastLineValueStashRef.current;
      }
      const candleWidthSecs = cfg.candleWidth ?? 1;
      const cwt = candleWidthTransRef.current;
      let morphT = -1;
      let displayCandleWidth;
      if (cwt.startMs > 0) {
        const elapsed = now_ms - cwt.startMs;
        const t = Math.min(elapsed / CANDLE_WIDTH_TRANS_MS, 1);
        morphT = (1 - Math.cos(t * Math.PI)) / 2;
        displayCandleWidth = candleWidthSecs;
        if (t >= 1) {
          displayCandleWidth = cwt.toWidth;
          cwt.startMs = 0;
          morphT = -1;
        }
      } else {
        displayCandleWidth = cwt.toWidth;
      }
      if (candleWidthSecs !== cwt.toWidth) {
        if (noMotion || prevCandleDataRef.current.candles.length === 0) {
          cwt.fromWidth = candleWidthSecs;
          cwt.toWidth = candleWidthSecs;
          cwt.startMs = 0;
          displayCandleWidth = candleWidthSecs;
          morphT = -1;
        } else {
          cwt.oldCandles = prevCandleDataRef.current.candles;
          cwt.oldWidth = prevCandleDataRef.current.width;
          cwt.fromWidth = displayCandleWidth;
          cwt.toWidth = candleWidthSecs;
          cwt.startMs = now_ms;
          morphT = 0;
          displayCandleWidth = candleWidthSecs;
        }
      }
      prevCandleDataRef.current = { candles: cfg.candles ?? [], width: candleWidthSecs };
      const lineModeProg = lineModeProgRef.current;
      const ldt = lineDensityTransRef.current;
      const hasTickData = effectiveLineData && effectiveLineData.length > 0;
      const densityTarget = cfg.lineMode && lineModeProg >= 0.3 && hasTickData ? 1 : 0;
      if (ldt.to !== densityTarget) {
        ldt.from = lineDensityProgRef.current;
        ldt.to = densityTarget;
        ldt.startMs = now_ms;
      }
      let lineDensityProg;
      if (ldt.startMs > 0) {
        const elapsed = now_ms - ldt.startMs;
        const t = Math.min(elapsed / LINE_DENSITY_MS, 1);
        lineDensityProg = ldt.from + (ldt.to - ldt.from) * (1 - (1 - t) * (1 - t));
        if (t >= 1) {
          lineDensityProg = ldt.to;
          ldt.startMs = 0;
        }
      } else {
        lineDensityProg = ldt.to;
      }
      lineDensityProgRef.current = lineDensityProg;
      const transition = windowTransitionRef.current;
      const windowResult = updateCandleWindowTransition(
        cfg.windowSecs,
        transition,
        displayWindowRef.current,
        displayMinRef.current,
        displayMaxRef.current,
        noMotion,
        now_ms,
        now,
        effectiveCandles,
        rawLive,
        candleWidthSecs,
        candleBuffer
      );
      paintedWindowCompletion = windowResult.completedWindowSecs;
      displayWindowRef.current = windowResult.windowSecs;
      const windowSecs = windowResult.windowSecs;
      const windowTransProgress = windowResult.windowTransProgress;
      const isWindowTransitioning = transition.startMs > 0;
      syncDegenPresentation(
        degenPresentationStateRef.current,
        `${cfg.mode}:${cfg.windowSecs}`,
        isWindowTransitioning,
        particleStateRef.current,
        shakeStateRef.current
      );
      const rightEdge = now + windowSecs * candleBuffer;
      const leftEdge = rightEdge - windowSecs;
      let smoothLive;
      if (rawLive) {
        const prev = displayCandleRef.current;
        if (!prev || prev.time !== rawLive.time) {
          displayCandleRef.current = {
            time: rawLive.time,
            open: rawLive.open,
            high: rawLive.open,
            low: rawLive.open,
            close: rawLive.open
          };
          liveBirthAlphaRef.current = 0;
        } else {
          const dc2 = displayCandleRef.current;
          dc2.open = lerp(dc2.open, rawLive.open, CANDLE_LERP_SPEED, pausedDt);
          dc2.high = lerp(dc2.high, rawLive.high, CANDLE_LERP_SPEED, pausedDt);
          dc2.low = lerp(dc2.low, rawLive.low, CANDLE_LERP_SPEED, pausedDt);
          dc2.close = lerp(dc2.close, rawLive.close, CANDLE_LERP_SPEED, pausedDt);
        }
        liveBirthAlphaRef.current = lerp(liveBirthAlphaRef.current, 1, 0.2, pausedDt);
        if (liveBirthAlphaRef.current > 0.99) liveBirthAlphaRef.current = 1;
        const dc = displayCandleRef.current;
        const bullTarget = dc.close >= dc.open ? 1 : 0;
        liveBullRef.current = lerp(liveBullRef.current, bullTarget, 0.12, pausedDt);
        if (liveBullRef.current > 0.99) liveBullRef.current = 1;
        if (liveBullRef.current < 0.01) liveBullRef.current = 0;
        smoothLive = dc;
      } else {
        displayCandleRef.current = null;
        liveBirthAlphaRef.current = 1;
        liveBullRef.current = 0.5;
      }
      if (rawLive) {
        if (!closeLineSmoothInitedRef.current) {
          closeLineSmoothRef.current = rawLive.close;
          closeLineSmoothInitedRef.current = true;
        } else {
          closeLineSmoothRef.current = lerp(
            closeLineSmoothRef.current,
            rawLive.close,
            CLOSE_LINE_LERP_SPEED,
            pausedDt
          );
          const gap = Math.abs(closeLineSmoothRef.current - rawLive.close);
          const range = displayMaxRef.current - displayMinRef.current || 1;
          if (gap < range * 5e-4) closeLineSmoothRef.current = rawLive.close;
        }
      } else if (!useStash) {
        closeLineSmoothInitedRef.current = false;
      }
      if (rawLive) {
        if (!lineSmoothInitedRef.current) {
          lineSmoothCloseRef.current = rawLive.close;
          lineSmoothInitedRef.current = true;
        } else {
          const valGap = Math.abs(rawLive.close - lineSmoothCloseRef.current);
          const prevRange = displayMaxRef.current - displayMinRef.current || 1;
          const gapRatio = Math.min(valGap / prevRange, 1);
          const adaptiveSpeed = LINE_LERP_BASE + (1 - gapRatio) * LINE_ADAPTIVE_BOOST;
          lineSmoothCloseRef.current = lerp(
            lineSmoothCloseRef.current,
            rawLive.close,
            adaptiveSpeed,
            pausedDt
          );
          if (valGap < prevRange * LINE_SNAP_THRESHOLD) lineSmoothCloseRef.current = rawLive.close;
        }
      } else if (!useStash) {
        lineSmoothInitedRef.current = false;
      }
      if (effectiveLineValue !== void 0 && hasTickData) {
        if (!lineTickSmoothInitedRef.current) {
          lineTickSmoothRef.current = effectiveLineValue;
          lineTickSmoothInitedRef.current = true;
        } else {
          const valGap = Math.abs(effectiveLineValue - lineTickSmoothRef.current);
          const prevRange = displayMaxRef.current - displayMinRef.current || 1;
          const gapRatio = Math.min(valGap / prevRange, 1);
          const adaptiveSpeed = LINE_LERP_BASE + (1 - gapRatio) * LINE_ADAPTIVE_BOOST;
          lineTickSmoothRef.current = lerp(
            lineTickSmoothRef.current,
            effectiveLineValue,
            adaptiveSpeed,
            pausedDt
          );
          if (valGap < prevRange * LINE_SNAP_THRESHOLD)
            lineTickSmoothRef.current = effectiveLineValue;
        }
      } else if (!useStash) {
        lineTickSmoothInitedRef.current = false;
      }
      const visible = [];
      for (const c of effectiveCandles) {
        if (c.time + candleWidthSecs >= leftEdge && c.time <= rightEdge) visible.push(c);
      }
      if (smoothLive && smoothLive.time + displayCandleWidth >= leftEdge && smoothLive.time <= rightEdge) {
        visible.push(smoothLive);
      }
      const oldVisible = [];
      if (morphT >= 0 && cwt.oldCandles.length > 0) {
        for (const c of cwt.oldCandles) {
          if (c.time + cwt.oldWidth >= leftEdge && c.time <= rightEdge) oldVisible.push(c);
        }
      }
      if (hasData) {
        lastCandlesRef.current = visible;
        lastLiveRef.current = smoothLive ?? null;
      }
      const effectiveVisible = useStash ? lastCandlesRef.current : visible;
      const effectiveLive = useStash ? lastLiveRef.current ?? void 0 : smoothLive;
      let chartW = w - pad.left - pad.right;
      const computed = effectiveVisible.length > 0 ? computeCandleRange([...effectiveVisible, ...oldVisible]) : { min: displayMinRef.current, max: displayMaxRef.current };
      const rangeResult = updateCandleRange(
        computed,
        rangeInitedRef.current,
        displayMinRef.current,
        displayMaxRef.current,
        isWindowTransitioning,
        windowTransProgress,
        transition,
        chartH,
        pausedDt
      );
      rangeResult.minVal = Math.min(rangeResult.minVal, computed.min);
      rangeResult.maxVal = Math.max(rangeResult.maxVal, computed.max);
      rangeResult.valRange = rangeResult.maxVal - rangeResult.minVal || rangeResult.valRange;
      rangeInitedRef.current = rangeResult.rangeInited;
      displayMinRef.current = rangeResult.displayMin;
      displayMaxRef.current = rangeResult.displayMax;
      const { minVal, maxVal, valRange } = rangeResult;
      const measurementLayout = {
        w,
        h,
        pad,
        chartW,
        chartH,
        leftEdge,
        rightEdge,
        minVal,
        maxVal,
        valRange,
        toX: (t) => pad.left + (t - leftEdge) / (rightEdge - leftEdge) * chartW,
        toY: (v) => pad.top + (1 - (v - minVal) / valRange) * chartH
      };
      applyMeasuredYAxisPadding(ctx, measurementLayout, cfg, gridStateRef.current);
      chartW = w - pad.left - pad.right;
      if (reportedPlotLayoutRef.current?.canvasWidth !== w || reportedPlotLayoutRef.current.showGrid !== cfg.showGrid) {
        reportedPlotLayoutRef.current = { canvasWidth: w, showGrid: cfg.showGrid };
        cfg.onPlotWidthChange?.(chartW);
      }
      const layout = {
        ...measurementLayout,
        chartW,
        toX: (t) => pad.left + (t - leftEdge) / (rightEdge - leftEdge) * chartW
      };
      const hoverPx = hoverXRef.current;
      let hoveredCandle = null;
      let isActiveHover = false;
      if (hoverPx !== null && hoverPx >= pad.left && hoverPx <= w - pad.right) {
        hoveredCandle = candleAtX(effectiveVisible, hoverPx, displayCandleWidth, layout);
        if (hoveredCandle) isActiveHover = true;
      }
      const scrubTarget = isActiveHover ? 1 : 0;
      scrubAmountRef.current = lerp(scrubAmountRef.current, scrubTarget, 0.12, dt);
      if (scrubAmountRef.current < 0.01) scrubAmountRef.current = 0;
      if (scrubAmountRef.current > 0.99) scrubAmountRef.current = 1;
      const scrubAmount = scrubAmountRef.current;
      let drawHoverX = hoverPx;
      let drawHoverTime = 0;
      let drawHoverCandle = hoveredCandle;
      if (!isActiveHover && scrubAmount > 0 && lastHoverRef.current) {
        drawHoverX = lastHoverRef.current.x;
        drawHoverTime = lastHoverRef.current.time;
        drawHoverCandle = candleAtX(
          effectiveVisible,
          lastHoverRef.current.x,
          displayCandleWidth,
          layout
        );
      } else if (isActiveHover && hoverPx !== null) {
        drawHoverTime = layout.leftEdge + (hoverPx - pad.left) / chartW * (layout.rightEdge - layout.leftEdge);
        lastHoverRef.current = {
          x: hoverPx,
          value: hoveredCandle?.close ?? 0,
          time: drawHoverTime
        };
      }
      let drawCandles = effectiveVisible;
      let drawOldCandles = oldVisible;
      let drawLive = effectiveLive;
      if (lineModeProg > 0.01 && drawLive && lineSmoothInitedRef.current) {
        const blended = drawLive.close + (lineSmoothCloseRef.current - drawLive.close) * lineModeProg;
        drawLive = { ...drawLive, close: blended };
        const li = drawCandles.length - 1;
        if (li >= 0 && drawCandles[li].time === drawLive.time) {
          drawCandles = drawCandles.slice();
          drawCandles[li] = { ...drawCandles[li], close: blended };
        }
      }
      if (lineModeProg > 0.01 && lineModeProg < 0.99) {
        const collapseOHLC = (c) => {
          const inv = 1 - lineModeProg;
          return {
            time: c.time,
            open: c.close + (c.open - c.close) * inv,
            high: c.close + (c.high - c.close) * inv,
            low: c.close + (c.low - c.close) * inv,
            close: c.close
          };
        };
        drawCandles = drawCandles.map(collapseOHLC);
        if (drawOldCandles.length > 0) drawOldCandles = drawOldCandles.map(collapseOHLC);
        if (drawLive) drawLive = collapseOHLC(drawLive);
      }
      let lineVisible;
      let lineSmoothValue;
      if (effectiveLineData && effectiveLineData.length > 0 && (lineDensityProg > 0.01 || lineModeProg > 0.05)) {
        const closeRefs = [];
        for (const c of drawCandles) {
          closeRefs.push({ t: c.time + displayCandleWidth / 2, v: c.close });
        }
        if (drawLive) closeRefs.push({ t: now, v: drawLive.close });
        lineVisible = [];
        let refIdx = 0;
        for (const pt of effectiveLineData) {
          if (pt.time < leftEdge || pt.time > rightEdge) continue;
          while (refIdx < closeRefs.length - 2 && closeRefs[refIdx + 1].t < pt.time) refIdx++;
          let interpClose;
          if (closeRefs.length === 0) {
            interpClose = pt.value;
          } else if (closeRefs.length === 1 || pt.time <= closeRefs[0].t) {
            interpClose = closeRefs[0].v;
          } else if (refIdx >= closeRefs.length - 1) {
            interpClose = closeRefs[closeRefs.length - 1].v;
          } else {
            const a = closeRefs[refIdx];
            const b = closeRefs[refIdx + 1];
            const span = b.t - a.t;
            const frac = span > 0 ? Math.max(0, Math.min(1, (pt.time - a.t) / span)) : 0;
            interpClose = a.v + (b.v - a.v) * frac;
          }
          const blended = interpClose + (pt.value - interpClose) * lineDensityProg;
          lineVisible.push({ time: pt.time, value: blended });
        }
        const smoothTick = lineTickSmoothInitedRef.current ? lineTickSmoothRef.current : effectiveLineValue ?? effectiveLineData[effectiveLineData.length - 1].value;
        lineSmoothValue = lineSmoothCloseRef.current + (smoothTick - lineSmoothCloseRef.current) * lineDensityProg;
      } else {
        lineVisible = drawCandles.map((c) => ({
          time: c.time + displayCandleWidth / 2,
          value: c.close
        }));
        lineSmoothValue = lineSmoothInitedRef.current ? lineSmoothCloseRef.current : drawLive?.close ?? drawCandles[drawCandles.length - 1]?.close ?? 0;
      }
      if (chartReveal < 1 && lineVisible.length >= 2) {
        const firstTime = lineVisible[0].time;
        const windowSpan = rightEdge - leftEdge;
        if (firstTime - leftEdge > windowSpan * 0.05) {
          const firstVal = lineVisible[0].value;
          const step = windowSpan / 32;
          const padded = [];
          for (let t = leftEdge; t < firstTime - step * 0.5; t += step) {
            padded.push({ time: t, value: firstVal });
          }
          lineVisible = [...padded, ...lineVisible];
        }
      }
      drawCandleFrame(ctx, layout, cfg.palette, {
        referenceLines: cfg.referenceLines,
        reducedMotion: noMotion,
        loading: cfg.loading,
        showPulse: cfg.showPulse,
        orderbookData: cfg.orderbookData,
        orderbookState: cfg.orderbookData ? orderbookStateRef.current : void 0,
        candles: drawCandles,
        displayCandleWidth,
        oldCandles: drawOldCandles,
        oldWidth: cwt.oldWidth,
        morphT,
        liveCandle: drawLive,
        closePriceCandle: closeLineSmoothInitedRef.current && rawLive ? { ...rawLive, close: closeLineSmoothRef.current } : rawLive,
        liveTime: effectiveLive?.time ?? -1,
        liveBirthAlpha: liveBirthAlphaRef.current,
        liveBullBlend: liveBullRef.current,
        lineModeProg,
        chartReveal,
        now_ms: presentationNowMs,
        now,
        pauseProgress,
        showGrid: cfg.showGrid,
        showTimeAxis: cfg.showTimeAxis,
        showValueLine: cfg.showValueLine,
        useSeriesColorForDashLines: cfg.useSeriesColorForDashLines,
        scrubAmount,
        hoverX: drawHoverX,
        hoverValue: drawHoverCandle?.close ?? null,
        hoverTime: drawHoverTime,
        hoveredCandle: drawHoverCandle,
        formatValue: cfg.formatValue,
        formatTime: cfg.formatTime,
        gridState: gridStateRef.current,
        timeAxisState: timeAxisStateRef.current,
        dt: pausedDt,
        targetWindowSecs: cfg.windowSecs,
        tooltipY: cfg.tooltipY,
        tooltipOutline: cfg.tooltipOutline,
        lineVisible,
        lineSmoothValue,
        emptyText: cfg.emptyText,
        loadingAlpha,
        // Show empty overlay when not loading AND loadingAlpha has fully
        // decayed. This prevents the gradient gap from flashing during
        // loading→live (where loadingAlpha starts at ~1), while still
        // allowing smooth fade-out during empty→live (loadingAlpha is 0).
        showEmptyOverlay: !(cfg.loading ?? false) && loadingAlpha < 0.01,
        lineCurve: cfg.lineCurve,
        edgeFade: cfg.edgeFade
      });
      if (badgeRef.current) {
        if (lineModeProg > 0.5 && cfg.showBadge) {
          const momentum = detectMomentum(lineVisible);
          badgeYRef.current = updateBadgeDOM(
            badgeRef.current,
            cfg,
            lineSmoothValue,
            layout,
            momentum,
            badgeYRef.current,
            badgeColorRef.current,
            isWindowTransitioning,
            noMotion,
            ctx,
            pausedDt,
            chartReveal
          );
          const badgeFade = (lineModeProg - 0.5) * 2;
          if (badgeRef.current.container.style.display !== "none") {
            const base = badgeRef.current.container.style.opacity ? parseFloat(badgeRef.current.container.style.opacity) : 1;
            badgeRef.current.container.style.opacity = String(
              base * badgeFade * (1 - pauseProgress)
            );
          }
        } else {
          badgeRef.current.container.style.display = "none";
        }
      }
    } else if (cfg.isMultiSeries && currentMultiSeries.length > 0 || useMultiStash) {
      const effectiveMultiSeries = useMultiStash ? lastMultiSeriesRef.current : currentMultiSeries;
      let chartW = w - pad.left - pad.right;
      const buffer = cfg.windowBuffer ?? (cfg.showBadge ? WINDOW_BUFFER : WINDOW_BUFFER_NO_BADGE);
      if (!useMultiStash) {
        const currentIds = new Set(effectiveMultiSeries.map((s) => s.id));
        for (const key of displayValuesRef.current.keys()) {
          if (!currentIds.has(key)) displayValuesRef.current.delete(key);
        }
      }
      const firstSeries = effectiveMultiSeries[0];
      const transition = windowTransitionRef.current;
      const liveNow = sourceFrame?.nowSeconds ?? cfg.nowSeconds ?? Date.now() / 1e3 - timeDebtRef.current;
      if (hasData) frozenNowRef.current = liveNow;
      const now = useMultiStash ? frozenNowRef.current : liveNow;
      let frameReferenceLine = cfg.referenceLine;
      if (sourceFrame?.referenceValue !== void 0 && frameReferenceLineRef.current?.value !== void 0) {
        frameReferenceLineRef.current.value = sourceFrame.referenceValue;
        frameReferenceLine = frameReferenceLineRef.current;
      }
      if (sourceFrame !== null && frameReferenceLineRef.current?.value !== void 0) {
        frameReferenceLineRef.current.points = sourceFrame.referencePoints;
        frameReferenceLine = frameReferenceLineRef.current;
      }
      const smoothValues = /* @__PURE__ */ new Map();
      for (const s of effectiveMultiSeries) {
        let dv = displayValuesRef.current.get(s.id);
        if (dv === void 0) dv = s.value;
        if (!useMultiStash && sourceFrame === null) {
          const adaptiveSpeed2 = computeAdaptiveSpeed(
            s.value,
            dv,
            displayMinRef.current,
            displayMaxRef.current,
            cfg.lerpSpeed,
            noMotion
          );
          dv = lerp(dv, s.value, adaptiveSpeed2, pausedDt);
          const prevRange = displayMaxRef.current - displayMinRef.current || 1;
          if (Math.abs(dv - s.value) < prevRange * VALUE_SNAP_THRESHOLD) dv = s.value;
          displayValuesRef.current.set(s.id, dv);
        }
        if (sourceFrame !== null) {
          dv = s.value;
          displayValuesRef.current.set(s.id, s.value);
        }
        smoothValues.set(s.id, dv);
      }
      const hiddenIds = cfg.hiddenSeriesIds;
      const seriesAlphas = seriesAlphaRef.current;
      for (const s of effectiveMultiSeries) {
        let alpha = seriesAlphas.get(s.id) ?? 1;
        const target = hiddenIds?.has(s.id) ? 0 : 1;
        alpha = noMotion ? target : lerp(alpha, target, SERIES_TOGGLE_SPEED, pausedDt);
        if (alpha < 0.01) alpha = 0;
        if (alpha > 0.99) alpha = 1;
        seriesAlphas.set(s.id, alpha);
      }
      const firstData = pausedMultiDataRef.current?.get(firstSeries.id)?.data ?? firstSeries.data;
      const windowResult = updateWindowTransition(
        cfg,
        transition,
        displayWindowRef.current,
        displayMinRef.current,
        displayMaxRef.current,
        noMotion,
        now_ms,
        now,
        firstData,
        pausedMultiDataRef.current?.get(firstSeries.id)?.value ?? firstSeries.value,
        buffer,
        frameReferenceLine
      );
      paintedWindowCompletion = windowResult.completedWindowSecs;
      if (transition.startMs > 0 && effectiveMultiSeries.length > 1) {
        const targetWindow = resolveLineWindow(cfg.windowSecs, cfg.historyStartTime, now, buffer);
        const targetRightEdge = now + targetWindow * buffer;
        const targetLeftEdge = targetRightEdge - targetWindow;
        let unionMin = Infinity;
        let unionMax = -Infinity;
        for (const s of effectiveMultiSeries) {
          const sData = pausedMultiDataRef.current?.get(s.id)?.data ?? s.data;
          const sv = pausedMultiDataRef.current?.get(s.id)?.value ?? s.value;
          if (sData.length > 0) {
            const bounds = computeWindowBounds(sData, targetLeftEdge, targetRightEdge, now, sv);
            if (bounds.min < unionMin) unionMin = bounds.min;
            if (bounds.max > unionMax) unionMax = bounds.max;
          }
        }
        if (isFinite(unionMin) && isFinite(unionMax)) {
          const unionRange = computeConfiguredRange(cfg, unionMin, unionMax, frameReferenceLine, {
            start: targetLeftEdge,
            end: targetRightEdge
          });
          transition.rangeToMin = unionRange.min;
          transition.rangeToMax = unionRange.max;
        }
      }
      displayWindowRef.current = windowResult.windowSecs;
      const windowSecs = windowResult.windowSecs;
      const windowTransProgress = windowResult.windowTransProgress;
      const isWindowTransitioning = transition.startMs > 0;
      const allowDegenEffects = syncDegenPresentation(
        degenPresentationStateRef.current,
        `${cfg.mode}:${cfg.windowSecs}`,
        isWindowTransitioning,
        particleStateRef.current,
        shakeStateRef.current
      );
      const baseRightEdge = now + windowSecs * buffer;
      const baseLeftEdge = baseRightEdge - windowSecs;
      timeHorizonProgressRef.current = stepTimeHorizonProgress(
        timeHorizonProgressRef.current,
        cfg.timeHorizon !== void 0 && hoverXRef.current !== null,
        frameAnimationDt,
        cfg.timeHorizon?.durationMs ?? 420,
        noMotion
      );
      const horizonFrame = resolveTimeHorizonFrame(
        baseLeftEdge,
        baseRightEdge,
        now,
        cfg.timeHorizon?.at ?? now,
        timeHorizonProgressRef.current
      );
      const { leftEdge, rightEdge } = horizonFrame;
      const filterRight = rightEdge - (rightEdge - now) * pauseProgress;
      const seriesEntries = [];
      let globalMin = Infinity;
      let globalMax = -Infinity;
      let paintedMin = Infinity;
      let paintedMax = -Infinity;
      for (const s of effectiveMultiSeries) {
        const snap = pausedMultiDataRef.current?.get(s.id);
        const seriesData = snap?.data ?? s.data;
        const liveValue = smoothValues.get(s.id) ?? s.value;
        const visible = buildRenderableLineWindow(
          seriesData,
          leftEdge - 2,
          Math.min(filterRight, now),
          now,
          liveValue
        );
        const projection = resolveTimeHorizonProjection(
          cfg.timeHorizon?.series?.[s.id],
          now,
          horizonFrame.lineEndTime,
          liveValue
        );
        visible.push(...projection.points);
        const displayedValue = projection.value;
        const alpha = seriesAlphas.get(s.id) ?? 1;
        if (hasRenderableLineWindow(visible)) {
          if (alpha > 0.01) {
            const rangePoints = projection.points.length === 0 ? seriesData : [...seriesData, ...projection.points];
            const rangeValue = projection.points.length === 0 ? snap?.value ?? s.value : displayedValue;
            const bounds = computeWindowBounds(
              rangePoints,
              leftEdge,
              filterRight,
              horizonFrame.lineEndTime,
              rangeValue
            );
            if (bounds.min < globalMin) globalMin = bounds.min;
            if (bounds.max > globalMax) globalMax = bounds.max;
            const paintedBounds = computeWindowBounds(
              visible,
              leftEdge,
              filterRight,
              horizonFrame.lineEndTime,
              displayedValue
            );
            paintedMin = Math.min(paintedMin, paintedBounds.min);
            paintedMax = Math.max(paintedMax, paintedBounds.max);
          }
          seriesEntries.push({
            id: s.id,
            visible,
            smoothValue: displayedValue,
            palette: s.palette,
            label: s.label,
            alpha,
            ...projection.points.length === 0 ? {} : {
              futureStartTime: now,
              futureOpacity: cfg.timeHorizon?.projectionOpacity ?? 0.6,
              ...cfg.timeHorizon?.endpointMarkers?.[s.id] === void 0 ? {} : { endpointMarker: cfg.timeHorizon.endpointMarkers[s.id] }
            },
            momentum: detectMomentum(visible),
            swingMagnitude: 0
          });
        }
      }
      if (seriesEntries.length === 0) {
        if (loadingAlpha > 0.01) {
          drawLoading(ctx, w, h, pad, cfg.palette, now_ms, loadingAlpha, cfg.palette.gridLabel);
        }
        if (1 - loadingAlpha > 0.01) {
          drawEmpty(ctx, w, h, pad, cfg.palette, 1 - loadingAlpha, now_ms, false, cfg.emptyText);
        }
        if (cfg.edgeFade) {
          ctx.save();
          ctx.globalCompositeOperation = "destination-out";
          const fadeGrad = ctx.createLinearGradient(pad.left, 0, pad.left + FADE_EDGE_WIDTH, 0);
          fadeGrad.addColorStop(0, "rgba(0, 0, 0, 1)");
          fadeGrad.addColorStop(1, "rgba(0, 0, 0, 0)");
          ctx.fillStyle = fadeGrad;
          ctx.fillRect(0, 0, pad.left + FADE_EDGE_WIDTH, h);
          ctx.restore();
        }
        if (badgeRef.current) badgeRef.current.container.style.display = "none";
        rafRef.current = requestAnimationFrame(draw);
        return;
      }
      const fittedRange = computeConfiguredRange(
        cfg,
        isFinite(globalMin) ? globalMin : 0,
        isFinite(globalMax) ? globalMax : 1,
        frameReferenceLine,
        { start: leftEdge, end: filterRight }
      );
      const adaptiveSpeed = noMotion ? 1 : Math.max(MULTI_RANGE_LERP_SPEED, cfg.lerpSpeed + ADAPTIVE_SPEED_BOOST * 0.5);
      const rangeResult = updateRange(
        fittedRange,
        rangeInitedRef.current,
        targetMinRef.current,
        targetMaxRef.current,
        displayMinRef.current,
        displayMaxRef.current,
        isWindowTransitioning,
        windowTransProgress,
        transition,
        adaptiveSpeed,
        chartH,
        pausedDt,
        Number.isFinite(paintedMin) && Number.isFinite(paintedMax) ? computeRangeFromBounds(paintedMin, paintedMax, void 0, cfg.exaggerate) : void 0
      );
      rangeInitedRef.current = rangeResult.rangeInited;
      targetMinRef.current = rangeResult.targetMin;
      targetMaxRef.current = rangeResult.targetMax;
      displayMinRef.current = rangeResult.displayMin;
      displayMaxRef.current = rangeResult.displayMax;
      const { minVal, maxVal, valRange } = rangeResult;
      for (const entry of seriesEntries) {
        const lookback = Math.min(5, entry.visible.length - 1);
        const recentDelta = lookback > 0 ? Math.abs(
          entry.visible[entry.visible.length - 1].value - entry.visible[entry.visible.length - 1 - lookback].value
        ) : 0;
        entry.swingMagnitude = valRange > 0 ? Math.min(recentDelta / valRange, 1) : 0;
      }
      const measurementLayout = {
        w,
        h,
        pad,
        chartW,
        chartH,
        leftEdge,
        rightEdge,
        minVal,
        maxVal,
        valRange,
        toX: (t) => pad.left + (t - leftEdge) / (rightEdge - leftEdge) * chartW,
        toY: (v) => pad.top + (1 - (v - minVal) / valRange) * chartH
      };
      applyMeasuredYAxisPadding(ctx, measurementLayout, cfg, gridStateRef.current);
      chartW = w - pad.left - pad.right;
      if (reportedPlotLayoutRef.current?.canvasWidth !== w || reportedPlotLayoutRef.current.showGrid !== cfg.showGrid) {
        reportedPlotLayoutRef.current = { canvasWidth: w, showGrid: cfg.showGrid };
        cfg.onPlotWidthChange?.(chartW);
      }
      const layout = {
        ...measurementLayout,
        chartW,
        toX: (t) => pad.left + (t - leftEdge) / (rightEdge - leftEdge) * chartW
      };
      const hoverPx = hoverXRef.current;
      let drawHoverX = null;
      let drawHoverTime = null;
      let isActiveHover = false;
      let hoverEntries = [];
      if (hoverPx !== null && hoverPx >= pad.left && hoverPx <= w - pad.right) {
        const maxHoverX = layout.toX(now);
        const clampedX = Math.min(hoverPx, maxHoverX);
        const t = leftEdge + (clampedX - pad.left) / chartW * (rightEdge - leftEdge);
        drawHoverX = clampedX;
        drawHoverTime = t;
        isActiveHover = true;
        for (const entry of seriesEntries) {
          if ((entry.alpha ?? 1) < 0.5) continue;
          const v = interpolateRenderedLineAtTime(entry.visible, now, entry.smoothValue, t);
          if (v !== null) {
            hoverEntries.push({
              id: entry.id,
              color: entry.palette.line,
              label: entry.label ?? "",
              value: v
            });
          }
        }
        lastHoverRef.current = { x: clampedX, value: hoverEntries[0]?.value ?? 0, time: t };
        lastHoverEntriesRef.current = hoverEntries;
        cfg.onHover?.({
          time: t,
          value: hoverEntries[0]?.value ?? 0,
          x: clampedX,
          y: layout.toY(hoverEntries[0]?.value ?? 0)
        });
      }
      const alwaysShowTooltip = cfg.crosshairStyle?.alwaysShow ?? cfg.alwaysShowTooltip;
      const pinLatestTooltip = !isActiveHover && alwaysShowTooltip && seriesEntries.length > 0;
      if (pinLatestTooltip) {
        drawHoverX = layout.toX(now);
        drawHoverTime = now;
        hoverEntries = seriesEntries.flatMap(
          (entry) => (entry.alpha ?? 1) < 0.5 ? [] : [
            {
              id: entry.id,
              color: entry.palette.line,
              label: entry.label ?? "",
              value: entry.smoothValue
            }
          ]
        );
      }
      const scrubTarget = isActiveHover || pinLatestTooltip ? 1 : 0;
      if (noMotion) {
        scrubAmountRef.current = scrubTarget;
      } else {
        scrubAmountRef.current += (scrubTarget - scrubAmountRef.current) * SCRUB_LERP_SPEED;
        if (scrubAmountRef.current < 0.01) scrubAmountRef.current = 0;
        if (scrubAmountRef.current > 0.99) scrubAmountRef.current = 1;
      }
      if (!isActiveHover && !pinLatestTooltip && scrubAmountRef.current > 0 && lastHoverRef.current) {
        drawHoverX = lastHoverRef.current.x;
        drawHoverTime = lastHoverRef.current.time;
        hoverEntries = lastHoverEntriesRef.current;
      }
      const sourceHeadSeconds = sourceFrame?.nowSeconds;
      const visibleOrderbook = resolveFrameOrderbook(cfg.orderbookData, sourceHeadSeconds);
      drawMultiFrame(ctx, layout, {
        loading: cfg.loading,
        showFill: cfg.showFill,
        series: seriesEntries,
        orderbookData: visibleOrderbook,
        orderbookState: visibleOrderbook ? orderbookStateRef.current : void 0,
        ...sourceHeadSeconds === void 0 ? {} : { orderbookNow: sourceHeadSeconds },
        now: horizonFrame.lineEndTime,
        showGrid: cfg.showGrid,
        showTimeAxis: cfg.showTimeAxis,
        showPulse: cfg.showPulse,
        dotSize: cfg.dotSize ?? 3,
        pulseSize: cfg.pulseSize ?? 19,
        referenceLine: frameReferenceLine,
        referenceLines: cfg.referenceLines,
        markers: resolveFrameMarkers(cfg.markers, sourceHeadSeconds),
        showMarkers: !cfg.loading,
        markerCondenseDistance: cfg.markerCondenseDistance,
        hoverX: drawHoverX,
        hoverTime: drawHoverTime,
        hoverEntries,
        scrubAmount: scrubAmountRef.current,
        pinnedTooltip: pinLatestTooltip,
        crosshairStyle: cfg.crosshairStyle,
        bucketSecs: estimateBucketSecs(seriesEntries),
        showValueLine: cfg.showValueLine,
        useSeriesColorForDashLines: cfg.useSeriesColorForDashLines,
        windowSecs,
        formatValue: cfg.formatValue,
        formatTime: cfg.formatTime,
        gridState: gridStateRef.current,
        timeAxisState: timeAxisStateRef.current,
        dt: frameAnimationDt,
        targetWindowSecs: Math.max(
          resolveLineWindow(cfg.windowSecs, cfg.historyStartTime, now, buffer),
          rightEdge - leftEdge
        ),
        tooltipY: cfg.tooltipY,
        tooltipOutline: cfg.tooltipOutline,
        chartReveal,
        pauseProgress,
        now_ms: presentationNowMs,
        reducedMotion: noMotion,
        primaryPalette: cfg.palette,
        lineCurve: cfg.lineCurve,
        particleState: cfg.degenOptions && allowDegenEffects ? particleStateRef.current : void 0,
        particleOptions: cfg.degenOptions,
        shakeState: cfg.degenOptions && allowDegenEffects ? shakeStateRef.current : void 0,
        edgeFade: cfg.edgeFade,
        ...cfg.timeHorizon === void 0 || timeHorizonProgressRef.current <= 0 ? {} : {
          timeHorizon: {
            progress: timeHorizonProgressRef.current,
            color: cfg.timeHorizon.color,
            label: `${cfg.timeHorizon.label} ${formatTimeHorizonCountdown(cfg.timeHorizon.at - now)}`
          }
        }
      });
      if (sourceFrame !== null) {
        sourceCanvasPaintedRef.current = true;
        cfg.frameSource?.onFramePainted?.(sourceFrame, {
          morphing: frameStateRef.current.morph !== null
        });
      }
      const bgAlpha = 1 - chartReveal;
      if (bgAlpha > 0.01 && revealTarget === 0 && !cfg.loading) {
        const bgEmptyAlpha = (1 - loadingAlpha) * bgAlpha;
        if (bgEmptyAlpha > 0.01) {
          drawEmpty(ctx, w, h, pad, cfg.palette, bgEmptyAlpha, now_ms, true, cfg.emptyText);
        }
      }
      if (badgeRef.current) badgeRef.current.container.style.display = "none";
    } else {
      const effectivePoints = useStash ? lastDataRef.current : points;
      const adaptiveSpeed = computeAdaptiveSpeed(
        cfg.value,
        displayValueRef.current,
        displayMinRef.current,
        displayMaxRef.current,
        cfg.lerpSpeed,
        noMotion
      );
      if (!useStash) {
        displayValueRef.current = lerp(displayValueRef.current, cfg.value, adaptiveSpeed, pausedDt);
        if (pauseProgress < 0.5) {
          const prevRange = displayMaxRef.current - displayMinRef.current || 1;
          if (Math.abs(displayValueRef.current - cfg.value) < prevRange * VALUE_SNAP_THRESHOLD) {
            displayValueRef.current = cfg.value;
          }
        }
      }
      const smoothValue = displayValueRef.current;
      let chartW = w - pad.left - pad.right;
      const baseBuffer = cfg.windowBuffer ?? (cfg.showBadge ? WINDOW_BUFFER : WINDOW_BUFFER_NO_BADGE);
      const needsArrowRoom = cfg.showMomentum && cfg.showBadge;
      const buffer = needsArrowRoom ? Math.max(baseBuffer, 37 / Math.max(chartW, 1)) : baseBuffer;
      const transition = windowTransitionRef.current;
      const liveNow = cfg.nowSeconds ?? Date.now() / 1e3 - timeDebtRef.current;
      if (hasData) frozenNowRef.current = liveNow;
      const now = useStash ? frozenNowRef.current : liveNow;
      const windowResult = updateWindowTransition(
        cfg,
        transition,
        displayWindowRef.current,
        displayMinRef.current,
        displayMaxRef.current,
        noMotion,
        now_ms,
        now,
        effectivePoints,
        smoothValue,
        buffer
      );
      paintedWindowCompletion = windowResult.completedWindowSecs;
      displayWindowRef.current = windowResult.windowSecs;
      const windowSecs = windowResult.windowSecs;
      const windowTransProgress = windowResult.windowTransProgress;
      const rightEdge = now + windowSecs * buffer;
      const leftEdge = rightEdge - windowSecs;
      const filterRight = rightEdge - (rightEdge - now) * pauseProgress;
      const visible = clipPointsToWindow(effectivePoints, leftEdge - 2, filterRight);
      if (!hasRenderableLineWindow(visible)) {
        if (badgeRef.current) badgeRef.current.container.style.display = "none";
        rafRef.current = requestAnimationFrame(draw);
        return;
      }
      const windowBounds = computeWindowBounds(visible, leftEdge, filterRight, now, smoothValue);
      const computedRange = computeConfiguredRange(
        cfg,
        windowBounds.min,
        windowBounds.max,
        cfg.referenceLine,
        { start: leftEdge, end: filterRight }
      );
      const isWindowTransitioning = transition.startMs > 0;
      const allowDegenEffects = syncDegenPresentation(
        degenPresentationStateRef.current,
        `${cfg.mode}:${cfg.windowSecs}`,
        isWindowTransitioning,
        particleStateRef.current,
        shakeStateRef.current
      );
      const rangeResult = updateRange(
        computedRange,
        rangeInitedRef.current,
        targetMinRef.current,
        targetMaxRef.current,
        displayMinRef.current,
        displayMaxRef.current,
        isWindowTransitioning,
        windowTransProgress,
        transition,
        adaptiveSpeed,
        chartH,
        pausedDt
      );
      rangeInitedRef.current = rangeResult.rangeInited;
      targetMinRef.current = rangeResult.targetMin;
      targetMaxRef.current = rangeResult.targetMax;
      displayMinRef.current = rangeResult.displayMin;
      displayMaxRef.current = rangeResult.displayMax;
      const { minVal, maxVal, valRange } = rangeResult;
      const measurementLayout = {
        w,
        h,
        pad,
        chartW,
        chartH,
        leftEdge,
        rightEdge,
        minVal,
        maxVal,
        valRange,
        toX: (t) => pad.left + (t - leftEdge) / (rightEdge - leftEdge) * chartW,
        toY: (v) => pad.top + (1 - (v - minVal) / valRange) * chartH
      };
      applyMeasuredYAxisPadding(ctx, measurementLayout, cfg, gridStateRef.current);
      chartW = w - pad.left - pad.right;
      if (reportedPlotLayoutRef.current?.canvasWidth !== w || reportedPlotLayoutRef.current.showGrid !== cfg.showGrid) {
        reportedPlotLayoutRef.current = { canvasWidth: w, showGrid: cfg.showGrid };
        cfg.onPlotWidthChange?.(chartW);
      }
      const layout = {
        ...measurementLayout,
        chartW,
        toX: (t) => pad.left + (t - leftEdge) / (rightEdge - leftEdge) * chartW
      };
      const momentum = cfg.momentumOverride ?? detectMomentum(visible);
      const hoverResult = updateHoverState(
        hoverXRef.current,
        pad,
        w,
        layout,
        now,
        visible,
        smoothValue,
        scrubAmountRef.current,
        lastHoverRef.current,
        cfg,
        noMotion,
        leftEdge,
        rightEdge,
        chartW,
        dt
      );
      scrubAmountRef.current = hoverResult.scrubAmount;
      lastHoverRef.current = hoverResult.lastHover;
      const {
        hoverX: drawHoverX,
        hoverValue: drawHoverValue,
        hoverTime: drawHoverTime
      } = hoverResult;
      const lookback = Math.min(5, visible.length - 1);
      const recentDelta = lookback > 0 ? Math.abs(
        visible[visible.length - 1].value - visible[visible.length - 1 - lookback].value
      ) : 0;
      const swingMagnitude = valRange > 0 ? Math.min(recentDelta / valRange, 1) : 0;
      drawFrame(ctx, layout, cfg.palette, {
        loading: cfg.loading,
        visible,
        smoothValue,
        now,
        momentum,
        arrowState: arrowStateRef.current,
        showGrid: cfg.showGrid,
        showTimeAxis: cfg.showTimeAxis,
        showMomentum: cfg.showMomentum,
        showPulse: cfg.showPulse,
        showFill: cfg.showFill,
        referenceLine: cfg.referenceLine,
        referenceLines: cfg.referenceLines,
        hoverX: drawHoverX,
        hoverValue: drawHoverValue,
        hoverTime: drawHoverTime,
        scrubAmount: scrubAmountRef.current,
        showValueLine: cfg.showValueLine,
        useSeriesColorForDashLines: cfg.useSeriesColorForDashLines,
        windowSecs,
        formatValue: cfg.formatValue,
        formatTime: cfg.formatTime,
        gridState: gridStateRef.current,
        timeAxisState: timeAxisStateRef.current,
        dt: frameAnimationDt,
        targetWindowSecs: resolveLineWindow(cfg.windowSecs, cfg.historyStartTime, now, buffer),
        tooltipY: cfg.tooltipY,
        tooltipOutline: cfg.tooltipOutline,
        orderbookData: cfg.orderbookData,
        orderbookState: cfg.orderbookData ? orderbookStateRef.current : void 0,
        particleState: cfg.degenOptions && allowDegenEffects ? particleStateRef.current : void 0,
        particleOptions: cfg.degenOptions,
        swingMagnitude,
        shakeState: cfg.degenOptions && allowDegenEffects ? shakeStateRef.current : void 0,
        chartReveal,
        pauseProgress,
        now_ms: presentationNowMs,
        reducedMotion: noMotion,
        lineCurve: cfg.lineCurve,
        edgeFade: cfg.edgeFade
      });
      const bgAlpha = 1 - chartReveal;
      if (bgAlpha > 0.01 && revealTarget === 0 && !cfg.loading) {
        const bgEmptyAlpha = (1 - loadingAlpha) * bgAlpha;
        if (bgEmptyAlpha > 0.01) {
          drawEmpty(ctx, w, h, pad, cfg.palette, bgEmptyAlpha, now_ms, true, cfg.emptyText);
        }
      }
      const badge = badgeRef.current;
      if (badge) {
        badgeYRef.current = updateBadgeDOM(
          badge,
          cfg,
          smoothValue,
          layout,
          momentum,
          badgeYRef.current,
          badgeColorRef.current,
          isWindowTransitioning,
          noMotion,
          ctx,
          pausedDt,
          chartReveal
        );
        if (pauseProgress > 0.01 && badge.container.style.display !== "none") {
          const base = badge.container.style.opacity ? parseFloat(badge.container.style.opacity) : 1;
          badge.container.style.opacity = String(base * (1 - pauseProgress));
        }
      }
      const valEl = cfg.valueDisplayRef?.current;
      if (valEl) {
        const displayVal = cfg.valueMomentumColor ? Math.abs(smoothValue) : smoothValue;
        valEl.textContent = cfg.formatValue(displayVal);
        if (cfg.valueMomentumColor) {
          const mc = momentum === "up" ? "#22c55e" : momentum === "down" ? "#ef4444" : "";
          if (mc) valEl.style.color = mc;
          else valEl.style.removeProperty("color");
        }
      }
    }
    if (paintedWindowCompletion !== null) {
      cfg.onWindowTransitionComplete?.(paintedWindowCompletion);
    }
    rafRef.current = requestAnimationFrame(draw);
  }, [canvasRef, orderbookStateRef, particleStateRef, shakeStateRef]);
  useEffect(() => {
    const onVisibilityChange = () => {
      const cfg = configRef.current;
      const sourceHead = resolveActiveFrameSourceHead(
        cfg.frameSource,
        lastSourceHeadSecondsRef.current
      );
      fenceOrderbookEntries(
        orderbookStateRef.current,
        resolveFrameOrderbook(cfg.orderbookData, sourceHead)
      );
      if (document.hidden) {
        if (cfg.frameSource !== void 0) sourceOrderbookFencedRef.current = false;
        frameStateRef.current.morph = null;
        cfg.frameSource?.onVisibilityChange?.(false);
        cancelAnimationFrame(rafRef.current);
        rafRef.current = 0;
        return;
      }
      cfg.frameSource?.onVisibilityChange?.(true);
      if (!rafRef.current) {
        lastFrameRef.current = 0;
        rafRef.current = requestAnimationFrame(draw);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    if (!document.hidden) {
      rafRef.current = requestAnimationFrame(draw);
    }
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    };
  }, [draw, orderbookStateRef]);
}
function useLazyRef(createValue) {
  const [initialValue] = useState(createValue);
  return useRef(initialValue);
}

// src/Liveline.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var defaultFormatValue = (v) => v.toFixed(2);
var defaultFormatTime = (t) => {
  const d = new Date(t * 1e3);
  const h = d.getHours().toString().padStart(2, "0");
  const m = d.getMinutes().toString().padStart(2, "0");
  const s = d.getSeconds().toString().padStart(2, "0");
  return `${h}:${m}:${s}`;
};
function useLivelineSeriesModel(args) {
  const previousSeriesRef = useRef2(args.series);
  const displayedSeries = args.series !== void 0 && args.series.length > 0 ? args.series : previousSeriesRef.current;
  useLayoutEffect2(() => {
    if (args.series !== void 0 && args.series.length > 0)
      previousSeriesRef.current = args.series;
  }, [args.series]);
  const palette = useMemo(() => {
    const next = resolveTheme(args.color, args.theme);
    if (args.lineWidth !== void 0) next.lineWidth = args.lineWidth;
    return next;
  }, [args.color, args.theme, args.lineWidth]);
  const seriesPalettes = useMemo(() => {
    if (args.series === void 0 || args.series.length === 0) return null;
    return resolveSeriesPalettes(args.series, args.theme, args.lineWidth);
  }, [args.series, args.theme, args.lineWidth]);
  const multiSeries = useMemo(() => {
    if (args.series === void 0 || seriesPalettes === null) return void 0;
    return args.series.map((series, index) => ({
      id: series.id,
      data: series.data,
      value: series.value,
      palette: seriesPalettes.get(series.id) ?? resolveTheme(series.color || SERIES_COLORS[index % SERIES_COLORS.length], args.theme),
      label: series.label
    }));
  }, [args.series, args.theme, seriesPalettes]);
  return {
    displayedSeries: displayedSeries ?? [],
    palette,
    multiSeries,
    isMultiSeries: args.series !== void 0 && args.series.length > 0
  };
}
function useLivelineControlModel(args) {
  const windowBarRef = useRef2(null);
  const windowBtnRefs = useRef2(/* @__PURE__ */ new Map());
  const [indicatorStyle, setIndicatorStyle] = useState2(
    null
  );
  const modeBarRef = useRef2(null);
  const modeBtnRefs = useRef2(/* @__PURE__ */ new Map());
  const [modeIndicatorStyle, setModeIndicatorStyle] = useState2(null);
  const [localSelectedSeries, setLocalSelectedSeries] = useState2(/* @__PURE__ */ new Set());
  const controlledHiddenSeries = useMemo(() => {
    if (!args.series.some((series) => series.visible !== void 0)) return void 0;
    return new Set(args.series.flatMap((series) => series.visible === false ? [series.id] : []));
  }, [args.series]);
  const selectedSeries = useMemo(
    () => args.selectedSeriesIds === void 0 ? localSelectedSeries : new Set(args.selectedSeriesIds),
    [args.selectedSeriesIds, localSelectedSeries]
  );
  const hiddenSeries = useMemo(
    () => controlledHiddenSeries ?? new Set(
      args.series.flatMap(
        (series) => selectedSeries.size > 0 && !selectedSeries.has(series.id) ? [series.id] : []
      )
    ),
    [controlledHiddenSeries, args.series, selectedSeries]
  );
  const [activeWindowSecs, setActiveWindowSecs] = useState2(
    args.windows !== void 0 && args.windows.length > 0 ? args.windows[0].secs : args.windowSecs
  );
  const activeMode = args.lineMode === true ? "line" : "candle";
  useLayoutEffect2(() => {
    if (args.windows === void 0 || args.windows.length === 0) return;
    const button = windowBtnRefs.current.get(activeWindowSecs);
    const bar = windowBarRef.current;
    if (button === void 0 || bar === null) return;
    const barRect = bar.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    setIndicatorStyle({ left: buttonRect.left - barRect.left, width: buttonRect.width });
  }, [activeWindowSecs, args.windows]);
  useLayoutEffect2(() => {
    if (args.onModeChange === void 0) return;
    const button = modeBtnRefs.current.get(activeMode);
    const bar = modeBarRef.current;
    if (button === void 0 || bar === null) return;
    const barRect = bar.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    setModeIndicatorStyle({ left: buttonRect.left - barRect.left, width: buttonRect.width });
  }, [activeMode, args.onModeChange]);
  const handleSeriesToggle = useCallback2(
    (id) => {
      const selected = new Set(selectedSeries);
      if (selected.has(id)) selected.delete(id);
      else selected.add(id);
      const next = new Set(
        args.series.flatMap(
          (series) => selected.size > 0 && !selected.has(series.id) ? [series.id] : []
        )
      );
      if (args.selectedSeriesIds === void 0) setLocalSelectedSeries(selected);
      for (const series of args.series) {
        if (hiddenSeries.has(series.id) !== next.has(series.id)) {
          args.onSeriesToggle?.(series.id, !next.has(series.id));
        }
      }
      args.onSeriesSelect?.(id);
    },
    [
      args.onSeriesToggle,
      args.onSeriesSelect,
      args.series,
      args.selectedSeriesIds,
      hiddenSeries,
      selectedSeries
    ]
  );
  const showSeriesToggle = args.displayedSeriesCount > 1;
  return {
    windowBarRef,
    windowBtnRefs,
    indicatorStyle,
    modeBarRef,
    modeBtnRefs,
    modeIndicatorStyle,
    hiddenSeries,
    activeWindowSecs,
    setActiveWindowSecs,
    activeMode,
    handleSeriesToggle,
    showSeriesToggle,
    effectiveWindowSecs: args.windows === void 0 ? args.windowSecs : activeWindowSecs,
    legendOnly: showSeriesToggle && (args.windows === void 0 || args.windows.length === 0) && args.onModeChange === void 0
  };
}
function resolveLivelineAppearance(args) {
  const isDark = args.theme === "dark";
  const defaultRight = args.badge ? 80 : args.grid ? 54 : 12;
  const degenEnabled = args.degen !== void 0 && args.degen !== false;
  return {
    isDark,
    momentumOverride: typeof args.momentum === "string" ? args.momentum : void 0,
    pad: {
      top: args.padding?.top ?? 12,
      right: args.padding?.right ?? defaultRight,
      bottom: args.padding?.bottom ?? 28,
      left: args.padding?.left ?? 12
    },
    degenOptions: degenEnabled ? typeof args.degen === "object" ? args.degen : {} : void 0,
    ws: args.windowStyle ?? "default",
    cursorStyle: args.scrub ? args.cursor : "default",
    activeColor: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.55)",
    inactiveColor: isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.22)"
  };
}
function Liveline(props) {
  const {
    series: seriesProp,
    theme = "dark",
    color = "#3b82f6",
    window: windowSecs = 30,
    grid = true,
    badge = true,
    momentum = true,
    scrub = true,
    degen: degenProp,
    showValue = false,
    windows,
    onWindowChange,
    windowStyle,
    padding: paddingOverride,
    cursor = "crosshair",
    lineMode,
    onModeChange,
    onSeriesToggle,
    onSeriesSelect,
    selectedSeriesIds,
    seriesToggleCompact = false,
    seriesToggleStyle,
    lineWidth,
    className,
    style
  } = props;
  const canvasRef = useRef2(null);
  const containerRef = useRef2(null);
  const valueDisplayRef = useRef2(null);
  const seriesModel = useLivelineSeriesModel({
    series: seriesProp,
    theme,
    color,
    lineWidth
  });
  const controls = useLivelineControlModel({
    windows,
    windowSecs,
    lineMode,
    onModeChange,
    onSeriesToggle,
    onSeriesSelect,
    selectedSeriesIds,
    series: seriesModel.displayedSeries,
    displayedSeriesCount: seriesModel.displayedSeries.length
  });
  const appearance = resolveLivelineAppearance({
    theme,
    badge,
    grid,
    momentum,
    degen: degenProp,
    padding: paddingOverride,
    windowStyle,
    scrub,
    cursor
  });
  useLivelineEngine(
    canvasRef,
    containerRef,
    buildLivelineEngineConfig(props, {
      palette: seriesModel.palette,
      effectiveWindowSecs: controls.effectiveWindowSecs,
      momentumOverride: appearance.momentumOverride,
      pad: appearance.pad,
      degenOptions: appearance.degenOptions,
      valueDisplayRef,
      multiSeries: seriesModel.multiSeries,
      isMultiSeries: seriesModel.isMultiSeries,
      hiddenSeries: controls.hiddenSeries
    })
  );
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        minHeight: 0
      },
      children: [
        showValue && /* @__PURE__ */ jsx(
          "span",
          {
            ref: valueDisplayRef,
            style: {
              display: "block",
              fontSize: 20,
              fontWeight: 500,
              fontFamily: '"SF Mono", Menlo, monospace',
              color: appearance.isDark ? "rgba(255,255,255,0.85)" : "#111",
              transition: "color 0.3s",
              letterSpacing: "-0.01em",
              marginBottom: 8,
              paddingTop: 4,
              paddingLeft: appearance.pad.left
            }
          }
        ),
        /* @__PURE__ */ jsx(
          LivelineControls,
          {
            windows,
            onWindowChange,
            activeWindowSecs: controls.activeWindowSecs,
            setActiveWindowSecs: controls.setActiveWindowSecs,
            windowBarRef: controls.windowBarRef,
            windowBtnRefs: controls.windowBtnRefs,
            indicatorStyle: controls.indicatorStyle,
            onModeChange,
            activeMode: controls.activeMode,
            modeBarRef: controls.modeBarRef,
            modeBtnRefs: controls.modeBtnRefs,
            modeIndicatorStyle: controls.modeIndicatorStyle,
            showSeriesToggle: controls.showSeriesToggle,
            series: seriesModel.displayedSeries,
            hiddenSeries: controls.hiddenSeries,
            handleSeriesToggle: controls.handleSeriesToggle,
            seriesToggleCompact,
            seriesToggleStyle,
            ws: appearance.ws,
            isDark: appearance.isDark,
            isMultiSeries: seriesModel.isMultiSeries,
            legendOnly: controls.legendOnly,
            leftPadding: appearance.pad.left,
            activeColor: appearance.activeColor,
            inactiveColor: appearance.inactiveColor
          }
        ),
        /* @__PURE__ */ jsx(
          "div",
          {
            ref: containerRef,
            className,
            style: {
              width: "100%",
              flex: "1 1 0%",
              minHeight: 0,
              position: "relative",
              ...style
            },
            children: /* @__PURE__ */ jsx("canvas", { ref: canvasRef, style: { display: "block", cursor: appearance.cursorStyle } })
          }
        )
      ]
    }
  );
}
function buildLivelineEngineConfig(props, resolved) {
  const isMultiSeries = resolved.isMultiSeries;
  return {
    data: props.data,
    value: props.value,
    palette: resolved.palette,
    windowSecs: resolved.effectiveWindowSecs,
    nowSeconds: props.nowSeconds,
    frameSource: props.frameSource,
    historyStartTime: props.historyStartTime,
    windowBuffer: props.windowBuffer,
    timeHorizon: props.timeHorizon,
    onWindowTransitionComplete: props.onWindowTransitionComplete,
    onPlotWidthChange: props.onPlotWidthChange,
    lerpSpeed: props.lerpSpeed ?? 0.08,
    showGrid: props.grid ?? true,
    showTimeAxis: props.timeAxis ?? true,
    showBadge: isMultiSeries ? false : props.badge ?? true,
    showMomentum: isMultiSeries ? false : props.momentum !== false,
    momentumOverride: resolved.momentumOverride,
    showFill: props.fill ?? !isMultiSeries,
    referenceLine: props.referenceLine,
    referenceLines: props.referenceLines,
    extendRange: props.extendRange,
    markers: props.markers,
    markerCondenseDistance: props.markerCondenseDistance ?? 0,
    formatValue: props.formatValue ?? defaultFormatValue,
    formatTime: props.formatTime ?? defaultFormatTime,
    padding: resolved.pad,
    axisOptions: props.axisOptions,
    onHover: props.onHover,
    showPulse: props.pulse ?? true,
    dotSize: props.dotSize,
    pulseSize: props.pulseSize,
    scrub: props.scrub ?? true,
    alwaysShowTooltip: props.alwaysShowTooltip ?? false,
    crosshairStyle: props.crosshairStyle,
    showValueLine: props.valueLine ?? true,
    useSeriesColorForDashLines: props.useSeriesColorForDashLines ?? false,
    exaggerate: props.exaggerate ?? false,
    degenOptions: resolved.degenOptions,
    edgeFade: props.edgeFade ?? true,
    badgeTail: props.badgeTail ?? true,
    badgeVariant: props.badgeVariant ?? "default",
    tooltipY: props.tooltipY ?? 14,
    tooltipOutline: props.tooltipOutline ?? true,
    valueMomentumColor: props.valueMomentumColor ?? false,
    valueDisplayRef: props.showValue === true ? resolved.valueDisplayRef : void 0,
    orderbookData: props.orderbook,
    getOrderbookInitialSeenIds: props.getOrderbookInitialSeenIds,
    loading: props.loading ?? false,
    paused: props.paused ?? false,
    emptyText: props.emptyText,
    mode: props.mode ?? "line",
    candles: props.candles,
    candleWidth: props.candleWidth,
    liveCandle: props.liveCandle,
    lineMode: props.lineMode,
    lineData: props.lineData,
    lineValue: props.lineValue,
    multiSeries: resolved.multiSeries,
    isMultiSeries,
    hiddenSeriesIds: resolved.hiddenSeries,
    lineCurve: props.curve ?? "monotone"
  };
}
function LivelineControls(props) {
  const hasWindows = props.windows !== void 0 && props.windows.length > 0;
  if (!hasWindows && props.onModeChange === void 0 && !props.showSeriesToggle) return null;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        marginBottom: 6,
        marginLeft: props.legendOnly ? 0 : props.leftPadding,
        overflowX: "auto",
        overflowY: "hidden",
        flexWrap: "nowrap",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        WebkitOverflowScrolling: "touch"
      },
      children: [
        /* @__PURE__ */ jsx(WindowControls, { ...props }),
        /* @__PURE__ */ jsx(ModeControls, { ...props }),
        /* @__PURE__ */ jsx(SeriesControls, { ...props })
      ]
    }
  );
}
function controlBarStyle(ws, isDark) {
  return {
    position: "relative",
    display: "inline-flex",
    gap: ws === "text" ? 4 : 2,
    background: ws === "text" ? "transparent" : isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
    borderRadius: ws === "rounded" ? 999 : 6,
    padding: ws === "text" ? 0 : ws === "rounded" ? 3 : 2
  };
}
function indicatorCss(ws, isDark, indicator) {
  return {
    position: "absolute",
    top: ws === "rounded" ? 3 : 2,
    left: indicator.left,
    width: indicator.width,
    height: ws === "rounded" ? "calc(100% - 6px)" : "calc(100% - 4px)",
    background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.035)",
    borderRadius: ws === "rounded" ? 999 : 4,
    transition: "left 0.25s cubic-bezier(0.4, 0, 0.2, 1), width 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
    pointerEvents: "none"
  };
}
function WindowControls(props) {
  if (props.windows === void 0 || props.windows.length === 0) return null;
  return /* @__PURE__ */ jsxs("div", { ref: props.windowBarRef, style: controlBarStyle(props.ws, props.isDark), children: [
    props.ws === "text" || props.indicatorStyle === null ? null : /* @__PURE__ */ jsx("div", { style: indicatorCss(props.ws, props.isDark, props.indicatorStyle) }),
    props.windows.map((windowOption) => {
      const isActive = windowOption.secs === props.activeWindowSecs;
      return /* @__PURE__ */ jsx(
        "button",
        {
          ref: (element) => {
            if (element === null) props.windowBtnRefs.current.delete(windowOption.secs);
            else props.windowBtnRefs.current.set(windowOption.secs, element);
          },
          onClick: () => {
            props.setActiveWindowSecs(windowOption.secs);
            props.onWindowChange?.(windowOption.secs);
          },
          style: {
            position: "relative",
            zIndex: 1,
            fontSize: 11,
            padding: props.ws === "text" ? "2px 6px" : "3px 10px",
            borderRadius: props.ws === "rounded" ? 999 : 4,
            border: "none",
            cursor: "pointer",
            fontFamily: "system-ui, -apple-system, sans-serif",
            fontWeight: isActive ? 600 : 400,
            background: "transparent",
            color: isActive ? props.activeColor : props.inactiveColor,
            transition: "color 0.2s, background 0.15s",
            lineHeight: "16px"
          },
          children: windowOption.label
        },
        windowOption.secs
      );
    })
  ] });
}
function modeButtonStyle(ws) {
  return {
    position: "relative",
    zIndex: 1,
    padding: "5px 7px",
    borderRadius: ws === "rounded" ? 999 : 4,
    border: "none",
    cursor: "pointer",
    background: "transparent",
    display: "flex",
    alignItems: "center"
  };
}
function ModeControls(props) {
  if (props.onModeChange === void 0) return null;
  const onModeChange = props.onModeChange;
  const lineColor = props.activeMode === "line" ? props.activeColor : props.inactiveColor;
  const candleColor = props.activeMode === "candle" ? props.activeColor : props.inactiveColor;
  return /* @__PURE__ */ jsxs("div", { ref: props.modeBarRef, style: controlBarStyle(props.ws, props.isDark), children: [
    props.ws === "text" || props.modeIndicatorStyle === null ? null : /* @__PURE__ */ jsx("div", { style: indicatorCss(props.ws, props.isDark, props.modeIndicatorStyle) }),
    /* @__PURE__ */ jsx(
      "button",
      {
        "aria-label": "Line chart",
        ref: (element) => {
          if (element === null) props.modeBtnRefs.current.delete("line");
          else props.modeBtnRefs.current.set("line", element);
        },
        onClick: () => onModeChange("line"),
        style: modeButtonStyle(props.ws),
        children: /* @__PURE__ */ jsx("svg", { width: "12", height: "12", viewBox: "0 0 12 12", fill: "none", children: /* @__PURE__ */ jsx(
          "path",
          {
            d: "M1 8.5C2.5 8.5 3 4 5.5 4S7.5 7 8.5 7C9.5 7 10 3.5 11 3.5",
            stroke: lineColor,
            strokeWidth: props.activeMode === "line" ? 1.5 : 1.2,
            strokeLinecap: "round",
            fill: "none"
          }
        ) })
      }
    ),
    /* @__PURE__ */ jsx(
      "button",
      {
        "aria-label": "Candlestick chart",
        ref: (element) => {
          if (element === null) props.modeBtnRefs.current.delete("candle");
          else props.modeBtnRefs.current.set("candle", element);
        },
        onClick: () => onModeChange("candle"),
        style: modeButtonStyle(props.ws),
        children: /* @__PURE__ */ jsxs("svg", { width: "12", height: "12", viewBox: "0 0 12 12", fill: "none", children: [
          /* @__PURE__ */ jsx("line", { x1: "3.5", y1: "1", x2: "3.5", y2: "11", stroke: candleColor, strokeWidth: "1" }),
          /* @__PURE__ */ jsx("rect", { x: "2", y: "3", width: "3", height: "5", rx: "0.5", fill: candleColor }),
          /* @__PURE__ */ jsx("line", { x1: "8.5", y1: "2", x2: "8.5", y2: "10", stroke: candleColor, strokeWidth: "1" }),
          /* @__PURE__ */ jsx("rect", { x: "7", y: "4", width: "3", height: "4", rx: "0.5", fill: candleColor })
        ] })
      }
    )
  ] });
}
function seriesControlsStyle(props) {
  return {
    display: "inline-flex",
    gap: props.seriesToggleStyle?.gap ?? (props.ws === "text" ? 4 : 2),
    background: props.seriesToggleStyle?.containerBackground ?? (props.ws === "text" ? "transparent" : props.isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)"),
    borderRadius: props.ws === "rounded" ? 999 : 6,
    padding: props.ws === "text" ? 0 : props.ws === "rounded" ? 3 : 2,
    paddingLeft: props.legendOnly ? 0 : void 0,
    opacity: props.isMultiSeries ? 1 : 0,
    transition: "opacity 0.4s",
    pointerEvents: props.isMultiSeries ? "auto" : "none",
    flexShrink: 0
  };
}
function seriesButtonStyle(props, hidden, index) {
  const verticalPadding = props.seriesToggleStyle?.paddingY ?? (props.seriesToggleCompact ? props.ws === "text" ? 2 : 5 : props.ws === "text" ? 2 : 3);
  const horizontalPadding = props.seriesToggleStyle?.paddingX ?? (props.seriesToggleCompact ? props.ws === "text" ? 4 : 7 : props.ws === "text" ? 6 : 8);
  const activeBackground = props.seriesToggleStyle?.activeBackground ?? (props.ws === "text" ? "transparent" : props.isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.035)");
  return {
    position: "relative",
    zIndex: 1,
    fontSize: props.seriesToggleStyle?.fontSize ?? 11,
    padding: `${verticalPadding}px ${horizontalPadding}px`,
    paddingLeft: props.legendOnly && index === 0 ? 0 : void 0,
    borderRadius: props.seriesToggleStyle?.borderRadius ?? (props.ws === "rounded" ? 999 : 4),
    border: "none",
    cursor: "pointer",
    fontFamily: "system-ui, -apple-system, sans-serif",
    fontWeight: props.seriesToggleStyle?.fontWeight ?? 500,
    background: hidden ? props.seriesToggleStyle?.hiddenBackground ?? "transparent" : activeBackground,
    color: hidden ? props.seriesToggleStyle?.hiddenColor ?? props.inactiveColor : props.seriesToggleStyle?.activeColor ?? props.activeColor,
    opacity: hidden ? 0.4 : 1,
    transition: "opacity 0.2s, background 0.15s, color 0.2s",
    lineHeight: "16px",
    display: "flex",
    alignItems: "center",
    gap: props.seriesToggleCompact ? 0 : props.seriesToggleStyle?.dotGap ?? 4
  };
}
function seriesDotStyle(props, hidden, color) {
  const size = props.seriesToggleStyle?.dotSize ?? (props.seriesToggleCompact ? 8 : 6);
  return {
    width: size,
    height: size,
    borderRadius: "50%",
    background: color,
    flexShrink: 0,
    opacity: hidden ? 0.4 : 1,
    transition: "opacity 0.2s"
  };
}
function SeriesControls(props) {
  if (!props.showSeriesToggle) return null;
  return /* @__PURE__ */ jsx("div", { style: seriesControlsStyle(props), children: props.series.map((series, index) => {
    const hidden = props.hiddenSeries.has(series.id);
    const color = series.color || SERIES_COLORS[index % SERIES_COLORS.length];
    return /* @__PURE__ */ jsxs(
      "button",
      {
        onClick: () => props.handleSeriesToggle(series.id),
        style: seriesButtonStyle(props, hidden, index),
        children: [
          /* @__PURE__ */ jsx("span", { style: seriesDotStyle(props, hidden, color) }),
          props.seriesToggleCompact ? null : series.label ?? series.id
        ]
      },
      series.id
    );
  }) });
}

// src/LivelineTransition.tsx
import { useEffect as useEffect2, useRef as useRef3, useState as useState3 } from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
function LivelineTransition({
  active,
  children,
  duration = 300,
  className,
  style
}) {
  const childArray = Array.isArray(children) ? children : [children];
  const [mounted, setMounted] = useState3(() => /* @__PURE__ */ new Set([active]));
  const [visible, setVisible] = useState3(active);
  const prevRef = useRef3(active);
  useEffect2(() => {
    if (active === prevRef.current) return () => {
    };
    const oldKey = prevRef.current;
    prevRef.current = active;
    setMounted((prev) => /* @__PURE__ */ new Set([...prev, active]));
    let raf1 = requestAnimationFrame(() => {
      raf1 = requestAnimationFrame(() => setVisible(active));
    });
    const timer = setTimeout(() => {
      setMounted((prev) => {
        const next = new Set(prev);
        next.delete(oldKey);
        return next;
      });
    }, duration + 50);
    return () => {
      cancelAnimationFrame(raf1);
      clearTimeout(timer);
    };
  }, [active, duration]);
  return /* @__PURE__ */ jsx2(
    "div",
    {
      className,
      style: { position: "relative", width: "100%", height: "100%", ...style },
      children: childArray.map((child) => {
        const key = String(child.key ?? "");
        if (!mounted.has(key)) return null;
        const isActive = key === visible;
        return /* @__PURE__ */ jsx2(
          "div",
          {
            style: {
              position: "absolute",
              inset: 0,
              opacity: isActive ? 1 : 0,
              transition: `opacity ${duration}ms ease`,
              pointerEvents: isActive ? "auto" : "none"
            },
            children: child
          },
          key
        );
      })
    }
  );
}
export {
  Liveline,
  LivelineTransition,
  windowForHistorySpan
};
